import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps, type MutableRefObject, type ReactNode } from 'react';
import { Icon } from '../components/icons/registry';
import { useIconSystem } from '../hooks/useIconSystem';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell/AppShell';
import { ChampionshipCard } from '../components/ChampionshipCard/ChampionshipCard';
import { ChampionshipCardDesktop } from '../components/ChampionshipCard/ChampionshipCardDesktop';
import { NoRaceCard, type NoRacePhase } from '../components/ChampionshipCard/NoRaceCard';
import { CoachCard } from '../components/CoachCard/CoachCard';
import { type CoachInsight, CoachStories } from '../components/CoachStories/CoachStories';
import { roomCoachMessage } from '../components/CoachStories/roomCoachHref';
import { ContextDrawer, type ContextItem } from '../components/ContextDrawer/ContextDrawer';
import { InlineAction } from '../components/today/InlineAction';
import { LastSession } from '../components/LastSession/LastSession';
import { MiniTile } from '../components/MiniTile/MiniTile';
import { NoPlanBanner } from '../components/NoPlanBanner/NoPlanBanner';
import { TeamInviteBanner } from '../components/team/TeamInviteBanner';
import { GuardianNudgeCard } from '../components/today/GuardianNudgeCard';
import { SerifHeading } from '../components/primitives';
import { QuickActions } from '../components/QuickActions/QuickActions';
import { ChampionshipCardSkeleton } from '../components/skeletons/ChampionshipCardSkeleton';
import { TodayHeroSkeleton } from '../components/skeletons/TodayHeroSkeleton';
import {
  TodayFrameGate,
  TodayFrameSwitch,
  TodaySkeletonV2,
  coldSkeletonPlace,
} from '../components/skeletons/TodaySkeletonV2';
import { WeekPlanSkeleton } from '../components/skeletons/WeekPlanSkeleton';
import skeletonStyles from '../components/skeletons/skeletons.module.css';
import { SeasonGlance } from '../components/season/SeasonGlance';
import { SeasonRail } from '../components/season/SeasonRail';
import { SideNav } from '../components/SideNav/SideNav';
import { TeamFeed } from '../components/TeamFeed/TeamFeed';
import { LoopHero } from '../components/today/LoopHero';
import { HealthHomeSlot } from '../components/today/HealthHomeSlot';
import { PlanLedger } from '../components/today/PlanLedger';
import { RewardStrip } from '../components/today/RewardStrip';
import { PinnedContext } from '../components/today/PinnedContext';
import {
  HealthSyncProvider,
  StatusRow,
  healthRowFor,
  useSharedHealthSync,
} from '../components/today/v2/StatusRow';
import { NextStepsCard } from '../components/today/v2/NextStepsCard';
import { WeekListV2 } from '../components/today/v2/WeekListV2';
import { StreakChip } from '../components/today/StreakChip';
import { TopRailUnread } from '../components/today/TopRailUnread';
import {
  LOCATION_PROMPT_DISMISSED_KEY,
  TodayHero,
  WeatherPromptContext,
  shouldPromptForLocation,
  type TodayHeroDefaultProps,
  type WeatherPrompt,
} from '../components/TodayHero/TodayHero';
import { LocationEditModal } from '../components/settings/v2/LocationEditModal';
import { TopBar } from '../components/TopBar/TopBar';
import { WeekPlan } from '../components/WeekPlan/WeekPlan';
import { useStaleWeek } from '../components/WeekPlan/useStaleWeek';
import { WeekPlanDesktop } from '../components/WeekPlan/WeekPlanDesktop';
import { useAuthContext } from '../contexts/AuthContext';
// B167 fix 2 — the hold below opens its OWN bounded cycle through the same
// recovery door the online/visibilitychange listeners use (`clearExhaustedMark`),
// rather than waiting on `retryConfigRead`'s shared `reaskSpent` door, which
// `AuthContext`'s own automatic re-ask has always already spent by the time
// this surface can render.
import { clearExhaustedMark, getSupabaseClient } from '../lib/supabase';
import { isNativeApp } from '../lib/platform';
import { SIGN_IN_LANDING_PATH } from '../components/Landing/v2/landingContent';
import { CHIP_AUX_NOT_READY, OAUTH_NOT_READY_PRIMARY } from '../lib/oauthChipCopy';
import {
  championshipFinal,
  coachStates,
  newbie,
  ramiro,
  ramiroMacroPlan,
  ramiroMacroPlanMeetLabels,
  ramiroTeam,
  ramiroToday,
  ramiroWeek,
} from '../fixtures';
import type { Race } from '../fixtures/race';
import type { Day, TodaySession, WeeklyPlan } from '../fixtures/plan';
import type { KudosEntry } from '../fixtures/team';
import type { MacroPlanStatus, MacroPlanWeek } from '../hooks/useMacroPlan';
import { EmptyMacroPlan } from '../components/plan/EmptyMacroPlan';
import { draftTargetWeeks, formatDraftWeeksLabel } from '../components/plan/BatchDraftCard';
import { LifecycleEmptyHero } from '../components/plan/LifecycleEmptyHero';
import { ReplanRefusalLine } from '../components/plan/ReplanRefusalLine';
// B99 seam 6 — one owner for the guardian hold: the helper decides, this route
// never restates the code (see the route-level derivation in `TodayRealData`).
// B196 L8 — the same house pattern for the two RULE 1 / RULE 2 codes
// (`age_below_minimum`, `age_band_required`); derived the same way,
// beside `guardianHeld`, and passed to the SAME `EmptyMacroPlan` props L5
// already built (`held`'s siblings `ageBelowMinimum` / `ageBandRequired`).
// B196 L9 — the third coded refusal (`age_review_pending`, RULE 2.6a, the
// actual-knowledge review hold) joins the pair above the same way, on the
// same house pattern.
import {
  editGuardianHold,
  GUARDIAN_HOLD_MESSAGE,
  editAgeBelowMinimum,
  editAgeBandRequired,
  editAgeReviewPending,
} from '../lib/planEdit';
import { isReplanBlocked } from '../lib/seasonPlanCopy';
import type { CodedError } from '../lib/roleRefusal';
import { useAccountRole } from '../hooks/useAccountRole';
import { useActiveSeason } from '../hooks/useActiveSeason';
import { useAthleteContext } from '../hooks/useAthleteContext';
import { useMacroPlan } from '../hooks/useMacroPlan';
import { useStartSeason } from '../hooks/useStartSeason';
// B129 (founder `B129: hold fact in the payload`, 2026-09-23) — the no-season
// card needs to know the COPPA hold BEFORE any "Start my season" attempt
// exists to carry a coded error. `coppaHeld` is R1's field on the same
// endpoint `derivedMinor` already reads.
import { useUserTier } from '../hooks/useUserTier';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useMotivation } from '../hooks/useMotivation';
import { useCoachReactionTodayState } from '../hooks/useCoachReactionToday';
import { useSeasonPath } from '../lib/train/routes';
import { useQueryState } from '../hooks/useQueryState';
import {
  isOnboardingComplete,
  loadPersistedStateForUser,
  type OnboardingAnswers,
} from '../hooks/onboardingReducer';
// B47 / B108 — ONE rule for "the screening answers are all there", shared with
// `buildOnboardingPayload` and the coach lite flow, so no path on this route can
// decide the screening is done on terms of its own.
import { parqAnswersComplete } from '../lib/parqAnswers';
import { usePersistOnboarding } from '../hooks/usePersistOnboarding';
import { usePlanDayCompletion } from '../hooks/usePlanDayCompletion';
import { useRaceResults } from '../hooks/useRaceResults';
import { useSeasonPlans } from '../hooks/useSeasonPlans';
import { useTeamHub } from '../hooks/useTeamHub';
import { useTeamKudos } from '../hooks/useTeamKudos';
import { useTeamMeets } from '../hooks/useTeamMeets';
import { useHeroWeather, weatherGlyphFor } from '../hooks/useHeroWeather';
import { useTodaySession } from '../hooks/useTodaySession';
import { useUpcomingRace } from '../hooks/useUpcomingRace';
import { useProactiveChecks } from '../hooks/useProactiveChecks';
import { useWeeklyPlan, type WeekDistanceSummary } from '../hooks/useWeeklyPlan';
import { sessionInUnit } from '../components/today/sessionInUnit';
import { useVisibilityRefetch } from '../hooks/useVisibilityRefetch';
import type { DistanceUnit } from '../lib/actualForPlanDay';
import { resolveDistanceUnit } from '../lib/vdot';
import { kudoRowsToEntries } from '../lib/teamKudosAdapter';
import {
  lastSessionFromRaces,
  type LastSessionData,
} from '../lib/lastSessionAdapter';
import {
  computeCurrentStreak,
  computeThisAndLastWeekCompliance,
  draftedWeeksFromPlans,
} from '../lib/seasonPlanAggregation';
import {
  beforeSeasonHeroProps,
  composeHeroMeta,
  composeHeroTitle,
  lifecycleHeroProps,
  phaseFromLabel,
  phaseFromMacroPlan,
  pillsToContextItems,
  racePhaseFromMacroPlan,
  totalWeeksOf,
} from '../lib/todayHeroAdapter';
import { isChoreTrigger, type ProactiveTriggerType } from '../lib/proactiveCopy';
// REL-13 — the hero coach-line precedence decision, extracted so it is testable.
import { resolveHeroCoachLine } from '../lib/heroCoachLine';
import { contextCoord } from '../lib/contextCoords';
import {
  type CoachMessage,
  type CoachMessageTriggerKind,
  mockCoachMessages,
} from '../lib/coachMessages';
import {
  evaluateCoachRules,
  toCoachInsights,
  toCoachMessage,
} from '../lib/coachInsightRules';
import { todayMondayBasedLocal } from '../lib/weekdayUtils';
import { daySessionLine } from '../lib/weekMeasure';
import { useCoachInsightsEnabled, usePlanNudgesEnabled } from '../hooks/useFeatureFlag';
import { resolveCurrentWeek } from '../lib/macroPlanPhases';
import type { PlanLifecycleInput } from '../lib/proactiveTriggers';
import { recallTodayLayout, rememberTodayLayout, type TodayLayout } from '../lib/todayLayoutMemory';
import {
  RpePrompt,
  type RpeDistanceCapture,
  type RpeCaptureResult,
  type RpePlannedDistance,
} from '../components/today/RpePrompt';
import { ReflowOfferCard } from '../components/today/ReflowOfferCard';
import { useServerFlag } from '../hooks/useServerFlag';
import { roomAvailableOnThisClient } from '../lib/roomAvailability';
import { useReflowOffer } from '../hooks/useReflowOffer';
import { useCoachMode } from '../hooks/useCoachMode';
import { resetHorizontalScroll } from '../lib/resetHorizontalScroll';
// B73 — the one display-name chain, shared with /settings. Never the email.
import { GENERIC_DISPLAY_LABEL, deriveDisplayName } from '../lib/displayName';
import styles from './today.module.css';

// Inline fixtures — these are specific to the dashboard page and don't
// earn top-level fixture files yet. Move to fixtures/ if they get reused
// by other routes in later phases.
const ramiroStreak = {
  cells: [true, true, true, true, true, true, true, true, true, true, true, true, false, false],
  todayIndex: 12,
  streakDays: 12,
};

const ramiroWeather = {
  tempDisplay: '57°',
  condition: 'overcast',
  windMph: 4,
  humidityPct: 87,
};

const ramiroCoachInsights: CoachInsight[] = [
  { id: 'training', label: 'Training', color: 'var(--c-training)', glyph: 'T', text: "Wednesday's tempo is the first real test." },
  { id: 'gear', label: 'Gear', color: 'var(--c-gear)', glyph: 'G', text: "Swap shoes at 287 mi. You're at 241." },
  { id: 'recovery', label: 'Recovery', color: 'var(--c-recovery)', glyph: 'R', text: 'Sleep debt is trending. Protect 8h tonight.' },
  { id: 'race-day', label: 'Race Day', color: 'var(--c-race)', glyph: 'P', text: 'Course is hillier than last season. Plan pacing.' },
  { id: 'mental', label: 'Mental', color: 'var(--c-mental)', glyph: 'M', text: 'Two minutes of breathwork pre-run today.' },
];

const ramiroContext: ContextItem[] = [
  { label: 'Age', value: '39' },
  { label: 'Best 1600m', value: '6:15' },
  { label: 'Pre-race coffee', value: 'yes · 80mg' },
  { label: 'Warmup', value: '30 to 60 min' },
  { label: 'Injuries', value: 'none logged', muted: true },
  { label: 'Sport', value: 'trail running' },
];

// CI-4 — "See all →" was a dead string since Phase 1; the 6 coaches live
// under /v2/chat (its left rail is literally "Coaches"), same target the
// SideNav Coaches item points at.
// B256 (founder ruling, option b) — "See all" must show the coach roster,
// never resume whichever coach's thread happened to be active/default. The
// `coachList` router history state (never the URL) tells chat.tsx to render
// that roster instead of the default coach's thread — see
// `chatContextMode.ts`'s `readCoachListEntry`.
const seeAllCoachesLink = (
  <Link
    to="/chat"
    state={{ coachList: true }}
    style={{ color: 'inherit', textDecoration: 'inherit' }}
  >
    See all →
  </Link>
);

// Helper — section label with right-aligned link. Appears 4x on mobile;
// not worth a dedicated component for Phase 1.
function SectionLabel({ title, link }: { title: string; link: ReactNode }) {
  return (
    <div className={styles.slabel}>
      <SerifHeading level="sm" as="div" className={styles.slabelTitle}>
        {title}
      </SerifHeading>
      <div className={styles.slabelLink}>{link}</div>
    </div>
  );
}

// Desktop-only inline fixtures. Move to fixtures/ if they get reused.
// PR 8 (2026-05-10): nav items gained `href` so the SideNav navigates
// between routes — the cutover-blocking gap (inert tabs that didn't
// actually change route) is closed.
// W1 (2026-05-30): a `Coaches` nav item was added here pointing at /v2/chat.
// Design pass (2026-06-19): the coaches MOAT shouldn't read as a 6th equal
// nav row — it's now an elevated "Ask a coach" action atop the SideNav (see
// SideNav.tsx), so the item is removed from this array (back to 5 places).
// 2026-06-12 (user report): Settings was missing HERE only — the
// races/team/settings routes all list it (e.g. TEAM_NAV_ITEMS), so from
// Today the only desktop entry was QuickActions' "Update context",
// which doesn't read as "profile/settings". Now consistent.
const ramiroNavItems = [
  { id: 'today', label: 'Today', href: '/today', active: true },
  { id: 'train', label: 'Train', href: '/plan' },
  { id: 'runs', label: 'Runs', href: '/run' },
  { id: 'races', label: 'Races', href: '/races' },
  { id: 'team', label: 'Team', href: '/team' },
  { id: 'settings', label: 'Settings', href: '/settings' },
];

const ramiroLastSession = {
  title: '1600m repeats',
  intensityLabel: 'Tough',
  stats: [
    { value: '4', label: 'reps' },
    { value: '7:12', label: 'avg /mi' },
    { value: '168', label: 'avg bpm' },
  ],
  note: '3rd rep was the tell: HR drift without pace drop. Logged.',
};

// SVG glyphs — extracted so both happy-path and first-run can use them.
/**
 * A2 — `sys.weather` @14. A module CONST became a COMPONENT so it can read the
 * flag (the same conversion GuardiansSection's LOCK needed).
 *
 * ⚠️ The legacy mark SELF-COLOURS (`stroke="var(--ink-700)"`), which the
 * registry forbids; the wrapper carries the token and the glyph inherits it.
 */
function WeatherGlyph(): JSX.Element {
  const iconsOn = useIconSystem();
  if (iconsOn) {
    return (
      <span aria-hidden style={{ display: 'inline-flex', color: 'var(--ink-700)' }}>
        <Icon name="sys.weather" size={14} />
      </span>
    );
  }
  return (
  <svg
    width="14"
    height="14"
    viewBox="0 0 20 20"
    fill="none"
    stroke="var(--ink-700)"
    strokeWidth="1.5"
    aria-hidden
  >
    <circle cx="7" cy="10" r="3.5" />
    <path d="M10 7a3.5 3.5 0 113.5 5.5H8" />
  </svg>
  );
}

/** A2 (audit T-14) — `chip.streak` @14. The same flame family the StreakChip,
 *  RewardStrip, TodayHero and the marketing hero already use. */
function FlameGlyph(): JSX.Element {
  const iconsOn = useIconSystem();
  if (iconsOn) return <Icon name="chip.streak" size={14} />;
  return (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
    <path d="M10 2c1 3 4 4 4 7a4 4 0 11-8 0c0-2 1-3 2-4 0 2 1 3 2 3-.5-2 0-4 0-6z" />
  </svg>
  );
}

/**
 * Phase 5 PR 5.1 — Mobile/desktop Today dashboard with real-data wiring.
 *
 * Two modes:
 *   1. **Real-data mode** (default — landing on `/v2/today` with no
 *      query params). Uses `useActiveSeason` + `useTodaySession` +
 *      `useWeeklyPlan` + `useUpcomingRace`. Loading → skeletons.
 *      Loaded null → empty-state (`<TodayHero variant="firstRun">` /
 *      `NoRaceCard`). Loaded data → real workout, week, and race.
 *      NEVER flashes fixture content (Phase 5 plan §"Decision 4").
 *
 *   2. **Demo mode** (explicit `?user=newbie` or `?user=ramiro`).
 *      Preserves the design-preview behavior shipped in Phase 1. Used
 *      for storybook-style review and the design team's preview links.
 *      Reads from `src/app/src/fixtures/`. Stable for as long as the
 *      fixtures live there.
 */

// Phase 6c long-tail PR 9 — the first-run TodayHero copy. Shared by
// all three "no session / no plan / no auth" fallbacks so the surface
// reads identically across mobile, desktop, and the full-page TodayFirstRun.
const FIRST_RUN_HERO_PROPS = {
  variant: 'firstRun' as const,
  title: "Let's build your first week.",
  body: 'Answer 4 quick questions and your training coach drafts a plan.',
  ctas: [{ label: 'Start setup', variant: 'primary' as const, href: '/v2/onboarding' }],
  // PR 5 — the loop preview from day one: reserved readiness slot + the
  // empty-but-honest reward strip (greyed flame "Start your streak",
  // hollow ring, "0 of 0 sessions"). No fake data (decision 6).
  readiness: <HealthHomeSlot />,
  reward: <RewardStrip streakDays={0} days={[]} weekMileageDone={0} weekMileageTarget={0} />,
};

/**
 * G1 (UX Session 3) — the empty / pre-season hero picker, shared by the
 * mobile + desktop "no loaded session" fallbacks. When a season + macro plan
 * exist but the resolver says we're still `before_season`, render the
 * countdown hero ("Your season starts {date}") instead of the generic
 * first-run hero (which falsely read "Let's build your first week" above an
 * already-rendered full season). Falls back to first-run for the true
 * no-plan / not-yet-set-up case.
 */
function renderEmptyOrBeforeSeasonHero(
  macroPlanWeeks: MacroPlanWeek[] | null,
  seasonName: string | null,
  draftedWeeks?: Set<number>,
  /** B-3 — re-aim the "Week N isn't drafted" hero at /v2/plan/draft when the
   *  flag is ON. Default false = byte-identical (chat deep-link) hero. */
  draftJourney = false,
  /** SP-8 (today_density_v1) — when ON, adopt the unified 16px gutter and
   *  mount the health-sync strip as a STANDALONE card directly under the hero
   *  (these non-loop heroes have no meta slot, so the sync affordance would
   *  otherwise disappear while a season exists). Default false = byte-identical. */
  densityOn = false,
) {
  // SP-8 — the standalone strip under the hero (1b). Its content still obeys
  // health_home_v1 (this pass only moves placement); "standalone" gives it the
  // full-width footprint the hero meta slot can't.
  const standaloneStrip = densityOn ? (
    <div className={styles.densityBlock}>
      <HealthHomeSlot className="standalone" />
    </div>
  ) : null;

  const before = beforeSeasonHeroProps(macroPlanWeeks ?? null, seasonName);
  if (before) {
    const hero = (
      <TodayHero
        variant="beforeSeason"
        {...before}
        planLink={{ label: 'Open season plan', href: '/v2/plan' }}
        // A season exists → the athlete is onboarded; no "Finish setup" ghost.
        setupComplete
        // Density: the strip moves OUT of the meta slot to the standalone
        // mount below (no duplication); flag OFF keeps it in the hero.
        readiness={densityOn ? undefined : <HealthHomeSlot />}
        reward={
          <RewardStrip streakDays={0} days={[]} weekMileageDone={0} weekMileageTarget={0} />
        }
      />
    );
    return densityOn ? (
      <>
        <div className={styles.heroGutter}>{hero}</div>
        {standaloneStrip}
      </>
    ) : (
      hero
    );
  }
  // P3-3 — in_season-but-undrafted / after_season lifecycle heroes (reuse the
  // firstRun variant). Slots BEFORE the generic first-run fallback so a mapped
  // season never reads "Let's build your first week."
  const lifecycle = lifecycleHeroProps(macroPlanWeeks ?? null, draftedWeeks, new Date(), draftJourney);
  if (lifecycle) {
    // SP-3 C3 — restyled to the empty-state anatomy card (frame 1i, card 2).
    return densityOn ? (
      <>
        <div className={styles.heroGutter}>
          <LifecycleEmptyHero hero={lifecycle} />
        </div>
        {standaloneStrip}
      </>
    ) : (
      <LifecycleEmptyHero hero={lifecycle} />
    );
  }
  // First-run / no-season fallback — unchanged surface (frame 1c): no gutter,
  // no standalone strip (no season context; Settings remains management).
  return <TodayHero {...FIRST_RUN_HERO_PROPS} />;
}

/**
 * B47 (founder ruling 2026-09-09, decision 1) — the copy for the held
 * retry surface. Exported so the register test can read the exact strings
 * with no render. Register: no em or en dashes, no exclamation marks,
 * never the word "safe".
 *
 * Founder copy ruling 2026-09-09 23:01 PT (row 3): the message stops at what
 * happened. The action lives on the button beside it, so "Try again." was said
 * twice to a screen reader.
 */
export const CONTEXT_RETRY_MESSAGE = "Your plan didn't load.";
export const CONTEXT_RETRY_ACTION_LABEL = 'Try again';
/**
 * B47 fix 2 (round-15 UX) — the retry gets a beat. The label changes while the
 * read is in flight (the house pattern, `EmptyMacroPlan` "Drafting your plan…"),
 * and a SECOND consecutive failure says something new instead of re-rendering
 * an identical DOM that announces nothing. One sentence, no blame, no advice
 * the athlete cannot act on.
 */
export const CONTEXT_RETRY_BUSY_LABEL = 'Trying again…';
export const CONTEXT_RETRY_AGAIN_MESSAGE =
  'Still not loading. Check your connection and try again.';

/**
 * B122 (founder ruling 2026-09-23) — the post-create generate failure line:
 * the season exists, the week's plan did not build, and the failure carries
 * no coded refusal (a 500 from `/api/macro-plan/generate`, the hook's own
 * 30s abort). Exported so a case can read the exact sentence with no render,
 * the same as `CONTEXT_RETRY_MESSAGE`. The full line also carries a link to
 * `/plan` and " to try again." (see `startHoldNotice` in `TodayRealData`).
 */
export const START_BUILD_FAILED_MESSAGE =
  "Your season is set up, but this week's plan didn't build.";

/**
 * B47 fix 2 (round-15 critic 4 / security 4) — THE SCREENING DATE OF A
 * REPLAYED BLOB.
 *
 * Both write paths on this route replay a localStorage blob that may be weeks
 * old, and `buildOnboardingPayload` stamps `_parq_completed_at: <today>` by
 * default because its usual caller is the wizard, where the screening IS
 * happening now. Stamping today here would falsify a clinical record (the
 * falsification `markerOnly` was invented to avoid) — `HealthSection` reads
 * that key and would show "Screened on <today>" for answers given in July.
 *
 * The persisted envelope carries no answered-at (`persistState` stores
 * `{version, step, answers, userId}`), so there is no honest date to send and
 * B108's `completedAt: null` OMITS the key: the POST is a patch, so any real
 * earlier date survives, and an account that never had one degrades onto
 * `HealthSection`'s existing "Completed (date unknown)". If the envelope ever
 * grows a screening timestamp, this constant is the one place that changes.
 */
const BLOB_SCREENING_DATE = null;

/**
 * B47 (founder ruling 2026-09-09, decision 2) — does this local onboarding
 * blob carry the answers a completion marker is supposed to stand for?
 *
 * The Today self-heal may write `_onboarding_completed` only when it can send
 * the data behind it in the SAME atomic POST: the journey (the coach's routing
 * signal, and the minimum data check `isOnboardingComplete` already uses) and
 * the PAR-Q screening. Every PAR-Q question needs an explicit boolean — an
 * unanswered question is a missing CLINICAL answer, and clinical inputs are
 * never inferred or defaulted. Anything short of that routes to the wizard,
 * which hydrates from this same blob and asks for what is missing.
 *
 * B108 / B47 fix 2 — the PAR-Q half is `lib/parqAnswers.parqAnswersComplete`,
 * the SAME predicate `buildOnboardingPayload` uses to decide whether the write
 * may claim a screening at all, so the route's routing decision and the
 * payload's clinical claim can never disagree. The journey stays here: it is a
 * routing fact, not a clinical one. BOTH write paths on this route (the B-28
 * self-heal and the localStorage migration) are guarded by this one function.
 *
 * Pure — no hooks, no storage. Exported for the route test.
 */
export function hasCompletionAnswers(answers: OnboardingAnswers): boolean {
  if (!answers || !answers.journey) return false;
  return parqAnswersComplete(answers.parq);
}

export function Today() {
  const [params] = useSearchParams();
  const isDemoMode = params.get('user') !== null;
  // B-15 — defensive one-time horizontal reset on route entry. Today has no
  // legitimate document-level horizontal scroll (the B-2 contract), so any
  // horizontal offset carried into this route is provably stale — the field
  // case was WKWebView's keyboard-reveal pan surviving the onboarding →
  // /today SPA navigation (a native-layer scroll: CSS `overflow-x: clip`
  // can't clear it, and nothing else in the app resets scroll). Runs once
  // per mount; a no-op when the axis is already at 0.
  useEffect(() => {
    resetHorizontalScroll();
  }, []);
  if (isDemoMode) return <TodayDemo />;
  return <TodayRealData />;
}

/**
 * Phase 7 PR 1 — derive the SideNav me-block user from the real
 * signed-in identity.
 *
 * B73 — the precedence chain itself now lives in `lib/displayName.ts`
 * (`preferred_name` → `Name` → OAuth `full_name`/`name` → NOTHING), shared
 * with `/settings` so the two copies cannot drift apart again. The email
 * local part used to sit second-from-last in BOTH, and it is the founder's
 * `GOOD EVENING, RAANROAL`. It is gone from both.
 *
 * TWO names come back, because two callers ask different questions:
 *
 *   · `greetingName` — the athlete's actual name, or `''`. The hero greeting
 *     reads THIS one: with no name it degrades to a plain `GOOD EVENING`,
 *     which is still a greeting. `GOOD EVENING, ATHLETE` is a form letter.
 *   · `displayName` — the never-blank label for the SideNav me-block, a fixed
 *     slot that cannot render empty. Falls through to the neutral generic,
 *     never to the email.
 *
 * `weekLabel` is derived best-effort from athleteContext keys; for
 * Phase 7 PR 1 we keep it minimal — empty string when no signal
 * exists. Future PRs can plumb the actual season/week context.
 *
 * Pure function — no hooks. Test-friendly.
 */
export function deriveSignedInUser(
  user: ReturnType<typeof useAuthContext>['user'],
  raw: Record<string, unknown>,
): { displayName: string; greetingName: string; initial: string; weekLabel: string } {
  const greetingName = deriveDisplayName(user, raw);
  const displayName = greetingName || GENERIC_DISPLAY_LABEL;
  const initial = displayName.charAt(0).toUpperCase() || 'A';
  // Derive a week label from athleteContext when available (e.g., a
  // training mode + week number stored by the trainer). Empty fallback
  // is fine — the SideNav me-block already handles empty gracefully
  // (the loading-state path passes `weekLabel: ''`).
  const weekLabel =
    typeof raw?.['Training mode'] === 'string'
      ? (raw['Training mode'] as string).toLowerCase()
      : '';
  return { displayName, greetingName, initial, weekLabel };
}

// ─── Ask 3 (Claude Design return 2026-10-19): the hero's "Add location" line ─────

/**
 * Provides the hero's weather prompt (`WeatherPromptContext`) while `active`, and hosts the
 * Settings location modal that "Add location" opens (`LocationEditModal`: the same
 * `LocationModal` and the same `saveFields` write the Training Profile uses, so a saved
 * location makes `useHeroWeather` fetch and the Weather row appear).
 *
 * "Not now" stamps `_location_prompt_dismissed_at` on the account's athlete context
 * through `mergeContext` (an existing per-account store: the server's patch merge takes any
 * key), so the line is gone on every device for good.
 *
 * Inactive: no provider value and no modal, and the hero is as it was.
 */
function HeroWeatherPromptHost({ active, children }: { active: boolean; children: ReactNode }) {
  const { mergeContext } = useAthleteContext();
  const [open, setOpen] = useState(false);
  const value = useMemo<WeatherPrompt | null>(
    () =>
      active
        ? {
            onAdd: () => setOpen(true),
            onDismiss: () => mergeContext({ [LOCATION_PROMPT_DISMISSED_KEY]: new Date().toISOString() }),
          }
        : null,
    [active, mergeContext],
  );
  return (
    <WeatherPromptContext.Provider value={value}>
      {children}
      {open && <LocationEditModal onClose={() => setOpen(false)} />}
    </WeatherPromptContext.Provider>
  );
}

// ─── Real-data path ──────────────────────────────────────────────────

function TodayRealData() {
  const auth = useAuthContext();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  // CI-2 — coach-insight rules engine flag (frontend; default OFF until CI-3).
  // Unconditional: hooks must run above the early returns below.
  const coachInsightsEnabled = useCoachInsightsEnabled();
  // NU-2 — plan-lifecycle nudges flag (frontend; default OFF until NU-3).
  const planNudgesEnabled = usePlanNudgesEnabled();
  const season = useActiveSeason();
  const seasonId = season.data?.id ?? null;
  // Season-model Phase 1 — the macro plan drives the CURRENT week (by date),
  // and that SAME week_number is threaded into useWeeklyPlan so the day-rows
  // render the same week the hero footer's "Week X of Y" reports. One source
  // of truth for "now" (was: footer=macro calendar week, day-rows=MAX
  // week_number — they diverged). See .claude/plans/season-plan-model-redesign.md.
  const macroPlan = useMacroPlan({ seasonId });
  const currentWeekNumber = phaseFromMacroPlan(macroPlan.data).weekNumber ?? null;
  // SP-1 (F2) — the current macro week's own week_start_date drives the "This
  // week" card's dates so Today/plan/calendar agree on one week model.
  const currentWeekStartDate =
    macroPlan.data?.find((w) => w.week_number === currentWeekNumber)?.week_start_date ?? null;
  const todaySession = useTodaySession({ seasonId, weekNumber: currentWeekNumber });
  // Phase 6c long-tail PR 7 — source PinnedContext (Ultra right rail) from the
  // real `useAthleteContext()` hook instead of the `ramiroContext` fixture.
  // Demo path (?user=ramiro) keeps the fixture so the design preview stays
  // stable. (Declared here — above useWeeklyPlan — so B-19 can thread the
  // athlete's display unit into the actuals pairing.)
  const athleteContext = useAthleteContext();
  // B-19 — the athlete's display unit (`athlete_context.distance_unit`); only
  // consulted when the b19_actuals_v1 flag is ON. Default 'mi'.
  const displayUnit: DistanceUnit = resolveDistanceUnit(athleteContext.raw);
  const weekly = useWeeklyPlan({
    seasonId,
    weekNumber: currentWeekNumber,
    weekStartDate: currentWeekStartDate,
    displayUnit,
  });
  // B332 / B314 — today's session in the athlete's unit, for the hero title and
  // the coach insight that quotes it. A mile athlete gets the very same object
  // back. B338 fix 1 (critic M1): `useMotivation` below gets this same session too.
  // Its prompt quotes a distance and the coach line it returns is read by the
  // athlete, so a raw session would have the line quote the stored unit.
  const displaySession = useMemo(
    () => sessionInUnit(todaySession.data, displayUnit),
    [todaySession.data, displayUnit],
  );
  // B309 fix round 2 — the week section keeps the last week on screen while a
  // refetch of the SAME week runs (an open day panel survives a write), but at a
  // week rollover that held data is the previous week's, re-dated by the hook
  // with the new week's dates. `weekIsStale` says so; the two mounts below then
  // treat the week as not loaded (skeleton) until the new week has landed.
  const weekIsStale = useStaleWeek(
    `${seasonId ?? ''}|${currentWeekNumber ?? ''}`,
    weekly.loading,
    weekly.planId,
    !!weekly.data,
  );
  // B-40 BUG 4b — refetch the week plan + today session when the user returns
  // to a tab/app that's been backgrounded ≥60s. Nothing else in the Today
  // data hooks does a focus/visibility refetch, so a phone left on Today
  // overnight showed a stale week until a manual refresh (founder-confirmed;
  // prod DB matched the phone). Both hooks share the same live source, so one
  // trigger keeps the Airo card + hero coherent.
  useVisibilityRefetch(() => {
    void weekly.refetch();
    void todaySession.refetch();
  });
  const race = useUpcomingRace({ season: season.data });
  // Phase 6c.1 — completion toggle for plan_days. `planId` comes from
  // useWeeklyPlan (the current-week training_plan row). Null when no
  // plan exists yet — `toggle()` throws in that case (caller no-ops
  // when `weekly.planId` is null since the day-row UI also won't
  // render an `id` for those cells).
  const completion = usePlanDayCompletion({ planId: weekly.planId });
  // AD-2.1 PR1 — actual-distance capture. The server flag gates BOTH the UI
  // affordance and the accept/persist; OFF → no distance field + the server
  // rejects the fields, so flag-off is fully inert.
  const actualDistanceCaptureOn = useServerFlag('actual_distance_capture_v1');
  // Piece 2 — whether the plan-page batch action exists; the draft-next-week
  // insight only uses its BATCH frame when this is true (else the nudge would
  // point at a CTA that doesn't render — flag off/rollback keeps the
  // always-working single-week chat deep-link).
  const batchAuthoringOn = useServerFlag('batch_authoring');
  // W1-TODAY-WIRE (spec 7.8, TWR-1) — the 2.0 Today. Read ONCE here, above the
  // first early return below (`auth.loading`): a hook under an auth-gate early
  // return is React error #310. The value reaches the effort sheet at the bottom
  // of this component and both happy paths as `v2`. Flag OFF → every consumer
  // below renders what it rendered before this flag existed. Config-exported
  // (`handleConfig`), seeded off (`supabase-migration-today-2-0-v1-flag.sql`).
  //
  // W1-TODAY-COLDLOAD — this boolean is a bare false for as long as the flag is
  // UNANSWERED, so it is not what decides which page paints before it is known: the
  // loading states (`DashboardSkeleton*`) and the happy path's own mount
  // (`TodayFrameGate`, at the bottom of this component) each ask `useTodayFrame`
  // (`components/skeletons/TodaySkeletonV2.tsx`), which says 'neutral' while the flag is
  // unanswered and uncached. By the time any read here has settled, this and that agree.
  const todayV2On = useServerFlag('today_2_0_v1');
  // B411: read as a literal, above every early return. With it on (web) a signed-out visitor goes to the landing.
  const landing2On = useServerFlag('landing_2_0_v1');
  // W1-TODAY-FIX2 (R3, R5) — the cold-load gate's memory of this page's own life. It sits
  // here, above the happy paths, because a breakpoint change (an iPad rotation, a window
  // resized across 1024) swaps the phone happy path for the desktop one and REMOUNTS the
  // gate with it, while this component and every read stay. Above every early return.
  const coldGateMemory = useRef<ColdGateMemory>({ settled: false, startedAt: null });
  // AD-2.1 PR4 — mid-week reflow offer (Today surface). The server gates on the
  // midweek_reflow_v1 flag + the server-only store; off/unconfigured → offer is
  // null and the card never renders (flag-off is inert here too).
  const reflow = useReflowOffer({ seasonId });
  // (macroPlan + currentWeekNumber are declared above so the current week can
  // be threaded into useWeeklyPlan — see the Season-model Phase 1 note.)
  // (athleteContext is declared above, next to useWeeklyPlan, so B-19 can
  // thread the athlete's display unit into the actuals pairing.)

  // Phase 7 PR 1 (post-smoke follow-up) — real-data wires for the
  // mobile TeamFeed section (was hidden entirely by commit 8e5163e
  // because it had been showing fake Lida/Marco fixtures). Two-step
  // fetch: useTeamHub for the team row (needed for teamId), then
  // useTeamKudos for the feed itself. Both hooks short-circuit when
  // there's no team (athlete has no membership → no fetch fires).
  // Entries map via `kudoRowsToEntries` to the legacy `KudosEntry`
  // shape TeamFeed already consumes.
  const teamHub = useTeamHub();
  const teamKudos = useTeamKudos({ teamId: teamHub.team?.id ?? null });
  const realKudosEntries: KudosEntry[] = teamKudos.data
    ? kudoRowsToEntries(teamKudos.data)
    : [];

  // Season 2c — "Start your season" flow for the authed-but-no-season case
  // (shared orchestrator with /v2/plan). onComplete refetches the active
  // season so the dashboard flips from the CTA to the real plan.
  const startSeason = useStartSeason({
    teamId: teamHub.team?.id ?? null,
    onComplete: async () => {
      await season.refetch();
    },
  });
  // B129 — read-only; the arrival hold fact for the no-season card below.
  const userTier = useUserTier();

  // ⚖️ B99 fix 2 (round-17 critic F1) — THE HOLD IS DERIVED HERE, NOT INSIDE
  // THE CARD, exactly as `routes/plan.tsx:381` derives it.
  //
  // `POST /api/seasons` used to carry no COPPA gate, so a held minor's CREATE
  // succeeded and only `/api/macro-plan/generate` answered 403; `useStartSeason`
  // awaits `onComplete()` — the season refetch — BEFORE it sets the error, so
  // `season.data` was already non-null by the time the hold existed. Derived
  // inside `TodayNoSeason` (which renders only while `!season.data`) the line
  // rendered zero frames: the card unmounted and Today fell through to the
  // post-create hero with no message at all.
  //
  // ⚖️ B119 (2026-09-09) CLOSED THAT DOOR AT THE FRONT: the create is gated too
  // now (`api/index.js`, the `POST /api/seasons` dispatch), so the everyday
  // shape is the refusal on the FIRST tap, with no season row and the card
  // still mounted — which is the `held` prop below. The route-level derivation
  // stays because the post-create surface is still reachable (a hold that
  // begins between the two calls, and the R71 409 the generate can still
  // return), and only a derivation up here reaches BOTH surfaces.
  const startHold = editGuardianHold(
    startSeason.error ? { code: (startSeason.error as CodedError).code } : null,
  );
  // …and it counts only while the hold is CURRENT (the b99-fix2 staleness
  // rule): a macro plan on the account means the generate this hold refused
  // has since landed, and a stale hold must never print under a rendered plan.
  //
  // B129 — ORed with the arrival fact from `/api/user/tier`, which needs no
  // attempt to be true and never depends on `startSeason.error` (`start()`
  // nulls its own error the instant a retry fires, which used to blank this
  // whole expression — and the line with it — for the length of the retry;
  // the arrival fact holds it shut across that gap). Both halves share the
  // same staleness rule: a rendered plan means any hold this named has
  // since lifted.
  const coppaHeldOnArrival = userTier.data?.coppaHeld === true;
  const guardianHeld =
    (startHold || coppaHeldOnArrival) && !(macroPlan.data && macroPlan.data.length > 0);
  // B196 L8 — the same derivation, same staleness rule, for the two other
  // coded refusals the same `startSeason.error` can carry (RULE 1's floor,
  // RULE 2's unknown-age wall). Feeds the no-season card's props only (see
  // `TodayNoSeason` below); this lane's OWNS does not extend to the
  // post-create `startHoldNotice` surface guardianHeld also reaches.
  const startAgeBelowMinimum = editAgeBelowMinimum(
    startSeason.error ? { code: (startSeason.error as CodedError).code } : null,
  );
  const ageBelowMinimumHeld =
    startAgeBelowMinimum && !(macroPlan.data && macroPlan.data.length > 0);
  const startAgeBandRequired = editAgeBandRequired(
    startSeason.error ? { code: (startSeason.error as CodedError).code } : null,
  );
  const ageBandRequiredHeld =
    startAgeBandRequired && !(macroPlan.data && macroPlan.data.length > 0);
  // B196 L9 — the third coded refusal, same derivation, same staleness rule.
  const startAgeReviewPending = editAgeReviewPending(
    startSeason.error ? { code: (startSeason.error as CodedError).code } : null,
  );
  const ageReviewPendingHeld =
    startAgeReviewPending && !(macroPlan.data && macroPlan.data.length > 0);
  // R71 parity (round-17 security 3) — the OTHER coded refusal the same chain
  // can return. Today passed `held` and not `refused`, so a 409
  // `replan_blocked_in_flight` still rendered the red block with the server's
  // raw sentence where /plan renders one quiet line.
  // B115 twin (round-24 security observation) — same staleness rule as
  // `guardianHeld` above: a macro plan on the account means the generate
  // this refusal named has since landed, so a stale refusal must never
  // print under a rendered plan.
  // Harvest 2026-09-22 critic 2 — the empty-data read is not proof of "no
  // plan": `useMacroPlan`'s catch sets `data` null on ANY failure too, so an
  // in-flight or failed macro read must never be read as settled-empty. The
  // line prints only once the read has actually SETTLED with no error.
  const startRefused =
    isReplanBlocked(startSeason.error) &&
    !macroPlan.loading &&
    !macroPlan.error &&
    !(macroPlan.data && macroPlan.data.length > 0);

  // ⚖️ B122 (founder ruling 2026-09-23) — THE ONE FAILURE THAT STILL SAID
  // NOTHING. `startHoldNotice` below named every CODED refusal (the hold, the
  // two age rules, the 409), and every other failure — a 500 from
  // `/api/macro-plan/generate`, the hook's own 30s abort — fell through to no
  // line at all: the create had already landed her a season, and the branch
  // that would have shown an error (`TodayNoSeason`) had already unmounted.
  // Same settle gate as `startRefused` (harvest critic 2): an in-flight or
  // failed macro read is not proof the plan never built, so this counts only
  // once the read has actually settled with no error and no rows.
  const startGenericFailed =
    !!startSeason.error &&
    !guardianHeld &&
    !ageBelowMinimumHeld &&
    !ageBandRequiredHeld &&
    !ageReviewPendingHeld &&
    !startRefused &&
    !macroPlan.loading &&
    !macroPlan.error &&
    !(macroPlan.data && macroPlan.data.length > 0);

  // Phase 6 cross-cutting PR 4b + PR 4d + PR 4e + PR 4f — proactive
  // trigger engine. Must be called BEFORE useMotivation so its
  // primaryHint can be threaded into the LLM-summary builder (PR 4f
  // handoff).
  //
  // Trigger wiring state (all 5 now live):
  //   ✅ comeback           (PR 4b — _last_dashboard_visit)
  //   ✅ pr_celebration     (PR 4d — useRaceResults)
  //   ✅ race_countdown     (PR 4d — useTeamMeets; D2 reroute baked in)
  //   ✅ streak_milestone   (PR 4e — useSeasonPlans + computeCurrentStreak)
  //   ✅ compliance_drop    (PR 4e — useSeasonPlans + computeThisAndLastWeekCompliance)
  //
  // The hook is fully idempotent (top-level + per-type debouncing +
  // pref gate). `enabled` waits on seasonPlans + athleteContext loading
  // so we don't fire with stale streak=0 / compliance=null before the
  // data hooks have settled.
  const raceResults = useRaceResults();
  const teamMeets = useTeamMeets();
  const seasonPlans = useSeasonPlans({ seasonId });

  // Phase 7 PR 1 (post-smoke fix #3) — LastSession real-data wire.
  // race_results is the strongest signal for "your last serious
  // session" we have until a wearable-sync telemetry hook ships. The
  // adapter returns null when no races exist → desktop falls back to
  // the ramiroLastSession fixture on demo, hides on real-data path.
  const realLastSession: LastSessionData | null = lastSessionFromRaces(
    raceResults.data ?? null,
  );

  // Phase 6c.1 — bridge `usePlanDayCompletion.toggle` with refetches so
  // that after a successful PUT, both weekly (current-week display) AND
  // seasonPlans (streak + compliance source for PR 4e) update. The
  // WeekPlan component shows its own in-flight pending state via the
  // returned promise; the refetches replace optimistic UI with the
  // canonical post-mutation data.
  //
  // Two refetches kick off in parallel — they're independent Supabase
  // queries and the WeekPlan visual update only needs `weekly`; the
  // seasonPlans refetch is for streak accuracy. The effort prompt does not
  // wait for either. B309 fix round 2: the RETURNED promise does wait for the
  // week refetch (it lands or fails, never rejects), because callers use it as
  // "this write is fully done": the day panel keeps its done / not done button
  // unavailable until then, so a second press cannot send a second write while
  // the week still shows the old status. The seasonPlans refetch is not waited
  // for (streak accuracy only).
  // RPE restore — after a COMPLETING toggle lands, ask for perceived
  // effort (skippable; vanilla plan-actions.js parity). The toggle has
  // already committed by then, so a prompt failure can never block or
  // lose the completion itself.
  const [rpeTarget, setRpeTarget] = useState<
    { dayId: string; label: string; rpe: number | null } | null
  >(null);
  const handleToggleDay = useCallback(
    async (dayId: string, currentStatus: Day['completionStatus']) => {
      const completing = currentStatus !== 'completed';
      await completion.toggle(dayId, currentStatus);
      const weekRefetch = weekly.refetch();
      void seasonPlans.refetch();
      if (completing) {
        const day = weekly.data?.days.find((d) => d.id === dayId);
        // F-110(c) — a Rest day has no effort to rate; completing one must NOT
        // fire the "Rest · Rate your perceived exertion" prompt. Skip capture.
        if (day?.rest) {
          setRpeTarget(null);
        } else {
          setRpeTarget({ dayId, label: day?.label ?? '', rpe: day?.rpe ?? null });
        }
      } else {
        setRpeTarget(null); // undo while the prompt is open → close it
      }
      // Settled = the write landed AND the week refetch landed or failed. The
      // refetch reports its own failure in its error state and does not reject;
      // the catch is for a refetch stand-in that does.
      await Promise.resolve(weekRefetch).catch(() => {});
    },
    [completion, weekly, seasonPlans],
  );
  // Hero "Add effort" / "Edit effort" recovery — reopen the capture sheet for
  // today. F-110(d): seed the existing RPE so an edit prefills (founders log
  // corrections); a first capture has rpe null.
  const handleAddEffort = useCallback(
    (dayId: string, workoutLabel: string) => {
      const day = weekly.data?.days.find((d) => d.id === dayId);
      setRpeTarget({ dayId, label: workoutLabel, rpe: day?.rpe ?? null });
    },
    [weekly],
  );
  // F-110(a) — the capture sheet's single Save commits BOTH the picked RPE and
  // the typed distance in ONE atomic PUT (the founder's loss case was a chip tap
  // committing RPE + closing, discarding a typed distance). In the low-friction
  // (no-distance) mode a chip tap arrives here with distance null — same path.
  const handleRpeCommit = useCallback(
    (result: RpeCaptureResult) => {
      const target = rpeTarget;
      setRpeTarget(null);
      if (!target) return;
      // Fire-and-forget: a failed save surfaces on the next refetch as a
      // still-missing chip (+ the hero's Add/Edit-effort recovery path).
      void completion
        .saveRpeAndDistance(target.dayId, result.rpe, result.distance)
        .then(() => void weekly.refetch())
        .catch(() => {});
    },
    [rpeTarget, completion, weekly],
  );
  // AD-2.1 PR1 — build the optional actual-distance capture for the RPE prompt.
  // Returns null (no distance UI) unless the flag is on AND the day is a
  // distance-running session. Prefilled from the day's planned mileage cell;
  // the server is the authority on running-only (a cross-train mis-show is
  // rejected, never stored). F-110(a): persistence moved to the atomic Save
  // (handleRpeCommit) — this only supplies the prefill config now.
  const buildDistanceCapture = useCallback(
    (dayId: string): RpeDistanceCapture | null => {
      if (!actualDistanceCaptureOn) return null;
      const day = weekly.data?.days.find((d) => d.id === dayId);
      if (!day || day.rest) return null;
      // B309 fix round: `day.mileage` is miles for every athlete, so a km
      // athlete was asked "What did you actually run?" in miles (and the log was
      // stored as miles). `mileageInUnit` carries the planned distance in the
      // athlete's own unit ("10.2 km"); a mile athlete has none and reads
      // `mileage` exactly as before. A day with no distance to prefill (a
      // time-based session) falls back to the athlete's unit, not to miles.
      const m = /(\d+(?:\.\d+)?)\s*(mi|km)\b/i.exec(day.mileageInUnit ?? day.mileage ?? '');
      const plannedValue = m ? Number(m[1]) : null;
      const unit: 'mi' | 'km' = m ? (m[2].toLowerCase() === 'km' ? 'km' : 'mi') : displayUnit;
      return { plannedValue, unit };
    },
    [actualDistanceCaptureOn, weekly, displayUnit],
  );
  // W1-TODAY-WIRE (TWR-7, SEAMS EFFORT) — the three values the 2.0 effort sheet
  // takes beside the legacy ones. Not a hook: a plain function of values held
  // above. Flag OFF → `{}`, so the sheet mounts with exactly the props it always
  // had. `sessionLabel` is string 76's pattern ("Easy run · 7 km") from the same
  // week row the day panel reads; `plannedDistance` is the capture's own planned
  // value and unit, so "Planned 7 km" can never disagree with the field's
  // prefill, and it is null wherever the distance question does not exist.
  const rpeSheetV2Props = (
    dayId: string,
    capture: RpeDistanceCapture | null,
  ): { variant?: 'v2'; sessionLabel?: string | null; plannedDistance?: RpePlannedDistance | null } => {
    if (!todayV2On) return {};
    const day = weekly.data?.days.find((d) => d.id === dayId);
    return {
      variant: 'v2',
      sessionLabel: day ? daySessionLine(day, ' · ') : null,
      plannedDistance:
        capture && capture.plannedValue != null
          ? { value: capture.plannedValue, unit: capture.unit }
          : null,
    };
  };

  const lastDashboardVisit = athleteContext.raw._last_dashboard_visit;
  // currentWeekNumber is computed above (threaded into useWeeklyPlan).
  const compliance = computeThisAndLastWeekCompliance(
    seasonPlans.data ?? [],
    currentWeekNumber,
  );
  // B-25 — clamp the walk to days that have occurred: without the
  // currentWeek/today bounds, batch-drafted future weeks (and the not-yet-run
  // remainder of this week) break the streak at the first upcoming day.
  // after_season guard (review amendment): once the season is over, today's
  // weekday is unrelated to the (finished) final week — a day clamp would
  // filter completed final-week days by calendar accident. Week clamp alone
  // walks the whole final week, which all occurred.
  const seasonWeekState = resolveCurrentWeek(macroPlan.data ?? [], null).state;
  const currentStreak = computeCurrentStreak(
    seasonPlans.data ?? [],
    currentWeekNumber,
    seasonWeekState === 'in_season' ? todayMondayBasedLocal() : null,
  );
  // NU-2 — plan-lifecycle input for the nudge trio (flag-gated; null
  // disables all three detectors → flag-off is byte-identical to PR 4e).
  // seasonState comes from resolveCurrentWeek with the SAME args
  // phaseFromMacroPlan uses, so state and currentWeekNumber can't disagree.
  // lastFired feeds the detectPlanNudge cap (pick the highest-priority
  // nudge that hasn't already fired for its debounce value).
  const lastFiredValue = (key: string): string | undefined => {
    const v = athleteContext.raw[key];
    return typeof v === 'string' ? v : undefined;
  };
  const planLifecycle: PlanLifecycleInput | null = planNudgesEnabled
    ? {
        seasonState: resolveCurrentWeek(macroPlan.data, null).state,
        currentWeekNumber,
        totalWeeks: macroPlan.data?.length ?? 0,
        draftedWeeks: draftedWeeksFromPlans(seasonPlans.data ?? []),
        weekDays: weekly.data?.days ?? null,
        lastFired: {
          draft_next_week: lastFiredValue('_proactive_last_draft_next_week'),
          unlogged_sessions: lastFiredValue('_proactive_last_unlogged_sessions'),
          season_wrap: lastFiredValue('_proactive_last_season_wrap'),
        },
      }
    : null;
  const proactive = useProactiveChecks({
    // NU-2 — when plan nudges are on, ALSO wait for the macro + weekly
    // plans to settle: the orchestrator burns its one-per-day run on its
    // first fire, and firing against unsettled lifecycle inputs would
    // skip the nudges for the whole day. Flag-off keeps the PR 4e gate
    // byte-identical (the extra clause short-circuits true).
    enabled:
      !!auth.user &&
      !athleteContext.loading &&
      !seasonPlans.loading &&
      (!planNudgesEnabled || (!macroPlan.loading && !weekly.loading)),
    input: {
      lastDashboardVisit:
        typeof lastDashboardVisit === 'string' ? lastDashboardVisit : null,
      thisWeekCompliancePct: compliance.thisWeek,
      lastWeekCompliancePct: compliance.lastWeek,
      upcomingMeets: teamMeets.data ?? [],
      currentStreak,
      raceResults: raceResults.data ?? [],
      planLifecycle,
    },
  });

  // Motivational coach — fires `/api/motivation` (LLM, daily-cached on
  // athlete_context._daily_motivation). Gated on the auth + season
  // path being live so we don't fire for signed-out / first-run flows.
  //
  // PR 4f — when a proactive trigger fired this load (e.g.,
  // pr_celebration), proactive.primaryHint surfaces it, and
  // useMotivation weaves it into the LLM prompt as
  // "IMPORTANT CONTEXT: …" so the day's message themes around the
  // trigger. Vanilla parity with motivational-coach.js:152-155.
  const motivation = useMotivation({
    enabled: !!auth.user && !!season.data,
    todaySession: displaySession,
    race: race.data,
    proactiveHint: proactive.primaryHint
      ? { type: proactive.primaryHint.type, message: proactive.primaryHint.message }
      : null,
  });

  // Coach-intel Rung 1 — the Today echo (§11). Server-authored, fail-closed.
  // Called unconditionally (rules-of-hooks); the tier decision is made below.
  // W1-TODAY-FIX3: the state hook, so the cold-load gate can wait for the read.
  const { reaction: coachReactionToday, loading: coachReactionLoading } = useCoachReactionTodayState();

  // B-24 — real hero weather from the athlete's stored location (Settings
  // Location modal → `_latitude`/`_longitude`). Fail-silent + non-blocking:
  // null until resolved (or forever, when no location / any fetch failure)
  // → the hero meta weather row is simply absent. The demo path
  // (?user=ramiro) never reaches TodayRealData and keeps its fixture.
  // B15 — `contextCoord` coerces a stored numeric STRING the way the server
  // does; the strict typeof-number read here is what hid the weather row.
  const heroWeather = useHeroWeather({
    enabled: !!auth.user && !!season.data,
    latitude: contextCoord(athleteContext.raw._latitude),
    longitude: contextCoord(athleteContext.raw._longitude),
  });
  // Ask 3 (strings 12 to 14, frame 3a) — with no stored location the hero's Weather slot
  // asks for one. `weather_apple_v1` is the provider's own flag: with it off there is no
  // weather to promise. The context counts as read only for THIS account and after a
  // settled, successful read (an empty row that failed to load is not "no location").
  const weatherApiOn = useServerFlag('weather_apple_v1');
  const promptForLocation = shouldPromptForLocation({
    todayV2On,
    weatherOn: weatherApiOn,
    contextReady: !athleteContext.loading && !athleteContext.loadFailed && athleteContext.rawIsMine,
    raw: athleteContext.raw,
  });

  // Phase 7 PR 1 — Onboarding gate (effect-driven, write-once).
  //
  // Goal: a signed-in athlete with no `_onboarding_completed_at` on the
  // server MUST land on /v2/onboarding (not /v2/today) so they complete
  // journey selection + PAR-Q before the dashboard renders. Special
  // case: existing React-preview athletes who completed onboarding ONLY
  // in localStorage need a one-shot migration write before redirect
  // (otherwise the gate would loop them through onboarding again).
  //
  // State machine — see plan doc §"Today onboarding gate state machine"
  // for full rationale + render-order discussion.
  type GateState =
    | { kind: 'idle' }
    | { kind: 'checking_local' }
    | { kind: 'migrating' }
    | { kind: 'migrated' }
    | { kind: 'failed' }
    | { kind: 'no_local' }
    // B-28 — server marker + local blob both missing, but the account has
    // an ACTIVE SEASON: lost-marker account, treated as onboarded (falls
    // through to the dashboard; the marker persist fires in the background).
    | { kind: 'season_evidence' };
  const [gate, setGate] = useState<GateState>({ kind: 'idle' });
  // CG-8 — coach detection for the gate below (cached; zero fetches
  // while coach_home_v1 is off).
  const coachMode = useCoachMode();
  // Write-once guard shared by BOTH marker-persist paths (localStorage
  // migration + B-28 season-evidence self-heal — mutually exclusive
  // branches): at most ONE completion write per mount, even on re-runs.
  const migrationFiredRef = useRef(false);
  const { persistOnboardingComplete } = usePersistOnboarding();

  useEffect(() => {
    if (athleteContext.loading) return;
    if (!auth.user) return;
    // A FAILED context load means `raw` is empty-because-UNKNOWN
    // (offline/network) — the render gate's P5 guard already excludes it
    // from needsOnboardingGate, and no marker persist may fire off an
    // empty `raw` either: the write sends `context: { ...raw, ...markers }`
    // alongside the patch, and if the server's context_merge_writes flag
    // read fails closed, the wholesale path would REPLACE context_data
    // with just the marker keys — a total context wipe of the very
    // account being protected. No gate work at all on loadFailed.
    if (athleteContext.loadFailed) return;
    if (athleteContext.raw._onboarding_completed_at) return; // server wins
    if (gate.kind !== 'idle') return; // already in flight or settled

    // B-28 — the no-blob decision below needs the active-season evidence,
    // and a transient not-yet-settled null must never read as "no season"
    // (useActiveSeason.settled contract: consumers that redirect on the
    // absence of a season MUST gate on `settled`). Waiting keeps the gate
    // 'idle' → skeleton; no wizard flash, no premature redirect.
    if (!season.settled) return;
    // A season-QUERY error is evidence-UNKNOWN, not "no season" — deciding
    // on it would wizard-bounce a lost-marker athlete on any transient
    // failure (the exact incident class). Stay 'idle' → skeleton; the next
    // successful load decides. (A settled success never carries an error.)
    if (season.error && !season.data) return;
    // The heal decision below also needs the (cached) coach-membership
    // verdict: a CG-8 membership-detected coach (undeclared role) who owns
    // a legacy season must not be marker-healed into the athlete dashboard.
    // Waiting keeps 'idle'; the render's coachMode branches already hold
    // the skeleton / route coaches to /team in exactly this window.
    if (coachMode.loading) return;

    // Server completion is missing — check localStorage migration path.
    // Keyed on the CURRENT user id: a completed blob left behind by a
    // different (e.g. deleted) account in this browser is treated as
    // absent, so a fresh signup never inherits its completion — it gets
    // the wizard, no completion flag synced up (fillers.md "Stale
    // localStorage re-onboards a fresh signup").
    setGate({ kind: 'checking_local' });
    const persisted = loadPersistedStateForUser(auth.user.id);
    if (!persisted || !isOnboardingComplete(persisted)) {
      // B-28 self-heal — marker missing AND no completed local blob, but
      // the account HAS an active season. A season only exists for an
      // athlete who got through setup, so this is a lost-marker account
      // (context row rebuilt / localStorage cleared), NOT a new athlete —
      // never route them into the wizard over a full season of data
      // (founder GO 2026-07-19).
      //
      // B47 (founder ruling 2026-09-09, decision 2) — SEASON EVIDENCE IS NO
      // LONGER ENOUGH ON ITS OWN. The old shape fired a MARKER-ONLY persist
      // (`persistOnboardingComplete({}, { markerOnly: true })`) that stamped
      // `_onboarding_completed` with NO journey and NO PAR-Q behind it: the
      // account then read as onboarded everywhere while the coach had no
      // routing signal and the clinical gates had no screening. The marker
      // now leaves this client only when the local blob carries the journey
      // AND every PAR-Q answer (`hasCompletionAnswers`), and it leaves inside
      // the FULL payload — the same atomic POST the wizard sends. A blob that
      // is short of that falls through to 'no_local' → `/onboarding`, which
      // hydrates from that same blob; it never loops, because nothing wrote
      // the marker the wizard's bounce-back reads. Clinical inputs are never
      // inferred, so a missing PAR-Q answer is asked for, never assumed.
      //
      // Declared guardian/coach accounts are excluded (belt-and-
      // suspenders: the server already refuses season creation for those
      // roles — isSeasonWriteRefusedForRole — so they shouldn't own a
      // season; a legacy row must not mark a non-athlete "onboarded").
      // Their render branches above route them to /guardian and /team;
      // ditto the CG-8 membership-detected coach (coachMode, settled above).
      const declaredRole = athleteContext.raw._account_role;
      if (
        season.data &&
        persisted &&
        hasCompletionAnswers(persisted.answers) &&
        declaredRole !== 'guardian' &&
        declaredRole !== 'coach' &&
        !coachMode.isCoach
      ) {
        if (!migrationFiredRef.current) {
          migrationFiredRef.current = true;
          persistOnboardingComplete(persisted.answers, {
            completedAt: BLOB_SCREENING_DATE,
          }).catch((err) => {
            // Best-effort: the season row plus the athlete's own answers
            // already gate this session in, and bouncing a full-season
            // athlete into the wizard on a transient POST failure is the
            // exact B-28 incident. A failed persist just re-heals next load.
            // eslint-disable-next-line no-console
            console.warn('[TodayRealData] onboarding self-heal persist failed:', err);
          });
        }
        setGate({ kind: 'season_evidence' });
        return;
      }
      setGate({ kind: 'no_local' });
      return;
    }

    // B47 fix 2 (round-15 security 1, the B108 hand-off) — THE MIGRATION WRITE
    // IS GUARDED BY THE SAME PREDICATE AS THE SELF-HEAL.
    //
    // `isOnboardingComplete` above is step 7 plus a journey and nothing else,
    // so a blob at the welcome screen with an EMPTY PAR-Q bag reached this
    // write and POSTed a completion for an athlete who was never screened.
    // Post-B108 the payload can no longer CLAIM the screening, but it would
    // still write a partial patch and then admit the athlete to a dashboard the
    // server considers un-onboarded, re-POSTing on every load. One rule for
    // both paths: the answers, or the wizard — which hydrates from this same
    // blob and asks for exactly what is missing.
    if (!hasCompletionAnswers(persisted.answers)) {
      setGate({ kind: 'no_local' });
      return;
    }

    // Migration write — fire EXACTLY ONCE even if the effect re-runs.
    if (migrationFiredRef.current) return;
    migrationFiredRef.current = true;
    setGate({ kind: 'migrating' });
    persistOnboardingComplete(persisted.answers, { completedAt: BLOB_SCREENING_DATE })
      .then(() => setGate({ kind: 'migrated' }))
      .catch((err) => {
        setGate({ kind: 'failed' });
        // eslint-disable-next-line no-console
        console.warn('[TodayRealData] onboarding migration failed:', err);
      });
  }, [
    athleteContext.loading,
    athleteContext.loadFailed,
    athleteContext.raw._onboarding_completed_at,
    athleteContext.raw._account_role,
    auth.user,
    coachMode.loading,
    coachMode.isCoach,
    gate.kind,
    persistOnboardingComplete,
    season.settled,
    season.data,
    season.error,
  ]);

  /**
   * ⚖️ B47 fix 10 (round-52 critic F1) — A PRESS IS THE SURFACE'S OWN BEAT.
   *
   * The retry surface below opens for a failed SEASON read too now, and its
   * press re-runs that read. `season.refetch()` clears `error` and raises
   * `loading` in the same batch, so the branch that opened the surface stops
   * being true and the skeleton guard above it would catch the press and tear
   * the surface down mid-gesture: the beat lost, the escalation sentence
   * unreachable, focus dropped, and a control-free skeleton in place of the one
   * thing she pressed. That is the tear-down fix 2 removed for the context
   * read, and this is the same answer for the season read — while a press of
   * hers still has a read out, those reads are HERS and the surface stays.
   *
   * ⚠️ It is not "a season read is running": nothing that started on its own
   * may hold this. The latch is set by the press handler and cleared the moment
   * both reads are done, so the post-create refetch (`startSeason.onComplete`)
   * and every other later load meet exactly the frame they always met.
   */
  const [seasonRetryPressed, setSeasonRetryPressed] = useState(false);
  useEffect(() => {
    if (!athleteContext.loading && !season.loading) setSeasonRetryPressed(false);
  }, [athleteContext.loading, season.loading]);

  // Auth still loading — show skeletons (no flash of demo content).
  if (auth.loading) {
    return isDesktop ? <DashboardSkeletonDesktop /> : <DashboardSkeletonMobile />;
  }

  // B47 (founder ruling 2026-09-09, decision 1) — a FAILED context read is
  // empty-because-UNKNOWN, and this route used to render the DASHBOARD off
  // that emptiness: `needsOnboardingGate` excluded `loadFailed`, so the gate
  // fell through and an athlete whose read (or whose completion save) had
  // just failed was treated as onboarded, with no retry anywhere. Nothing
  // about this athlete is known here, so the route decides NOTHING — not
  // onboarding state, not season state — and holds on a quiet retry surface
  // instead. `loadFailed` stays true for the whole in-flight retry (it is
  // cleared only by a read that SUCCEEDS), so the surface holds across the
  // reload rather than flashing a dashboard it cannot fill.
  //
  // B47 fix 2 — it also returns ABOVE the season-loading skeleton now: the
  // retry refetches the season too, and a held surface that tore itself down
  // into a skeleton on every retry would be the same flash by another name.
  if (auth.user && athleteContext.loadFailed) {
    return (
      <TodayContextRetry
        isDesktop={isDesktop}
        // Both reads: the athlete is waiting on the pair, not on one of them.
        // ⚖️ B47 fix 4 — this is "a read is running", not "a retry is running".
        // The surface turns it into a retry only after a press.
        readsInFlight={athleteContext.loading || season.loading}
        onRetry={() => {
          // ⚖️ B47 fix 2 (round-15 critic 5) — RETRY RE-RUNS BOTH READS. In the
          // outage that produced this surface the season query failed too, and
          // reloading the context alone could clear `loadFailed` while
          // `season.data` was still the failure's null — landing an onboarded
          // athlete on "Start your season" with a season already in the row.
          void athleteContext.reload();
          void season.refetch();
        }}
      />
    );
  }

  // Either read still in flight — hold the skeleton. B47 fix 2 (round-15
  // critic 3): `needsOnboardingGate` is false while the context is loading, so
  // this route used to fall straight through to `TodayNoSeason` and offer
  // "Start my season" in the frames before the verdict landed. Nothing about
  // this athlete is known yet, so nothing that CREATES data may be offered.
  //
  // ⚖️ B47 fix 3 (round-20 critic F1) — THE CONTEXT CLAUSE COSTS ONLY THE CTA.
  // `auth.user && athleteContext.loading` alone withheld the WHOLE dashboard
  // from an athlete who already has a season: the seasons select answers in
  // 300 ms, the context select stalls on a half-open socket, and a stall never
  // throws, so `loadFailed` never sets and the retry surface above never
  // opens — she sits on a skeleton with a season in hand. The thing that must
  // not render before the verdict is the season-CREATING CTA, and that branch
  // is reached only when `!season.data`, so the clause is scoped to it. The
  // season clause is untouched (it predates B47): a season read in flight is
  // the same unknown it always was.
  //
  // ⚖️ B47 fix 10 (round-52 critic F1) — AND NEVER OVER HER OWN PRESS. The
  // retry surface below re-runs BOTH reads, so both clauses here go true on the
  // press that is meant to keep her on the surface, and the skeleton caught it
  // first: the surface unmounted under the finger that pressed it. A press is
  // answered on the surface it was made from (`seasonRetryOut`, set by the
  // press handler alone), so the frame falls through to that surface instead.
  // The condition itself is untouched, and so is the `season_evidence` pin that
  // reads this line (`onboardingSelfHeal.b28.test.ts`).
  const seasonRetryOut = seasonRetryPressed && (athleteContext.loading || season.loading);
  if (season.loading || (auth.user && athleteContext.loading && !season.data)) {
    if (!seasonRetryOut) {
      return isDesktop ? <DashboardSkeletonDesktop /> : <DashboardSkeletonMobile />;
    }
  }

  // Phase 7 PR 1 — Onboarding gate render branches.
  // CRITICAL ORDER: the idle-with-onboarding-needed guard must come
  // FIRST (before the not-signed-in branch's first-run hero would
  // render), otherwise the dashboard / setup-first hero can briefly
  // flash before the effect transitions gate.kind off 'idle'.
  const needsOnboardingGate =
    !athleteContext.loading &&
    !!auth.user &&
    // P5 offline boot: a FAILED context load means empty-because-unknown
    // (offline/network) — never route an existing athlete into the wizard
    // as a "new user" (founder device test 2026-07-09). B47 — this clause is
    // now unreachable (the held retry surface above returns first) and stays
    // as the second lock on the same rule: a failed read decides nothing.
    !athleteContext.loadFailed &&
    !athleteContext.raw._onboarding_completed_at;

  // CG-7 — a declared guardian account (onboarding step-0 fork) is not
  // an un-onboarded athlete: its home is /guardian, never the wizard.
  if (needsOnboardingGate && athleteContext.raw._account_role === 'guardian') {
    return <Navigate to="/guardian" replace />;
  }

  // P7(a) — a coach-provisioned account (onboarding "Set up your team" fork)
  // is not an un-onboarded athlete: its home is /team. Routes immediately on
  // the durable role flag, without waiting for the coach-mode team fetch.
  if (needsOnboardingGate && athleteContext.raw._account_role === 'coach') {
    return <Navigate to="/team" replace />;
  }

  // CG-8 — a coach who trains a team but never onboarded as an athlete
  // lands on their team home, not the athlete wizard. Wait for the
  // (cached) check rather than flashing the wizard at a coach.
  if (needsOnboardingGate && coachMode.loading) {
    return isDesktop ? <DashboardSkeletonDesktop /> : <DashboardSkeletonMobile />;
  }
  if (needsOnboardingGate && coachMode.isCoach) {
    return <Navigate to="/team" replace />;
  }

  if (needsOnboardingGate && gate.kind === 'idle') {
    // One-frame flash guard.
    return isDesktop ? <DashboardSkeletonDesktop /> : <DashboardSkeletonMobile />;
  }
  if (needsOnboardingGate && (gate.kind === 'no_local' || gate.kind === 'failed')) {
    return <Navigate to="/onboarding" replace />;
  }
  if (needsOnboardingGate && (gate.kind === 'checking_local' || gate.kind === 'migrating')) {
    return isDesktop ? <DashboardSkeletonDesktop /> : <DashboardSkeletonMobile />;
  }
  // needsOnboardingGate === false (server flag set or onboarding migrated),
  // or gate.kind is 'migrated' / 'season_evidence' (B-28 self-heal)
  // → fall through to existing render logic.

  // ⚖️ B167 (founder ruling 2026-09-17, decision X) — NO CLIENT IS NOT A
  // SIGN-OUT. `!auth.user` used to mean one thing on this route — nobody has
  // signed in yet, so paint the first-run hero and invite setup. But the
  // provider reports the same falsy `user` when `/api/config` never answered
  // at all: there is no client to sign anyone in WITH, and the hero's "Let's
  // build your first week" is a promise the app cannot keep. A real sign-out
  // (a live client, no session) still falls through to the hero below
  // unchanged; a dead config holds on the quiet surface instead.
  //
  // ⚖️ B167 fix 2 (post-deploy critic finding 2) — AND THE HOLD COVERS THE
  // RE-ASK ITSELF, NOT ONLY ITS EXHAUSTED END. Gating on `auth.configExhausted`
  // alone missed the window between cycle 1 failing and cycle 2 (the
  // provider's own automatic re-ask) settling: `auth.loading` is already false
  // by then (the catch sets it before starting the re-ask) and
  // `configExhausted` isn't true yet, so the old check fell through to the
  // plain `!auth.user` branch below and painted the first-run hero for the
  // length of cycle 2. `auth.error` is narrowed to config failures only
  // (fix-3) and is set for that whole window AND for the exhausted end alike
  // — `!auth.supabase && auth.error` is a strict superset of the old
  // `configExhausted` gate that also catches the gap.
  if (!auth.user && !auth.supabase && auth.error) {
    return <TodayConfigHold isDesktop={isDesktop} />;
  }

  // B135 — a real sign-out (a live client, no session) redirects to landing
  // like every other guarded route (`settings.tsx`, `plan.tsx`, `chat.tsx`),
  // rather than painting a personalised-looking dashboard (an avatar letter,
  // weather, a streak row) whose only CTA lands on landing anyway with no
  // explanation. Mirrors `settings.tsx`'s native/web split.
  if (!auth.user) {
    if (landing2On && !isNativeApp()) return <Navigate to={SIGN_IN_LANDING_PATH} replace />;
    return <Navigate to={isNativeApp() ? '/welcome' : '/landing'} replace />;
  }

  // ⚖️ B47 fix 9 (round-46 critic S5) — AN UNKNOWN SEASON IS NOT NO SEASON.
  //
  // Fix 8 gave the season read a 10 s deadline, and a deadline settles:
  // `loading` false, `data` null, `error` set. On a cold boot where the context
  // read answers in 300 ms and the `seasons` read is accepted and never
  // answered, `loadFailed` is false, so the retry surface above never opens —
  // and the branch below then offered "Start your season" to an athlete whose
  // season is simply unknown. The self-heal at `:923` already refuses to decide
  // on that exact state (`if (season.error && !season.data) return;`); the
  // render has to refuse too, and for the same reason as the comment above:
  // nothing that CREATES data may be offered before the verdict. (B152's Today
  // twin; the `/plan` and `plan-draft` twins are not this lane's.)
  //
  // ⚖️ B47 fix 10 (round-52 critic F1) — AND THE REFUSAL HAS A WAY OUT.
  //
  // Fix 9 refused with the SKELETON, and that refusal never ended. The
  // predicate is EVERY failure of the `seasons` read, not only the deadline —
  // a PostgREST 500, an RLS refusal, a connection that resolves `{ error }` at
  // 200 ms — and nothing on that screen could ask again: `season.refetch()` is
  // reachable only from the CTA this branch suppresses and from the retry
  // surface above, which opens on `loadFailed` alone, and no visibility refetch
  // touches the season. One transient error and an athlete with no season could
  // not reach "Start my season" at all any more, while three nested live
  // regions announced "Loading" for the rest of the session (driven at five
  // minutes).
  //
  // So: the same quiet line and the same one control the failed context read
  // has had since fix 2 — no new copy and no second shape to learn — and its
  // press re-runs the reads beside the role fact and the team check this
  // surface already re-asks. What does NOT move: the branch still sits below
  // the identity branches, so a coach or a guardian is routed to their own home
  // before any athlete frame is painted, and "Start my season" is still never
  // offered on an unknown season.
  if ((season.error && !season.data) || seasonRetryOut) {
    return (
      <TodayContextRetry
        isDesktop={isDesktop}
        readsInFlight={athleteContext.loading || season.loading}
        onRetry={() => {
          // The latch that keeps this surface up for the length of its own
          // press (see `seasonRetryOut`).
          setSeasonRetryPressed(true);
          // Both reads, exactly as the failed-context press does: the outage
          // that fails one read fails the other, and a context reload that
          // succeeds while the season is still unknown lands on this branch
          // again anyway.
          void athleteContext.reload();
          void season.refetch();
        }}
      />
    );
  }

  // B129/B132 fix round (UX blocking 1) — the no-season card must not paint
  // a LIVE "Start my season" before the arrival hold fact is known: season
  // and tier are two uncoordinated reads, and season answers first on the
  // common path, so `guardianHeld` above briefly read `false` for a held
  // account until `userTier` caught up a moment later — a visible flicker
  // from live CTA to resting hold line. Scoped exactly like B47 fix 3 scoped
  // the context clause: only the season-CREATING branch below waits on it
  // (this line is never reached once `season.data` exists), and
  // `useUserTier`'s own deadline (10s) bounds the wait so a hung tier read
  // degrades to the pre-B129 refusal-time line instead of stalling here.
  if (!season.data && userTier.loading) {
    return isDesktop ? <DashboardSkeletonDesktop /> : <DashboardSkeletonMobile />;
  }

  // Season 2c — signed in but NO active season → "Start your season" CTA
  // (was: the generic first-run surface with no way to create a season).
  // One click creates the season + generates its plan; onComplete refetches
  // the season so this view flips to the real dashboard.
  if (!season.data) {
    return (
      <TodayNoSeason
        isDesktop={isDesktop}
        meetCount={race.data ? 1 : 0}
        onStartSeason={() => void startSeason.start()}
        starting={startSeason.starting}
        startPhase={startSeason.phase}
        // R71 / B99 / B196 L8 — the control settles back to REST for any
        // coded refusal: no red block, no server sentence, no "Try again"
        // that can never work. Every OTHER failure keeps the block it had.
        startError={
          guardianHeld ||
          ageBelowMinimumHeld ||
          ageBandRequiredHeld ||
          ageReviewPendingHeld ||
          startRefused
            ? null
            : startSeason.error
        }
        refused={startRefused}
        held={guardianHeld}
        ageBelowMinimum={ageBelowMinimumHeld}
        ageBandRequired={ageBandRequiredHeld}
        ageReviewPending={ageReviewPendingHeld}
        navUser={deriveSignedInUser(auth.user, athleteContext.raw)}
      />
    );
  }

  // F-509 — the right-rail race panel derives its phase label from the ACTUAL
  // macro phase for the current week (the same macro rows the season rail
  // reads), not the race-date heuristic default baked into `adaptToRace`
  // (which is always 'Build'). Single source of truth: the panel can no longer
  // read BUILD while the season rail reads BASE. Null (no macro week matches
  // today) → keep the race's own phase. The countdown (daysAway/date) is
  // untouched.
  const macroRacePhase = racePhaseFromMacroPlan(macroPlan.data);
  const raceWithMacroPhase: Race | null = race.data
    ? macroRacePhase
      ? { ...race.data, phase: macroRacePhase }
      : race.data
    : null;

  // Compose the LLM-generated motivational line into the Race object.
  // The card hides the motivational block when motivationalLine is
  // falsy, so the loading + error paths render cleanly without a fake
  // placeholder.
  const raceWithMotivation: Race | null = raceWithMacroPhase
    ? motivation.data?.message
      ? { ...raceWithMacroPhase, motivationalLine: motivation.data.message }
      : raceWithMacroPhase
    : null;

  // CI-2 — production CoachCard (desktop) + CoachStories (mobile) source
  // from the PURE coach-insight rules engine (coachInsightRules.ts), gated
  // on the frontend `coach_insights` flag (default OFF until CI-3). Every
  // insight cites only data the hooks above already loaded. Held back
  // (null) until every input settles, so a quiet surface always means
  // "nothing real to say" — never "not loaded yet". Flag off → null/null =
  // exact prior behavior (empty CoachCard slot, demo-only CoachStories).
  // The demo path (?user=ramiro) dispatches before TodayRealData and keeps
  // its fixtures.
  const insightsLoading =
    todaySession.loading ||
    weekly.loading ||
    race.loading ||
    macroPlan.loading ||
    seasonPlans.loading ||
    raceResults.loading;
  const coachRules =
    coachInsightsEnabled && !insightsLoading
      ? evaluateCoachRules({
          session: displaySession,
          week: weekly.data,
          race: race.data ? { name: race.data.name, daysAway: race.data.daysAway } : null,
          currentWeekNumber,
          totalWeeks: macroPlan.data?.length ?? 0,
          draftedWeeks: draftedWeeksFromPlans(seasonPlans.data ?? []),
          currentStreak,
          thisWeekCompliancePct: compliance.thisWeek,
          lastWeekCompliancePct: compliance.lastWeek,
          raceResults: raceResults.data ?? [],
          todayDayOfWeek: todayMondayBasedLocal(),
          batchDraftAvailable: batchAuthoringOn,
        })
      : null;
  const coachMessage = coachRules ? toCoachMessage(coachRules) : null;
  const coachInsights = coachRules ? toCoachInsights(coachRules) : null;

  // Phase 6c PR 7 — map the deduped pills to ContextItem[] for the
  // Ultra rail card. Returns null when empty → PinnedContext returns
  // null → card hides cleanly. Demo path (?user=ramiro) doesn't
  // pass through here, so it keeps the ramiroContext fixture.
  const contextItems = pillsToContextItems(athleteContext.pills);
  const realDataContextItems = contextItems.length > 0 ? contextItems : null;

  // Active season — render happy path with hook data + per-section
  // skeletons for any sub-hook still loading.
  //
  // Phase 6 cross-cutting PR 6 — thread `proactive.primaryHint?.type`
  // into both happy-path components so the ChampionshipCard's moti
  // block renders the `RELATED · <type-readable>` kicker + `--today`
  // left-rule when a trigger fired today. This is the user-visible
  // side of PR 4f's LLM prompt biasing (the message themes around the
  // trigger; the kicker tells the user why).
  const proactiveType = proactive.primaryHint?.type ?? null;

  // Coach-intel Rung 1 — the reaction echo slots at the DAILY-MOTIVATION tier
  // (§11): it MAY replace the motivation line, and never touches the top slot
  // (TeamInviteBanner).
  //
  // ⚖️ REL-13 (founder-ruled 2026-08-16) — IT NO LONGER YIELDS TO EVERYTHING.
  // This was `!proactiveType && …`, one `!` that treated a PR celebration and a
  // "time to draft week 4" nag as equally entitled to silence a post-run note.
  // On a run day the note WINS over the chore; celebrations and safety signals
  // still win over the note. The whole decision, and the reasoning the memo
  // paid for, lives in `lib/heroCoachLine.ts` — it is a pure function precisely
  // so it can be tested, which the inline conjunct never was.
  const hero = resolveHeroCoachLine({
    proactiveType,
    reaction: coachReactionToday,
    motivationLine: motivation.data?.message,
  });
  const heroCoachLine = hero.line;
  const heroCoachHref = hero.href;

  // Phase 7 PR 1 — Derive the SideNav me-block user from the actual
  // signed-in identity. Precedence: athleteContext.raw.Name (set
  // during Settings or vanilla onboarding) → auth.user user_metadata
  // (Google OAuth's full_name) → email-before-@ → "Athlete" fallback.
  // Surfaced by user during waisports test-account smoke
  // 2026-05-15: SideNav had been showing "Ramiro" regardless of who
  // signed in, because TodayHappyPathDesktop hard-coded the `ramiro`
  // fixture.
  const signedInUser = deriveSignedInUser(auth.user, athleteContext.raw);

  // Season-model Phase 3 — which weeks have a DRAFTED weekly plan. Reuses the
  // seasonPlans fetch already loaded above (streak / compliance), funnelled
  // through the shared `draftedWeeksFromPlans` invariant — so Today and /v2/plan
  // agree on drafted-vs-undrafted and Today pays for no extra query. Held back
  // (undefined) until the fetch settles so the SeasonGlance strip never flashes
  // "all undrafted" before the data lands.
  const draftedWeeks = seasonPlans.loading
    ? undefined
    : draftedWeeksFromPlans(seasonPlans.data ?? []);

  // B-23 — hold the hero skeleton until the empty-vs-loop truth is KNOWN.
  // useTodaySession reports loading:false when the current week number is
  // merely unresolved (macroPlan still in flight → weekNumber null), which
  // made every cold boot flash the first-run "Let's build your first week."
  // hero over a real season before the loop hero replaced it (founder
  // report 2026-07-19). The empty-hero picker needs macroPlan (before-season
  // / lifecycle variants) and seasonPlans (drafted weeks) settled to pick
  // correctly — but ONLY while there's no loaded session: seasonPlans
  // refetches after every day-completion toggle (streak/compliance), and an
  // unscoped gate would unmount a live loop hero into a skeleton mid-
  // celebration on every tap (adversarial-review catch). A loaded hero
  // never blanks; the skeleton holds only while empty could still mean
  // "still resolving".
  const heroLoading =
    todaySession.loading ||
    (!todaySession.data && (macroPlan.loading || seasonPlans.loading));

  // W1-TODAY-FIX1 (F1, F3) — the 2.0 cold-load gate's other reads. `heroLoading` above
  // waits on the macro plan and the season's plans only while there is no session, which
  // is right for the legacy section skeletons and not for a gate that has to hold until
  // the 2.0 page can be painted whole: the Draft row needs the drafted weeks, the kicker
  // and the race card need the macro plan, the unit and the weather row need the athlete
  // context and the stored location's fetch. (Today's own Apple Health read joins them
  // in the happy path, through `HealthSyncProvider`.) The unit and the weather belong to
  // the 2.0 hero, so they are waited for only once there is a session to show: a first
  // run or an undrafted week keeps its legacy look and never waits on a weather fetch.
  //
  // W1-TODAY-FIX2 (R4): the lines that can land after the swap and push the card join
  // the same gate while their reads are in flight (under the same ceiling, R3), and a
  // read that has settled empty reserves nothing. They are: the reflow or guardian
  // notice (`useReflowOffer`, one read for both), the two reads the proactive trigger is
  // detected from (`useTeamMeets`, `useRaceResults`), and the daily line the proactive
  // "RELATED" line prints (`useMotivation`; FIX4 narrowed it, see `waitsForMotivation`).
  //
  // W1-TODAY-FIX3: and the coach's post-run echo (`useCoachReactionTodayState().loading`:
  // true only while `coach_react_v1` is on and its first read is open, so a flag off or a
  // read that settled empty reserves nothing). It prints in the coach line above the week
  // card, so a late one pushed the card.
  // Flag off: the value is not read.
  //
  // W1-TODAY-FIX4 (finding 2): the reads are split in two, because the ceiling treats them
  // differently (`useColdLoadGate`). ESSENTIAL: the reads whose absence puts the legacy
  // chrome or a section skeleton on screen (the macro plan and the season's plans here;
  // the session, the week and the race in the happy path). OPTIONAL: everything else, whose
  // late arrival only adds a line to a page that is already the 2.0 page.
  const coldEssentialPending = macroPlan.loading || seasonPlans.loading;
  // W1-TODAY-FIX4 (finding 3): the daily line is waited for only when it will print and
  // nothing else can take its place. With a CHORE trigger the post-run echo outranks it
  // when there is one (and the chore waits its turn when there is not), so the line may
  // land after the swap; with no trigger the 2.0 coach line prints nothing from it.
  const waitsForMotivation =
    !!proactiveType && !isChoreTrigger(proactiveType) && !hero.echo && motivation.loading;
  const coldOptionalPending =
    !!todaySession.data &&
    (athleteContext.loading ||
      !!heroWeather.loading ||
      reflow.loading ||
      teamMeets.loading ||
      raceResults.loading ||
      coachReactionLoading ||
      waitsForMotivation);
  // The weather row's place in the skeleton (W1-TODAY-FIX2, R1): while the fetch runs AND
  // after it has resolved with a row to show. It used to be `loading` alone, so when
  // weather landed before the session or week reads (one hop against two or three) the
  // placeholder vanished while the gate was still closed and the week card jumped by the
  // row's height at the swap.
  const weatherRowPending = !!heroWeather.loading || !!heroWeather.data;

  // ⚖️ B99 fix 2 (round-17 critic F1) — THE SURFACE A HELD ATHLETE ACTUALLY
  // LANDS ON. Her create succeeded and the generate was refused, so the
  // no-season card has already unmounted; without this she reads the first-run
  // hero and nothing else, having just been told nothing. Same quiet anatomy
  // the /plan no-plan branch uses.
  //
  // ⚖️ B47 fix 3 (round-20 critic F2) — and the OTHER coded refusal the same
  // chain returns. `refused` reached `TodayNoSeason` only, so a 409
  // `replan_blocked_in_flight` on the generate half — after the create has
  // already unmounted that card — printed nothing here at all. Chained after
  // the hold, the way `EmptyMacroPlan` and both `/plan` surfaces chain them,
  // so the two never stack on one screen.
  //
  // ⚖️ B47 fix 4 (round-20 UX 1) — AND IT GOES INSIDE THE FRAME. It used to
  // render as a SIBLING before `{branch}`, and the branch owns the whole page:
  // its `.page` wrapper and, on mobile, the FIXED TopBar. So at 375 the line
  // laid out at y=10 to 46 UNDER that bar (z-index 5) and a held minor read a
  // 46px gap; at 1440 it rendered above the entire AppShell as a grey sentence
  // floating over the logo. jsdom paints nothing, so both tests passed. It is
  // a prop now: the branch drops it below the TopBar, above the hero it
  // explains, and there is one owner of the page frame again.
  //
  // ⚖️ B122 (founder ruling 2026-09-23) — AND THE ONE FAILURE THAT STILL SAID
  // NOTHING JOINS THEM. A real generate failure with no coded refusal used to
  // reach this exact surface and print nothing at all: the season had already
  // landed, `TodayNoSeason` had already unmounted, and the athlete read the
  // happy-path hero as if nothing had gone wrong. `ReplanRefusalLine` takes a
  // string only, so this line borrows its anatomy locally (a link belongs in
  // it) rather than growing new chrome of its own.
  const startHoldNotice =
    guardianHeld || startRefused || startGenericFailed ? (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 16px 12px' }}>
        {guardianHeld ? (
          <ReplanRefusalLine message={GUARDIAN_HOLD_MESSAGE} />
        ) : startRefused ? (
          <ReplanRefusalLine />
        ) : (
          <p className={styles.startBuildFailedLine} role="status">
            <span className={styles.startBuildFailedDot} aria-hidden="true" />
            {START_BUILD_FAILED_MESSAGE} <Link to="/plan">Go to Plan</Link> to try again.
          </p>
        )}
      </div>
    ) : null;

  /**
   * ⚖️ B47 fix 5 (round-26 critic F4) — THE OTHER TWO ROUTE-LEVEL LINES JOIN
   * IT, for the reason fix 4 moved the first one.
   *
   * `ReflowOfferCard` and the CG-3 guardian note rendered in the same slot the
   * hold line used to: siblings BEFORE `{branch}`, and the branch owns the
   * whole page — its `.page` wrapper and, on mobile, the FIXED TopBar. So at
   * 375 they laid out underneath that bar (z-index 5) and at 1440 above the
   * entire AppShell, floating over the logo. The CG-3 note is minor-facing:
   * the one sentence telling her why her plan did not change was the one the
   * bar covered. One `notice` prop, one owner of the page frame.
   *
   * Document order is unchanged (offer, then the guardian note, then the
   * post-create hold), and the three are mutually exclusive in practice: the
   * note renders only when there is no offer, and the hold line only on the
   * post-create surface.
   */
  const routeNotice = (
    <>
      {/* AD-2.1 — mid-week reflow offer. Renders only when the server surfaces an
          offer (flag + server-only store on); otherwise inert. NOTE: precise
          in-layout placement is a deferred design pass — see the handoff. */}
      {reflow.offer && (
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 16px' }}>
          <ReflowOfferCard
            offer={reflow.offer}
            onAccept={() => void reflow.accept().then(() => void weekly.refetch())}
            onDismiss={() => void reflow.dismiss()}
            onShown={() => void reflow.markShown('today')}
          />
        </div>
      )}
      {/* CG-3 — U16 guardian gate: the accepted adjustment is waiting on a
          guardian's confirmation. Passive note; the plan is unchanged. */}
      {!reflow.offer && reflow.held && (
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 16px' }}>
          <p
            role="status"
            style={{
              // TWR-8 — the ramp's bridge for the old 13px: computes to 13px with
              // `type_ramp_v1` off (RAMP pins it at :root), a body-small sentence on.
              fontSize: 'var(--ramp-bridge-13)',
              color: 'var(--ink-500)',
              background: 'var(--cream)',
              border: '1px dashed var(--ink-200)',
              borderRadius: 8,
              padding: '10px 14px',
              margin: '0 0 12px',
            }}
          >
            This week&rsquo;s adjustment was sent to your guardian to confirm.
            Your plan stays as-is until they approve it.
          </p>
        </div>
      )}
      {startHoldNotice}
    </>
  );

  // The open effort sheet's two derived props (the distance question's config,
  // and the 2.0 label values when `today_2_0_v1` is on). Evaluated once per render.
  const sheetDistanceCapture = rpeTarget ? buildDistanceCapture(rpeTarget.dayId) : null;
  const sheetV2 = rpeTarget ? rpeSheetV2Props(rpeTarget.dayId, sheetDistanceCapture) : {};

  // RPE restore — the prompt is fixed-position, so it renders ONCE here
  // beside whichever tier branch is active.
  const branch = isDesktop ? (
    <TodayHappyPathDesktop
      notice={routeNotice}
      coachState="active"
      coachMessage={coachMessage}
      coachLine={heroCoachLine}
      coachHref={heroCoachHref}
      raceState={race.data ? 'present' : 'none'}
      session={displaySession}
      weekPlan={weekIsStale ? null : weekly.data}
      race={raceWithMotivation}
      seasonName={season.data.name}
      macroPlanWeeks={macroPlan.data}
      draftedWeeks={draftedWeeks}
      contextItems={realDataContextItems}
      currentStreak={currentStreak}
      onToggleDay={handleToggleDay}
      onAddEffort={handleAddEffort}
      signedInUser={signedInUser}
      proactiveType={proactiveType}
      kudosEntries={realKudosEntries}
      lastSession={realLastSession}
      weather={heroWeather.data}
      sessionLoading={heroLoading}
      weekLoading={weekly.loading || weekIsStale}
      raceLoading={race.loading}
      v2={todayV2On}
      approvalStatus={macroPlan.status}
      coldEssentialPending={coldEssentialPending}
      coldOptionalPending={coldOptionalPending}
      weatherRowPending={weatherRowPending}
      promptForLocation={promptForLocation}
      gateMemory={coldGateMemory}
    />
  ) : (
    <TodayHappyPath
      notice={routeNotice}
      raceState={race.data ? 'present' : 'none'}
      session={displaySession}
      weekPlan={weekIsStale ? null : weekly.data}
      race={raceWithMotivation}
      seasonName={season.data.name}
      macroPlanWeeks={macroPlan.data}
      draftedWeeks={draftedWeeks}
      currentStreak={currentStreak}
      onToggleDay={handleToggleDay}
      onAddEffort={handleAddEffort}
      proactiveType={proactiveType}
      signedInUser={signedInUser}
      kudosEntries={realKudosEntries}
      contextItems={realDataContextItems}
      coachInsights={coachInsights}
      coachLine={heroCoachLine}
      coachHref={heroCoachHref}
      weather={heroWeather.data}
      sessionLoading={heroLoading}
      weekLoading={weekly.loading || weekIsStale}
      raceLoading={race.loading}
      v2={todayV2On}
      approvalStatus={macroPlan.status}
      coldEssentialPending={coldEssentialPending}
      coldOptionalPending={coldOptionalPending}
      weatherRowPending={weatherRowPending}
      promptForLocation={promptForLocation}
      gateMemory={coldGateMemory}
    />
  );

  return (
    <>
      {/* ⚖️ B47 fix 4 (round-20 UX 1) and fix 5 (round-26 critic F4) — the
          post-create hold line, the reflow offer and the CG-3 guardian note
          all used to render HERE, outside the frame the branch owns. They are
          `notice` on the branch now (derived above); nothing of Today's own
          may render beside `{branch}`, because `{branch}` is the page. The
          RPE prompt below is the one exception and is not an exception at
          all: it is `position: fixed` and belongs to the viewport. */}
      {/* W1-TODAY-FIX1 (F2, F3) — with `today_2_0_v1` on, the one reader of the Apple
          Health status sits above the page: the cold-load gate waits for it, and the
          hero's connect entry and the status row read it (one request, TWR-10). With the
          flag off the branch renders bare, as before, and `HealthHomeSlot` reads it. */}
      {/* W1-TODAY-COLDLOAD — the happy path is a different page with the flag on and with it
          off, and `todayV2On` is a bare false while the flag is UNANSWERED. Every read above
          can be settled before it is (a client booted from a 503's own env fields has no
          flags map yet), so with no cached value the gate shows the one neutral frame and
          builds neither page; it renders its children as they are otherwise (no element). */}
      <TodayFrameGate v2On={todayV2On}>
        {todayV2On ? (
          <HealthSyncProvider>
            <HeroWeatherPromptHost active={promptForLocation}>{branch}</HeroWeatherPromptHost>
          </HealthSyncProvider>
        ) : (
          branch
        )}
      </TodayFrameGate>
      {rpeTarget && (
        <RpePrompt
          workoutLabel={rpeTarget.label || null}
          onCommit={handleRpeCommit}
          onDismiss={() => setRpeTarget(null)}
          distanceCapture={sheetDistanceCapture}
          initialRpe={rpeTarget.rpe}
          {...sheetV2}
        />
      )}
    </>
  );
}

// ─── Demo-mode dispatch (preserves Phase 1 design preview) ───────────

const NEW_COACH_TRIGGERS: ReadonlySet<CoachMessageTriggerKind> = new Set<
  CoachMessageTriggerKind
>(['active', 'pattern', 'hello', 'mention', 'plan-shipped']);

/**
 * Phase 6c PR 6 — resolve `?coach=` URL param to a `CoachMessage` when
 * the param is one of the new 6c trigger kinds. Returns null for
 * legacy values (`selfcoach`, `newuser`, `onboarding_incomplete`) so
 * the legacy fixture path renders for those.
 *
 * For real-data mode (no URL override), returns the 'active' message
 * — the rules-engine fallback. When the rules engine ships, this
 * becomes a `useCoachMessage()` hook against the server-selected
 * record.
 */
function resolveCoachMessage(
  rawCoach: string | null,
  isDemoOverride = false,
): CoachMessage | null {
  const messages = mockCoachMessages();
  if (rawCoach && NEW_COACH_TRIGGERS.has(rawCoach as CoachMessageTriggerKind)) {
    return messages[rawCoach as CoachMessageTriggerKind];
  }
  // Legacy values (selfcoach / newuser / onboarding_incomplete) → null
  // so the legacy <CoachCardLegacy> fixture path renders.
  if (rawCoach) return null;
  // Default: active fallback for the design-preview demo path AND for
  // real-data path until the rules engine wires in.
  return isDemoOverride ? messages.active : messages.active;
}

function TodayDemo() {
  const { user, coach, race } = useQueryState();
  const [params] = useSearchParams();
  const rawCoach = params.get('coach');
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  if (user === 'newbie') {
    return isDesktop ? <TodayFirstRunDesktop /> : <TodayFirstRun />;
  }
  // Resolve the new CoachMessage from the raw URL param. For legacy
  // values it returns null and the route falls through to the
  // fixture-driven `coachState` path.
  const coachMessage = resolveCoachMessage(rawCoach, true);
  return isDesktop ? (
    <TodayHappyPathDesktop coachState={coach} coachMessage={coachMessage} raceState={race} />
  ) : (
    <TodayHappyPath raceState={race} />
  );
}

// ─── Loop-hero composition helper (PR 2) ───────────────────────────

/**
 * Compose the loop hero from the existing data shapes (TodaySession +
 * Race + WeeklyPlan + MacroPlanWeek[]) and render `<LoopHero>` — the
 * whole morning loop (session + reward strip + thumb CTA + readiness +
 * one coach line) in ONE TodayHero card, with the completion celebration.
 *
 * Lives here (not in todayHeroAdapter.ts) because it returns JSX — the
 * pure adapters live in lib/. Shared by the mobile + desktop happy-paths
 * so the composition rule stays one place.
 */
function renderLoopHero(args: {
  session: TodaySession;
  race: Race | null;
  /** The distance summary is present on a real-data week; the demo fixture has none. */
  weekPlan: (WeeklyPlan & Partial<WeekDistanceSummary>) | null;
  macroPlanWeeks: MacroPlanWeek[] | null;
  streakDays: number;
  /** B-24 — real weather (useHeroWeather) or the demo fixture. Null →
   *  the meta weather row is simply absent (composeHeroMeta prunes it). */
  weather: { tempDisplay: string; condition?: string; windMph: number } | null;
  /**
   * REL-8 — is the `weather` above REAL Apple data, rather than the
   * `?user=ramiro` fixture? Only the CALL SITE can answer: the demo
   * substitution happens there, and by the time the value arrives here the two
   * are indistinguishable. Drives the Apple Weather mark, so the fixture must
   * never set it — crediting Apple for a hardcoded fixture is a small lie told
   * inside a legal notice.
   */
  weatherFromApple?: boolean;
  athleteName: string;
  onToggleDay?: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /** RPE restore — reopen the effort prompt for today (hero recovery path). */
  onAddEffort?: (dayId: string, workoutLabel: string) => void;
  /** The daily coach voice line (`useMotivation().data?.message`, or the
   *  coach-reaction echo text when it takes the motivation tier — §11). */
  coachLine: string | null;
  /** Coach hue tap target — the coach chat; carries the run reference when the
   *  reaction echo is active (§11). Defaults to the generic coach chat. */
  coachHref?: string;
  proactiveType?: ProactiveTriggerType | null;
  /** SP-8 (today_density_v1) — density hero: drop the status footer (phase
   *  pill + week/countdown text + "Open week plan ›") + the eyebrow streak
   *  chip (all move to the Plan ledger / race card / reward strip, decision
   *  1-2), keep the ambient mode rule, adopt the unified 16px gutter (decision
   *  8), and micro-trim the reward strip (D4). Default false = byte-identical. */
  densityOn?: boolean;
  /** W1-TODAY-WIRE — the 2.0 hero (`today_2_0_v1`). `<LoopHero variant="v2">` sets
   *  `TodayHero`'s variant itself, so this is the only thing passed. The callers
   *  pass `densityOn: false` with it: density is ignored under the 2.0 flag, so the
   *  non-density `heroProps` (which carry `weekOfTotal`, the v2 kicker's source)
   *  are the ones built. Default false = byte-identical. */
  v2?: boolean;
}): ReactNode {
  const {
    session,
    race,
    weekPlan,
    macroPlanWeeks,
    streakDays,
    weather,
    weatherFromApple,
    athleteName,
    onToggleDay,
    onAddEffort,
    coachLine,
    coachHref = '/v2/chat',
    proactiveType,
    densityOn = false,
    v2 = false,
  } = args;
  const title = composeHeroTitle({
    lead: session.titleLead,
    em: session.titleEm,
    trail: session.titleTrail,
  });
  const macroDerived = phaseFromMacroPlan(macroPlanWeeks);
  const phase = macroDerived.phase ?? phaseFromLabel(session.phaseLabel) ?? undefined;
  const total = totalWeeksOf(macroPlanWeeks);
  const weekOfTotal: [number, number] | undefined =
    macroDerived.weekNumber && total > 0 ? [macroDerived.weekNumber, total] : undefined;
  const meta = composeHeroMeta({
    pace: session.intensityLabel,
    // B-24 — null weather (not resolved / no stored location) → null string
    // → composeHeroMeta drops the row. Condition is optional (unknown WMO
    // code): "57° overcast · wind 4" with it, "57° · wind 4" without.
    weather: weather
      ? `${weather.tempDisplay}${weather.condition ? ` ${weather.condition}` : ''} · wind ${weather.windMph}`
      : null,
    // CD visual polish §1 — the condition glyph, keyed off the SAME word the
    // value string prints. An unknown or absent word resolves to null and the
    // row renders exactly as it does today; there is no placeholder glyph. The
    // `?user=ramiro` demo fixture flows through here too, so it gets the glyph
    // for its fixed word without a second code path.
    weatherGlyph: weatherGlyphFor(weather?.condition),
  });

  // B-19 (frame §1-§8) — the Today-hero completed-state sub-line. Gated on the
  // SAME family as the sister surfaces: densityOn (today_density_v1, the density
  // hero the frame rides) + the `today` Day carrying `actual.hero`, which
  // useWeeklyPlan populates ONLY when isB19ActualsEnabled (b19_actuals_v1) AND a
  // real `actual_distance` exists (actual_distance_capture_v1). The extra
  // `completed` check honors §1/§3 ("mounts ONLY in the completed state"). Off on
  // any of these → undefined → byte-identical hero. Attached ONLY to the density
  // branch, so the flag-off (non-density) hero can never render it.
  const todayDay = weekPlan?.days.find((d) => d.today) ?? null;
  const completedSubline =
    densityOn && todayDay?.completionStatus === 'completed'
      ? todayDay.actual?.hero ?? undefined
      : undefined;

  const heroProps: TodayHeroDefaultProps = densityOn
    ? {
        variant: 'default',
        date: new Date(),
        athleteName,
        // Decision 2 — streak chip removed from the eyebrow (the reward strip
        // owns streak). `phase` stays so the ambient mode rule/dot keep their
        // hue; `suppressFooter` drops the footer that phase would otherwise
        // trigger (decision 1 — its facts move to the ledger/race card).
        title,
        meta,
        weatherAttribution: weatherFromApple,
        phase,
        suppressFooter: true,
        completedSubline,
      }
    : {
        variant: 'default',
        date: new Date(),
        athleteName,
        streakDays,
        title,
        meta,
        weatherAttribution: weatherFromApple,
        phase,
        weekOfTotal,
        daysToNextRace: race?.daysAway,
        nextRaceName: race?.name,
        // De-dup (HANDOFF §1): week mileage now lives in the reward strip, so
        // it's dropped from the footer status. Week-of-total stays in the footer.
        planLink: {
          // UX-S2 W5 — point at the CURRENT week so the label matches the
          // destination; fall back to the overview when the week isn't derivable.
          label: 'Open week plan',
          href: weekOfTotal ? `/v2/plan/week/${weekOfTotal[0]}` : '/v2/plan',
        },
      };

  const hero = (
    <LoopHero
      heroProps={heroProps}
      today={weekPlan?.days.find((d) => d.today) ?? null}
      days={weekPlan?.days ?? []}
      streakDays={streakDays}
      // B332 / B314: the strip states the week in the athlete's own unit (miles
      // for a mile athlete, so the figure is the one it always was). B330: the
      // on-track line also needs the plan comparison the real-data week carries;
      // the demo fixture has none and keeps its look.
      weekMileageDone={weekPlan?.weekDistanceDone ?? weekPlan?.weekMileageDone ?? 0}
      weekMileageTarget={weekPlan?.weekMileageTarget ?? 0}
      distanceUnit={weekPlan?.distanceUnit}
      weekOnTrack={weekPlan?.weekOnTrack}
      onToggleDay={onToggleDay}
      onAddEffort={onAddEffort}
      coachLine={coachLine}
      coachHref={coachHref}
      proactiveType={proactiveType}
      dense={densityOn}
      variant={v2 ? 'v2' : undefined}
    />
  );

  // Decision 8 — the density hero adopts the same 16px side gutter as every
  // other card (it is full-bleed today). Flag OFF → the bare hero, unchanged.
  return densityOn ? <div className={styles.heroGutter}>{hero}</div> : hero;
}

// ─── 2.0 Today helpers (W1-TODAY-WIRE, spec 7.8) ──────────────────────────────

interface ColdGateMemory {
  /** The gate has settled (or hit its ceiling) once; a remount starts open. */
  settled: boolean;
  /** `Date.now()` when the gate first held, so the ceiling is counted from there across a remount. */
  startedAt: number | null;
}
/**
 * PROVISIONAL (W1-TODAY-FIX2, R3): 4 s, the weather provider's own bound. W1-TODAY-FIX4
 * (finding 2): this is the ceiling of the OPTIONAL reads only (weather, health, echo,
 * reflow, meets, results, motivation, athlete context).
 */
export const COLD_GATE_CEILING_MS = 4000;
/**
 * PROVISIONAL (W1-TODAY-FIX4, finding 2): 12 s, the safety ceiling. The ESSENTIAL reads
 * (macro plan, season's plans, session, week, race) keep the gate closed past
 * COLD_GATE_CEILING_MS; only a read that is still hung after this long opens the page, so
 * a dead endpoint never leaves "Loading today" for good.
 */
export const COLD_GATE_SAFETY_CEILING_MS = 12000;

/**
 * TWR-6 — the cold-load gate. With `today_2_0_v1` on, the session, week and race
 * reads close ONE gate: while any of them is still open (`pending`) the page shows
 * the single "Loading today" skeleton, and the swap to content happens once, when
 * the last one settles. Flag off → always false, the section skeletons stay as they
 * were.
 *
 * It is a COLD-load gate: once the first settle has happened it never closes again.
 * Every later refetch (the 60 s visibility refetch re-opens the session read) keeps
 * the page on screen, because blanking Today under an open day panel or effort sheet
 * is worse than a stale line for a moment. A hook: it is called at the top of each
 * happy path, above everything that can return early.
 *
 * W1-TODAY-FIX1 (F1): a read's own `loading` is not proof that it has settled. The
 * session and week reads report "not loading" while the current week number is still
 * unknown (the macro plan is in flight) and only start loading in an effect once it
 * lands, and the macro plan and the season's plans read "not loading" until their own
 * effect starts. So there are frames, on arrival and again the moment the macro plan
 * resolves, in which every read looks settled and none has begun. The first such frame
 * used to latch the gate open, and the page then showed the legacy skeletons. The gate
 * therefore closes in two steps: a frame that looks settled only ARMS it (and still
 * holds the skeleton), the effects of that frame run (which is where the reads start),
 * and only a frame that still looks settled after them settles it for good. A frame
 * that looks pending in between disarms it. A real settle costs one extra frame of the
 * skeleton, never a flash of legacy content.
 *
 * W1-TODAY-FIX2 (R3): the gate has a CEILING. Weather is bounded at 4 s (`run/weather.ts`),
 * but the Apple Health status read is not (`authFetch` has no timeout and is shared by
 * every caller, so none is added there), and a hung read must not hold Today on a
 * skeleton for good. COLD_GATE_CEILING_MS after the gate first has a reason to hold, it
 * lets go of the reads that are still pending (the optional ones, see FIX4 below). A late
 * row may then move the card; that is accepted.
 *
 * W1-TODAY-FIX4 (finding 2): TWO ceilings, because opening onto the legacy chrome and the
 * section skeletons, then flipping to the 2.0 stack when the session lands, is the double
 * layout change the gate exists to prevent (on slow cellular the session read is the slow
 * one). The reads are therefore passed in two groups. OPTIONAL reads (a late one adds a line
 * to a page that is already the 2.0 page) stop holding the gate COLD_GATE_CEILING_MS after it
 * first held. ESSENTIAL reads (without one the page is not the 2.0 page yet) keep it closed
 * past that, and only COLD_GATE_SAFETY_CEILING_MS opens it whatever is still pending.
 *
 * W1-TODAY-FIX2 (R5): a gate that has already settled in this page's life starts open. A
 * breakpoint change swaps the phone happy path for the desktop one, which REMOUNTS the
 * gate while the reads (above, in `TodayRealData`) stay settled, and a gate that started
 * closed painted "Loading today" over a loaded page for one frame. The memory is a ref
 * owned by `TodayRealData` (`memory`), so a REAL cold start still starts closed: on the
 * very first frame every read looks settled and none has begun (F1), which is not the
 * same thing as a gate that has already settled, and starting open there would bring the
 * legacy skeletons back. Without a memory (the demo path) the hook keeps its own.
 */
function useColdLoadGate(
  v2: boolean,
  essentialPending: boolean,
  optionalPending: boolean,
  memory?: MutableRefObject<ColdGateMemory>,
): boolean {
  const ownMemory = useRef<ColdGateMemory>({ settled: false, startedAt: null });
  const mem = memory ?? ownMemory;
  // The optional ceiling has passed (FIX4): the optional reads stop holding the gate. Counted
  // from the memory's start, so a remount does not restart it.
  const [optionalCapped, setOptionalCapped] = useState(
    () => mem.current.startedAt !== null && Date.now() - mem.current.startedAt >= COLD_GATE_CEILING_MS,
  );
  const pending = essentialPending || (optionalPending && !optionalCapped);
  // 0 = open, 1 = the reads looked settled once (armed), 2 = settled for good.
  const [stage, setStage] = useState<0 | 1 | 2>(() => (mem.current.settled ? 2 : 0));
  useEffect(() => {
    if (!v2) return;
    setStage((s) => (s === 2 ? s : pending ? 0 : s === 0 ? 1 : 2));
  }, [v2, pending, stage]);
  const open = stage === 2;
  // The ceilings (R3, FIX4): counted from the first moment the gate held, kept in the memory
  // so a remount does not restart them. At the first the optional reads let go; at the
  // second (the safety ceiling) the gate opens whatever is still pending.
  useEffect(() => {
    if (!v2 || open) return;
    const m = mem.current;
    if (m.startedAt === null) m.startedAt = Date.now();
    const elapsed = Date.now() - m.startedAt;
    const optionalTimer = setTimeout(
      () => setOptionalCapped(true),
      Math.max(0, COLD_GATE_CEILING_MS - elapsed),
    );
    const safetyTimer = setTimeout(() => setStage(2), Math.max(0, COLD_GATE_SAFETY_CEILING_MS - elapsed));
    return () => {
      clearTimeout(optionalTimer);
      clearTimeout(safetyTimer);
    };
  }, [v2, open, mem]);
  // The memory (R5): once open, a remount of the gate starts open.
  useEffect(() => {
    if (v2 && open) mem.current.settled = true;
  }, [v2, open, mem]);
  if (!v2 || open) return false;
  return pending || stage === 0;
}

/**
 * W1-TODAY-LAYOUT-MEMORY (option A, founder ruling 2026-10-18) - WHAT THE 2.0 LOADING FRAME
 * DRAWS. Not what the reads say: what this device's last visit drew
 * (`lib/todayLayoutMemory`). The frame cannot know, before their reads answer, whether Today
 * will draw a Next steps card or a rest day's short hero, so a frame that waited for those
 * facts inserted a block mid-load and the week card moved (UX pixel check 2026-10-18,
 * defect 1; the fourth time on the cold-load class). Read once per mount and per user id:
 * the layout holds from the first paint to the swap, whatever any read answers meanwhile.
 *
 * `null` is "no usable memory" and draws the plain frame. The user id is null until auth
 * resolves (after the first paint), when the recall takes the device's last visit and the
 * stamp is checked once the id is known. A hook (it reads the auth context): every caller
 * is a component, called above any early return of its own.
 */
function useRememberedLayout(): TodayLayout | null {
  const userId = useAuthContext().user?.id ?? null;
  return useMemo(() => recallTodayLayout(userId), [userId]);
}

/**
 * The other half: remember the block set the loaded 2.0 page drew, once it has settled
 * (`layout` is null until then, and always with the flag off, so nothing is read or written).
 * Written when its VALUE changes, so a later change the athlete causes (a day marked done,
 * the effort added) is what the next visit's frame draws too. A hook: above every early
 * return of the page. Storage failures are swallowed inside `rememberTodayLayout`.
 */
function useRememberTodayLayout(userId: string | null, layout: TodayLayout | null): void {
  const value = layout ? JSON.stringify(layout) : '';
  useEffect(() => {
    if (userId && layout) rememberTodayLayout(userId, layout);
    // `value` is the layout's content; the object itself is rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, value]);
}

/**
 * The no-race card's phase key from the macro plan's current week (TC-4). The card
 * knows four labels; `competition` is "comp". `recovery` (and no current week) has no
 * label to underline, so it reports null and the card reads `seasonRunning` instead.
 */
function noRacePhaseFromMacro(weeks: MacroPlanWeek[] | null): NoRacePhase | null {
  switch (phaseFromMacroPlan(weeks).phase) {
    case 'base':
      return 'base';
    case 'build':
      return 'build';
    case 'competition':
      return 'comp';
    case 'taper':
      return 'taper';
    default:
      return null;
  }
}

/**
 * The Next steps card's Draft label: the weeks the next draft would author, from the
 * SAME `draftTargetWeeks` + `formatDraftWeeksLabel` /plan uses (no third copy), and only
 * with two or more undrafted weeks, because /plan shows its draft button only then; a
 * Draft row for one week would land on a page with no draft CTA. Null until the
 * drafted set has settled (`draftedWeeks` undefined), so the row never flashes.
 */
function draftWeeksLabelFor(
  weeks: MacroPlanWeek[] | null,
  currentWeek: number | null,
  drafted: Set<number> | undefined,
): string | null {
  if (!weeks || !drafted) return null;
  const target = draftTargetWeeks(
    weeks.map((w) => w.week_number),
    currentWeek,
    drafted,
  );
  return target.length >= 2 ? formatDraftWeeksLabel(target) : null;
}

/**
 * The status row and the Next steps card, in their gutter. Each is absent when
 * nothing is true, and an absent card leaves an EMPTY slot, which the stylesheet
 * removes from the layout (`:empty`), so an absent row costs no gap (TC-2, TC-3).
 * `gutter` is the phone's 16px side padding; the desktop main column has none.
 */
function TodayV2Cards({
  gutter,
  approvalStatus,
  draftWeeksLabel,
  draftJourneyOn,
  hasGoalRace,
}: {
  gutter: boolean;
  approvalStatus: MacroPlanStatus;
  draftWeeksLabel: string | null;
  draftJourneyOn: boolean;
  hasGoalRace: boolean;
}) {
  const slot = gutter ? styles.v2Block : styles.v2Slot;
  return (
    <>
      <div className={slot}>
        <StatusRow />
      </div>
      <div className={slot}>
        <NextStepsCard
          approvalStatus={approvalStatus}
          draftWeeksLabel={draftWeeksLabel}
          draftJourneyOn={draftJourneyOn}
          hasGoalRace={hasGoalRace}
        />
      </div>
    </>
  );
}

// ─── Mobile happy-path (now data-prop driven; falls back to fixtures) ──

interface TodayHappyPathProps {
  /**
   * ⚖️ B47 fix 4 (round-20 UX 1) — a passive route-level line (today: the B99
   * guardian hold / the R71 refusal on the post-create surface), rendered
   * INSIDE this frame: below the fixed TopBar, above the hero it explains.
   * The route used to render it as a sibling of this component, where the
   * fixed bar covered it at 375 and it floated above the AppShell at 1440.
   */
  notice?: ReactNode;
  raceState: 'present' | 'none';
  /** Today's session — null while still loading or when no session exists. */
  session?: TodaySession | null;
  /** This week's plan — null while loading or absent. */
  weekPlan?: WeeklyPlan | null;
  /** Next race — null while loading or absent. */
  race?: Race | null;
  /** Active season display name for the SeasonGlance kicker. */
  seasonName?: string | null;
  /** Macro-plan weeks (Phase 6c PR 4 — drives <SeasonGlance />).
   *  Null while loading or when no plan exists yet. */
  macroPlanWeeks?: MacroPlanWeek[] | null;
  /**
   * Season-model Phase 3 — week_numbers with a DRAFTED weekly plan. When
   * provided, SeasonGlance marks drafted vs undrafted weeks. Omitted on the
   * demo path → no marking (stable showcase).
   */
  draftedWeeks?: Set<number>;
  /**
   * Phase 6c.1 — when provided, WeekPlan cells with a real `id`
   * become tap targets that open the day's details (B309: a tap never
   * writes); the details button calls this callback to toggle completion.
   * Demo path (fixture days have no `id`) → cells stay inert.
   */
  onToggleDay?: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /** RPE restore — hero "Add effort" recovery (today completed, no RPE). */
  onAddEffort?: (dayId: string, workoutLabel: string) => void;
  /**
   * Phase 6c.1 — real consecutive-completed-day training streak from
   * `computeCurrentStreak(seasonPlans.data)`. Drives the hero
   * `streakDays` value + the MiniTile streak counter. Demo path
   * passes undefined → falls back to the `ramiroStreak.streakDays`
   * fixture so the design preview stays stable.
   */
  currentStreak?: number;
  /**
   * Phase 6 cross-cutting PR 6 — when a proactive trigger fired this
   * load, threads down to `<ChampionshipCard proactiveType={...}>` so
   * the moti block renders the `RELATED · <type-readable>` kicker +
   * `--today` left-rule. Null/undefined → default kicker.
   */
  proactiveType?: ProactiveTriggerType | null;
  /**
   * Phase 7 PR 1 — Real signed-in user metadata for the TopBar
   * avatar + hero greeting ("GOOD EVENING, RAMIRO" line). Provided by
   * `TodayRealData` (derived from auth.user + athleteContext.raw.Name).
   * Demo path leaves undefined → falls back to the `ramiro` fixture
   * for design-preview stability.
   */
  signedInUser?: {
    displayName: string;
    /** B73 — the PERSONAL name, or '' when the app knows none. The hero
     *  greeting reads this (never the email local part); `displayName`
     *  stays the never-blank me-block label. Absent on the demo path. */
    greetingName?: string;
    initial: string;
    weekLabel: string;
  };
  /**
   * Phase 7 PR 1 (post-smoke follow-up) — Real kudos feed for the
   * "Team" section. Provided by `TodayRealData` via `useTeamHub` +
   * `useTeamKudos` + `kudoRowsToEntries`. Empty array → section
   * hides (TeamFeed itself returns null on 0 entries). Demo path
   * leaves undefined → falls back to the `ramiroTeam` fixture for
   * design-preview stability.
   */
  kudosEntries?: KudosEntry[];
  /**
   * Phase 7 PR 1 (post-smoke follow-up) — Real athlete context for
   * the "Coach context" drawer. Mirrors the desktop PinnedContext
   * wire shipped in 6c long-tail PR 7. Empty/null → drawer hides.
   * Demo path leaves undefined → falls back to the `ramiroContext`
   * fixture for design-preview stability.
   */
  contextItems?: ContextItem[] | null;
  /**
   * CI-2 — real rules-engine insights for the mobile "From your coaches"
   * section (coachInsightRules.ts adapters, flag `coach_insights`).
   * undefined/null → flag off or still loading (demo keeps its fixture);
   * [] → loaded-but-quiet; non-empty → render the real cards.
   * (CI-4 dropped the never-consumed `coachMessage` prop this replaced.)
   */
  coachInsights?: CoachInsight[] | null;
  /** PR 2 — the daily coach voice line (`useMotivation().data?.message`)
   *  shown inside the loop hero's CoachLine. Null while the LLM line is
   *  in flight (the line hides cleanly). */
  coachLine?: string | null;
  /** Coach-intel Rung 1 — the coach-hue tap target; carries the run reference
   *  when the reaction echo owns the line (§11). Defaults to the coach chat. */
  coachHref?: string;
  /** Per-section loading flags from real-data hooks. */
  sessionLoading?: boolean;
  weekLoading?: boolean;
  raceLoading?: boolean;
  /** B-24 — real hero weather from `useHeroWeather` (null until resolved /
   *  when no stored location → the hero meta row hides). Demo path leaves
   *  undefined → falls back to the `ramiroWeather` fixture for
   *  design-preview stability. */
  weather?: { tempDisplay: string; condition?: string; windMph: number } | null;
  /**
   * W1-TODAY-WIRE — `today_2_0_v1`, read once in `TodayRealData`. Absent (the demo
   * path) or false = the legacy Today. On, with a loaded session, both density
   * branches render the one 2.0 phone order; the no-session states (first run,
   * before season, undrafted week) keep their legacy look.
   */
  v2?: boolean;
  /** The macro plan's approval status (`useMacroPlan().status`), for Next steps. */
  approvalStatus?: MacroPlanStatus;
  /**
   * W1-TODAY-FIX1 (F1, F3) — the 2.0 cold-load gate's other reads: the macro plan, the
   * season's plans (the drafted weeks), the athlete context (the unit and the stored
   * location) and the hero weather. True while any is still open. With the session,
   * week and race flags above and the shared Apple Health status read, they close ONE
   * gate, so the weather row, the status row and the Draft row are in the first paint.
   * W1-TODAY-FIX2 (R4) adds the reflow or guardian notice, the reads the proactive trigger
   * is detected from and, with a trigger detected, the daily line it prints.
   *
   * W1-TODAY-FIX4 (finding 2): two props, because the ceiling treats them differently.
   * ESSENTIAL (the macro plan and the season's plans here, with the session, week and race
   * above): the gate stays closed past the optional ceiling, to a safety ceiling.
   * OPTIONAL (the rest): the gate opens at the optional ceiling whatever is still open.
   */
  coldEssentialPending?: boolean;
  coldOptionalPending?: boolean;
  /**
   * The skeleton draws the weather row's place: a weather fetch is in flight for a stored
   * location, or it has resolved with a row the hero will show (W1-TODAY-FIX2, R1).
   */
  weatherRowPending?: boolean;
  /**
   * B413: the page asks for a location (`shouldPromptForLocation`): the layout memory keeps it,
   * so the next visit's loading frame holds the prompt's place and the week card does not drop
   * at the swap. The hero itself decides whether it draws (never on a rest day).
   */
  promptForLocation?: boolean;
  /**
   * W1-TODAY-FIX2 (R3, R5) — the cold-load gate's memory of this page's life, owned by
   * `TodayRealData` so it survives the phone/desktop remount. Absent (the demo path): the
   * gate keeps its own.
   */
  gateMemory?: MutableRefObject<ColdGateMemory>;
}

function TodayHappyPath({
  notice,
  raceState,
  session,
  weekPlan,
  race,
  seasonName,
  macroPlanWeeks,
  draftedWeeks,
  currentStreak,
  onToggleDay,
  onAddEffort,
  proactiveType,
  signedInUser,
  kudosEntries,
  contextItems,
  coachInsights,
  coachLine,
  coachHref,
  sessionLoading,
  weekLoading,
  raceLoading,
  weather,
  v2,
  approvalStatus,
  coldEssentialPending,
  coldOptionalPending,
  weatherRowPending,
  promptForLocation,
  gateMemory,
}: TodayHappyPathProps) {
  // B-3 — the "Week N isn't drafted" hero re-aims to the /v2/plan/draft takeover
  // when the flag is ON (else the legacy chat deep-link). §0.8 rake: read via
  // useServerFlag (config export). Flag OFF → byte-identical hero.
  const draftWeekJourneyOn = useServerFlag('draft_week_journey_v1');
  // SP-8 + B-4/B-5 — Today density & hierarchy. §0.8 rake: read via
  // useServerFlag (config export). Flag OFF → byte-identical Today (the
  // density branches below never execute). Mobile-only; desktop is OUT.
  const densityOn = useServerFlag('today_density_v1');
  // Team page CD · FRAME 5 · E7 — the ONE empty state in the Team vocabulary
  // that lives on TODAY (D5.5, the founder's screenshotted state). It rides
  // `athlete_kudos_v1` because it is a kudos string. ⛔ It does NOT touch
  // `maxEntries`: today-density's ratified cap governs THIS host and frame 4's
  // D4.15 cap of 3 governs `/team`. Flag OFF → byte-identical Today.
  const athleteKudosOn = useServerFlag('athlete_kudos_v1');
  // Wave 2 amendment A2 (spec 4.6): with `races_2_0_v1` on the phone's results row prints "Add a race"
  // (the target is unchanged). A hook: above the cold-load return below. Flag off → "Log a result".
  const racesV2On = useServerFlag('races_2_0_v1');
  // 2.0 chat (P9): with `chat_2_0_v1` on, "See all" (the coach roster) is retired: the room's faces are the
  // roster now. A hook, above the cold-load return below. Flag off → the link as it was. Build 35: never in the iOS app.
  const roomOn = useServerFlag('chat_2_0_v1') && roomAvailableOnThisClient();
  // W1-TODAY-WIRE (TWR-6) — the 2.0 cold-load gate: one skeleton until the session,
  // week and race reads have all settled. A hook, so it sits here with the others,
  // above every return. Flag off → always false.
  //
  // W1-TODAY-FIX1 (F1, F3): the gate waits for EVERY read the 2.0 page paints: the
  // three above, the macro plan, the season's plans, the athlete context, the weather
  // and the one shared Apple Health status read (`HealthSyncProvider`). The weather
  // row, the status row and the Draft row are therefore in the first paint, and the
  // week card does not move under the athlete when they arrive. The unit, the weather
  // and the status are waited for once there is a session (the pages with no session
  // keep their legacy look, which paints none of them).
  const sharedHealth = useSharedHealthSync();
  const coldGate = useColdLoadGate(
    !!v2,
    !!sessionLoading || (!!weekLoading && !weekPlan) || !!raceLoading || !!coldEssentialPending,
    !!coldOptionalPending || (!!session && !!sharedHealth && !sharedHealth.loaded),
    gateMemory,
  );
  // W1-TODAY-LAYOUT-MEMORY: whose layout the loaded page remembers (see `useRememberTodayLayout`),
  // and whether the hero's action area is Start run (the frame cannot read the flag before the
  // config answers, so the memory carries it).
  const layoutUserId = useAuthContext().user?.id ?? null;
  const runCaptureOn = useServerFlag('run_capture_v1');
  // Demo mode: undefined props → use ramiro fixtures. Real-data mode:
  // null props → empty state; loading flags → skeleton; real data →
  // pass through to existing components unchanged.
  const isDemo = session === undefined && weekPlan === undefined && race === undefined;

  // Phase 6c PR 4 — TodayHero replaces the Greeting + HeroToday split.
  // Mobile compose path:
  //   - effective session (real data, demo, or null)
  //   - eyebrow: today's date + greeting + name
  //   - title: composed from session.titleLead/Em/Trail
  //   - phase: prefer macro-plan-derived; fall back to phaseLabel string
  //   - status footer: phase pill + week + days-to-race + week mileage +
  //                    "Open week plan ›" link
  const effectiveSession = session ?? (isDemo ? ramiroToday : null);
  const effectiveMacro: MacroPlanWeek[] | null =
    macroPlanWeeks ?? (isDemo ? ramiroMacroPlan : null);
  const effectiveSeasonName = seasonName ?? (isDemo ? 'Spring 2026 XC' : null);
  // B333 — a season is running (today falls inside the macro plan, not before it
  // or after its last week): the no-race card's quote must not say "off-season".
  const seasonRunning = resolveCurrentWeek(effectiveMacro, null).state === 'in_season';
  // #2a — surface that you can plan AHEAD (not just the current week / a single
  // "next week" nudge). Count future macro weeks that aren't drafted yet; when
  // any exist, Today shows a "draft upcoming weeks" entry into the full season
  // plan, where any week is draftable. Real path only (draftedWeeks known).
  const glanceCurrentWeek = effectiveMacro ? phaseFromMacroPlan(effectiveMacro).weekNumber ?? null : null;
  const undraftedUpcomingCount = effectiveMacro && draftedWeeks && glanceCurrentWeek != null
    ? effectiveMacro.filter((w) => w.week_number > glanceCurrentWeek && !draftedWeeks.has(w.week_number)).length
    : 0;
  // Phase 7 PR 1 — Real signed-in user when available; ramiro fixture
  // fallback for demo path. Without this the TopBar avatar + hero
  // greeting "GOOD EVENING, RAMIRO" showed regardless of who signed
  // in. Surfaced during waisports test-account smoke 2026-05-15.
  const heroUser = signedInUser ?? ramiro;
  // B73 — the GREETING name, which is allowed to be empty. On the real-data
  // path this is `greetingName` (personal name or ''), so a nameless account
  // gets a plain "GOOD EVENING" instead of the uppercased local part of their
  // email address. The demo fixture has no greetingName and keeps its own.
  const heroGreetingName = signedInUser ? signedInUser.greetingName ?? '' : heroUser.displayName;
  // B62 — the black "Next up" card's ▸ affordance. See the ChampionshipCard
  // mount below for why the season plan is the destination.
  const navigate = useNavigate();
  // W1-TODAY-FIX3 (TC-5): where "the season plan" lives right now (`/plan` with
  // `train_2_0_v1` off). A hook: read here, above the cold-gate return below.
  const seasonPath = useSeasonPath();

  // W1-TODAY-WIRE (TWR-3, TWR-5) — the 2.0 phone layout applies when the flag is on
  // AND there is a session to show (or the cold gate is still holding the page for
  // one). Everything else (first run, before season, an undrafted week, which all
  // have no session) keeps its legacy look, flag on or off. Density is ignored
  // under this flag: the two density branches below are both replaced.
  const v2Layout = !!v2 && (coldGate || !!effectiveSession);

  // W1-TODAY-LAYOUT-MEMORY: the block set this loaded page draws, once every read that decides
  // it has settled, is what the NEXT visit's loading frame draws. `coldSkeletonPlace` is the
  // decision each block makes for itself (pinned against the real blocks by the FIX4 contract
  // tests), written once more. Flag off: null, nothing computed, read or written. A hook, above
  // the cold-load return below.
  useRememberTodayLayout(
    layoutUserId,
    v2 && v2Layout && !coldGate && !!effectiveSession && !!weekPlan &&
      !sessionLoading && !weekLoading && !raceLoading && !coldEssentialPending && !coldOptionalPending &&
      !(sharedHealth && !sharedHealth.loaded)
      ? {
          ...coldSkeletonPlace({
            approvalStatus,
            draftWeeksLabel: draftWeeksLabelFor(effectiveMacro, glanceCurrentWeek, draftedWeeks),
            draftJourneyOn: draftWeekJourneyOn,
            noGoalRace: !race && !raceLoading,
            today: weekPlan.days.find((d) => d.today) ?? null,
            days: weekPlan.days,
            coachLine,
            coachHref,
            proactiveType,
          }),
          weatherRow: !!weatherRowPending,
          weatherPrompt: !!promptForLocation,
          healthRow: healthRowFor(sharedHealth),
          startRun: isNativeApp() && runCaptureOn,
        }
      : null,
  );

  // TWR-6 — the cold-load gate is the whole page body: the bar, then ONE skeleton. What the
  // skeleton draws is the device's remembered layout (W1-TODAY-LAYOUT-MEMORY), not a guess from
  // the reads still open: nothing in it is inserted or removed while the gate holds.
  if (coldGate) {
    return <TodayColdFrameMobile userInitial={heroUser.initial} />;
  }

  return (
    <div className={styles.page}>
      <TopBar userInitial={heroUser.initial} />

      {/* ⚖️ B47 fix 4 (round-20 UX 1) — the route's passive line, INSIDE the
          page and below the fixed bar. Above it, as a sibling of this whole
          component, the fixed TopBar simply covered it. (2.0: it moves below the
          race card with the rest of the tail, Q7 — see `v2Layout` further down.) */}
      {!v2Layout && notice}

      {/* P7(a) A1 — team-invite acceptance banner (self-contained; null
          when the signed-in user has no claimed-pending invites). */}
      {!v2Layout && <TeamInviteBanner />}

      {/* Hero — TodayHero (Phase 6c PR 4) replaces Greeting + HeroToday */}
      {/* Phase 6c.1 — streakDays uses real `currentStreak` when present;
          falls back to ramiroStreak fixture for demo / un-wired paths. */}
      {v2Layout && effectiveSession ? (
        /* 2.0 Today (spec 7.3): date line and hero, status row, Next steps, the
           week list, then the race card below. CoachStories, the reward strip and
           the ring are not part of it. A refetch never blanks it (`coldGate`
           only closes once). */
        <div className={styles.v2Stack}>
          <div className={styles.v2Block}>
            {renderLoopHero({
              session: effectiveSession,
              race: race ?? null,
              weekPlan: weekPlan ?? null,
              macroPlanWeeks: effectiveMacro,
              streakDays: typeof currentStreak === 'number' ? currentStreak : 0,
              weather: weather ?? null,
              weatherFromApple: !!weather,
              athleteName: heroGreetingName,
              onToggleDay,
              coachLine: coachLine ?? null,
              coachHref,
              proactiveType,
              onAddEffort,
              v2: true,
            })}
          </div>
          <TodayV2Cards
            gutter
            approvalStatus={approvalStatus ?? 'none'}
            draftWeeksLabel={draftWeeksLabelFor(effectiveMacro, glanceCurrentWeek, draftedWeeks)}
            draftJourneyOn={draftWeekJourneyOn}
            hasGoalRace={!!race}
          />
          {weekPlan ? (
            <div className={styles.v2Block}>
              <WeekListV2 plan={weekPlan} onToggle={onToggleDay} />
            </div>
          ) : weekLoading ? (
            <WeekPlanSkeleton />
          ) : null}
        </div>
      ) : sessionLoading ? (
        <TodayHeroSkeleton />
      ) : effectiveSession ? (
        renderLoopHero({
          session: effectiveSession,
          race: race ?? (isDemo ? championshipFinal : null),
          weekPlan: weekPlan ?? (isDemo ? ramiroWeek : null),
          macroPlanWeeks: effectiveMacro,
          streakDays:
            typeof currentStreak === 'number'
              ? currentStreak
              : ramiroStreak.streakDays,
          // B-24 — real weather on the real-data path (null → row hides);
          // the fixture is DEMO-ONLY (never a real athlete's weather).
          weather: weather ?? (isDemo ? ramiroWeather : null),
          // REL-8 — the mark credits APPLE, so it rides the real value only.
          weatherFromApple: !!weather,
          athleteName: heroGreetingName,
          onToggleDay,
          // PR 2 — the loop hero now OWNS the mark-complete CTA + reward
          // strip; the standalone CTA + the weather/streak MiniTiles are
          // retired (weather folds into hero meta, streak into the reward
          // strip). Coach voice: real motivation line, demo fixture on the
          // ?user=ramiro preview path.
          coachLine: coachLine ?? (isDemo ? ramiroToday.heroCoachNote?.text ?? null : null),
          coachHref,
          proactiveType,
          onAddEffort,
          densityOn,
        })
      ) : (
        renderEmptyOrBeforeSeasonHero(
          effectiveMacro,
          effectiveSeasonName,
          draftedWeeks,
          draftWeekJourneyOn,
          densityOn,
        )
      )}

      {/* UAT-B2 (frame 1d) — one-time guardian-invite resurface, BELOW the
          hero (never the top slot — TeamInviteBanner owns that). Self-gates
          to a skipped 13–15 minor; null otherwise. (2.0: below the race card.) */}
      {!v2Layout && <GuardianNudgeCard />}

      {v2Layout ? null : densityOn ? (
        /* SP-8 + B-4/B-5 — the Plan ledger replaces the SeasonGlance strip,
           the "Draft upcoming weeks" InlineAction, and the "This week"
           SectionLabel + WeekPlan block: season + weekly collapse into ONE
           card, mounted exactly where SeasonGlance did. Only when a macro
           plan exists (the no-season surface carries no ledger, matching 1c). */
        effectiveMacro && effectiveMacro.length > 0 ? (
          <div style={{ padding: '0 16px', marginTop: 18 }}>
            <PlanLedger
              // Row A omitted (weekPlan null) in the undrafted-week state — no
              // drafted week exists, so an empty grid would be filler (1b).
              // B309 fix round: a refetch (every completion write triggers one)
              // keeps the last week on screen instead of unmounting the chips,
              // so an open day panel and its focus survive the write.
              weekPlan={weekPlan ?? (weekLoading ? null : (isDemo ? ramiroWeek : null))}
              onToggleDay={onToggleDay}
              weeks={effectiveMacro}
              currentWeek={glanceCurrentWeek}
              seasonName={effectiveSeasonName}
              // Real path passes the true drafted set; the demo showcase pins
              // W1–2 drafted so the preview matches frame 1a.
              draftedWeeks={draftedWeeks ?? (isDemo ? new Set([1, 2]) : undefined)}
              meetLabels={isDemo ? ramiroMacroPlanMeetLabels : undefined}
              undraftedUpcomingCount={undraftedUpcomingCount}
              weekHref={
                glanceCurrentWeek != null ? `/plan/week/${glanceCurrentWeek}` : '/plan'
              }
              // B409: "the season plan" is `useSeasonPath()`, `/plan` with `train_2_0_v1` off
              // and `/plan/season` with it on, as the Next steps card and the race card read it.
              planHref={seasonPath}
              draftAheadHref={seasonPath}
            />
          </div>
        ) : null
      ) : (
        <>
          {/* SeasonGlance strip — Phase 6c PR 4 — between the loop hero and
              WeekPlan on mobile/comfortable. Shown when we have macro-plan
              weeks; empty variant when we have a season but no plan yet
              (caller-side decision). Hidden entirely when there's no
              season. */}
          {effectiveMacro && effectiveMacro.length > 0 && (
            <div style={{ padding: '0 16px', marginTop: 18 }}>
              <SeasonGlance
                display="strip"
                weeks={effectiveMacro}
                currentWeek={phaseFromMacroPlan(effectiveMacro).weekNumber ?? null}
                // B2.1 — make the strip cells navigable (matches the already-
                // navigable Ultra SeasonRail). Cells render as <a> when set.
                weekHref={(n) => `/v2/plan/week/${n}`}
                seasonName={effectiveSeasonName ?? undefined}
                openLink={{ label: 'Open season plan', href: '/v2/plan' }}
                meetLabels={isDemo ? ramiroMacroPlanMeetLabels : undefined}
                // Phase 3 — mark drafted vs undrafted (real path only; undefined
                // on demo → unmarked showcase).
                draftedWeeks={draftedWeeks}
              />
            </div>
          )}

          {/* #2a — explicit "plan ahead" entry: drafting isn't limited to the
              current week. Shown only when upcoming weeks are still undrafted. */}
          {undraftedUpcomingCount > 0 && (
            <InlineAction
              to="/plan"
              label="Draft upcoming weeks"
              sub={`${undraftedUpcomingCount} not drafted yet`}
            />
          )}

          {/* Week plan section. B309 fix round: the skeleton shows only while there
              is no week to show yet. A refetch (every completion write triggers
              one) keeps the last week mounted, so an open day panel and its
              focus survive the write instead of the grid being swapped out. */}
          {weekLoading && !weekPlan ? (
            <>
              <SectionLabel title="This week" link="—" />
              <WeekPlanSkeleton />
            </>
          ) : weekPlan ? (
            <>
              <SectionLabel title="This week" link={`${weekPlan.dateRange} →`} />
              <WeekPlan plan={weekPlan} onToggle={onToggleDay} />
            </>
          ) : isDemo ? (
            <>
              <SectionLabel title="This week" link={`${ramiroWeek.dateRange} →`} />
              <WeekPlan plan={ramiroWeek} />
            </>
          ) : (
            // F-116(c) — the "add a race" prompt is carried by the "Next up" rail
            // card (<NoRaceCard>) below; the old <NoPlanBanner> here duplicated it
            // under "This week". Dedupe by dropping the banner (and its now-empty
            // section header) — the rail card is the single race prompt.
            null
          )}
        </>
      )}

      {/* Race section. The Motivational Coach block renders only when
          a coach message has actually loaded — `useMotivation` calls
          `/api/motivation` (LLM-generated, daily-cached) and composes
          the result into `race.motivationalLine` upstream. While the
          LLM is in flight, motivationalLine is undefined → the block
          hides cleanly rather than showing a placeholder. */}
      {/* 2.0: no "Next up" heading. The race card names itself (frame 1a draws none)
          and its arrow, "Open season plan", is the season plan link. */}
      {!v2Layout && (
        <SectionLabel
          title="Next up"
          link={
            <Link to={seasonPath} style={{ color: 'inherit', textDecoration: 'inherit' }}>
              Season plan →
            </Link>
          }
        />
      )}
      {raceLoading ? (
        <ChampionshipCardSkeleton />
      ) : race ? (
        <ChampionshipCard
          race={race}
          withMotivation={!!race.motivationalLine}
          proactiveType={proactiveType}
          // B62 — the ▸ was an inert <button> with no handler. Same destination
          // as `<ChampionshipCardDesktop>`'s "Season plan ›" CTA: one card, two
          // variants, one destination. (There is no race-detail route to land
          // on — App.tsx has /races but no /races/:id.)
          onOpenSeasonPlan={() => navigate(seasonPath)}
          v2={v2Layout}
        />
      ) : isDemo && raceState !== 'none' ? (
        <ChampionshipCard
          race={championshipFinal}
          withMotivation
          onOpenSeasonPlan={() => navigate(seasonPath)}
        />
      ) : (
        <NoRaceCard
          variant="mobile"
          seasonRunning={seasonRunning}
          v2={v2Layout}
          phase={v2Layout ? noRacePhaseFromMacro(effectiveMacro) : undefined}
        />
      )}

      {/* Phase 7 PR 1 — Auxiliary sections, real-data aware.
          Smoke surface 2026-05-16: user requested ALL widgets render
          on real-data path (CoachStories + TeamFeed + ContextDrawer)
          so the "show-off" account exercises the full layout.

          Current real-data wiring state:
            - CoachStories: rules engine deferred. Until it ships, we
                            keep the fixture so the section renders
                            with a placeholder message (avoids leaving
                            visible empty space on the dashboard).
                            Inline TODO when the LLM rules engine
                            wires in.
            - TeamFeed:     useTeamHub + useTeamKudos →
                            kudoRowsToEntries() (this PR)
            - ContextDrawer: useAthleteContext.pills →
                             pillsToContextItems() (this PR)
       */}
      {/* CoachStories — CI-2 three-state gate. Demo (?user=ramiro) keeps its
          fixture for the design preview. Real path renders ONLY when the
          rules engine produced insights (flag `coach_insights` on + loaded +
          non-empty); off/loading/quiet → hidden honestly, no filler — the
          fabricated-fixture-on-real-data failure (DD-1) stays impossible.
          2.0 Today retires the coach cards (spec 7.3). */}
      {v2Layout ? null : isDemo ? (
        <>
          <SectionLabel title="From your coaches" link={roomOn ? null : seeAllCoachesLink} />
          {/* SP-8 coaches-alignment — dense cards subordinate the carousel to
              the density card grammar (founder: "disconnected and way too
              big"). Flag OFF → the current full-size cards, unchanged. */}
          {/* B345 — see `.coachStoriesGutter` in today.module.css. */}
          <div className={styles.coachStoriesGutter}>
            <CoachStories insights={ramiroCoachInsights} dense={densityOn} />
          </div>
        </>
      ) : coachInsights && coachInsights.length > 0 ? (
        <>
          <SectionLabel title="From your coaches" link={roomOn ? null : seeAllCoachesLink} />
          {/* B345 — see `.coachStoriesGutter` in today.module.css. */}
          <div className={styles.coachStoriesGutter}>
            <CoachStories insights={coachInsights} dense={densityOn} />
          </div>
        </>
      ) : null}

      {/* 2.0 tail (spec 7.3, Q7): the route's passive notices, the invite banner and
          the guardian nudge keep their relative order and sit below the race card,
          ahead of the team feed. Same components, same props; legacy has them above
          and below the hero. */}
      {v2Layout && (
        <>
          {notice}
          <TeamInviteBanner />
          <GuardianNudgeCard />
        </>
      )}

      {/* Team section — the kudos feed, or a teaser empty-state (UX-S3 DD-4)
          when there are no kudos yet (keep + teach, per the user's call — not
          hide). Demo shows the fixture feed. */}
      {(() => {
        const entries: KudosEntry[] = isDemo
          ? ramiroTeam
          : (kudosEntries ?? []);
        return (
          <>
            {/* B-41 (cd-team-page audit) — "My Team →" was a dead string since
                SP-8, the same bug class CI-4 fixed for "See all →" above; the
                coaches sibling was already a real Link. */}
            {v2Layout ? (
              /* 2.0 (Claude Design return 2026-10-18, item 6): a DM Sans heading at the title role,
                 like "Next steps" and "This week", and ONE link to Team: "My Team →" here, with a
                 44px target (today.module.css `.v2TeamLink`). The empty feed's own link leaves via
                 `hideEmptyLink` below. Same words as the legacy label. */
              <div className={styles.v2TeamHead}>
                <h2 className={styles.v2TeamTitle}>Team</h2>
                <Link to="/team" className={styles.v2TeamLink}>
                  My Team →
                </Link>
              </div>
            ) : (
              <SectionLabel
                title="Team"
                link={
                  <Link to="/team" style={{ color: 'inherit', textDecoration: 'inherit' }}>
                    My Team →
                  </Link>
                }
              />
            )}
            {/* SP-8 decision 7 — mobile caps the feed at 2 with "View all team
                (N) →" (the affordance TeamFeed already ships for desktop). Flag
                OFF → the full list, unchanged. 2.0 ignores density and keeps the
                cap, the shipped phone behavior (density is live in prod), so the
                two density states render one DOM. */}
            <TeamFeed
              entries={entries}
              showEmptyHint
              maxEntries={densityOn || v2Layout ? 2 : undefined}
              viewAllHref={densityOn || v2Layout ? '/team' : undefined}
              kudosV1={athleteKudosOn}
              hideEmptyLink={v2Layout}
            />
          </>
        );
      })()}

      {/* Coach context drawer — show on demo (fixture) OR when real
          athlete context has any deduped pills. Hidden when neither
          branch has anything to show. */}
      {(() => {
        const items: ContextItem[] = isDemo
          ? ramiroContext
          : (contextItems ?? []);
        if (items.length === 0) return null;
        return <ContextDrawer items={items} />;
      })()}

      {/* B-40 BUG 3 — ContextDrawer's `.drawer` has margin-bottom 0 and the
          shared InlineAction `.act` has no margin, so "Log a race" collapses
          against the drawer above it (the founder's cramped mobile spacing).
          Restore the 28px section rhythm (matching `.slabel`) with a scoped
          wrapper — NOT by touching `.act`, which is shared with the
          "Draft upcoming weeks" / "Start setup" InlineActions. */}
      <div className={styles.logRaceRow}>
        {/* R1 (M1) — the results door speaks past tense everywhere. A2: with `races_2_0_v1` on the
            one door is "Add a race" (the Races route decides what it opens). */}
        <InlineAction to="/races?openRaceForm=1" label={racesV2On ? 'Add a race' : 'Log a result'} />
      </div>
      {/* The end of the page. With `today_2_0_v1` on, every layout of this page, the 2.0 one and the legacy look
          it keeps when there is no session, adds the coach button's clearance below 1024 (`.v2EndInset` in
          today.module.css, `--fab-clearance`); B367: the second kept a fixed 96px, less than the button's room
          between 601 and 1023 wide, so "Log a result" sat under it. Flag off keeps its 96px spacer, byte for byte. */}
      <div className={v2 ? styles.v2EndInset : undefined} style={v2 ? undefined : { height: 96 }} />
    </div>
  );
}

/**
 * Brand-new user (no auth or no season). Entire surface is set up
 * around the first-run TodayHero (formerly SetupFirstHero, formally
 * promoted to <TodayHero variant="firstRun"> in Phase 6c long-tail
 * PR 9). The hero's eyebrow ("WELCOME") replaces the previous
 * `<Greeting>` line so the page opens with one focal element instead
 * of greeting + hero stacked.
 *
 * Weekly plan / coach stories / team feed / context drawer all
 * omitted — nothing meaningful to show yet.
 */
function TodayFirstRun() {
  return (
    <div className={styles.page}>
      <TopBar userInitial={newbie.initial} hasNotification={false} />

      <TodayHero {...FIRST_RUN_HERO_PROPS} />

      <div className={styles.tilePair}>
        <MiniTile glyph={<WeatherGlyph />} hint={`${ramiroWeather.tempDisplay} ${ramiroWeather.condition}`}>
          <div className={`${styles.tileBig} serif tnum`}>{ramiroWeather.tempDisplay}</div>
          <div className={styles.tileSub}>
            wind {ramiroWeather.windMph} · {ramiroWeather.humidityPct}%
          </div>
        </MiniTile>
        <MiniTile glyph={<FlameGlyph />} glyphBg="var(--today-soft)" hint="streak">
          <div
            className={`${styles.tileBig} serif`}
            style={{
              // TWR-8 — `--ramp-bridge-18`: 18px with `type_ramp_v1` off, the stat title on.
              fontSize: 'var(--ramp-bridge-18)',
              lineHeight: 1.2,
            }}
          >
            Start your streak
          </div>
          <div className={styles.tileSub}>Log your first run to begin.</div>
        </MiniTile>
      </div>

      <NoPlanBanner />

      {/* The first-run hero already carries the primary "Start setup" CTA
          (FIRST_RUN_HERO_PROPS → /v2/onboarding); the old "Start setup" FAB was
          a redundant duplicate, so it's simply removed (no inline equivalent).
          The CoachFab is the sole floating action here on mobile. */}
      <div style={{ height: 96 }} />
    </div>
  );
}

/**
 * Desktop first-run: no side nav / rail yet (the user hasn't committed
 * to the product), just a centered max-width version of the mobile
 * first-run layout. Keeps focus on setup CTA.
 */
function TodayFirstRunDesktop() {
  return (
    <div className={styles.firstRunDesktop}>
      <TodayFirstRun />
    </div>
  );
}

/**
 * B167 (founder ruling 2026-09-17, decision X) — the hold for a dead config:
 * `!auth.user` because `/api/config` never answered, not because nobody
 * signed in. Same frame as every other Today state (`TopBar` on mobile, the
 * `TodayContextRetry` `AppShell` precedent on desktop) and the X pair as the
 * only copy — `OAUTH_NOT_READY_PRIMARY` / `CHIP_AUX_NOT_READY`
 * (`lib/oauthChipCopy.ts`), read here rather than restated, so a copy change
 * to the chip and this hold can never drift apart. `role="status"`, same as
 * `TodayContextRetry`: the button sits OUTSIDE the live region so a screen
 * reader does not read the control as part of the news.
 *
 * B167 fix 2 (post-deploy critic finding 1) — THE FIRST PRESS REALLY TRIES.
 * `retryConfigRead`'s shared door (b149-fix4) is spent by `AuthContext`'s own
 * automatic re-ask (cycle 2) before this surface can ever render — a route
 * that only reaches `configExhausted` once BOTH cycles are spent finds the
 * door already closed, so a `retryConfigRead()` call here answered with that
 * same spent failure at once and the first press did nothing. This control
 * opens its OWN bounded cycle instead, through the same door the
 * `online`/`visibilitychange` recovery listeners use: `clearExhaustedMark()`
 * resets the module's exhausted mark, then `getSupabaseClient()` runs a
 * genuine new four-read cycle. A failure leaves the hold exactly as it was —
 * the label reverts to "Try again", and the NEXT press reloads instead, the
 * one action left that can actually succeed. A success boots the app through
 * the provider's own path: nothing here may write to `AuthContext` (not
 * owned by this lane) to make the provider notice the client that now exists
 * at the module level, so the reload is what actually re-runs its boot — and
 * it lands on the first try this time, because the read that just succeeded
 * is the proof the network is back.
 */
function TodayConfigHold({ isDesktop }: { isDesktop: boolean }) {
  const spentRef = useRef(false);
  const [pending, setPending] = useState(false);

  const handleRetry = () => {
    if (pending) return;
    if (spentRef.current) {
      window.location.reload();
      return;
    }
    spentRef.current = true;
    setPending(true);
    clearExhaustedMark();
    getSupabaseClient()
      .then(() => {
        // A client now exists at the module level; the reload is the only
        // way this route (not `AuthContext`) can make the provider boot on
        // it.
        window.location.reload();
      })
      .catch(() => {
        /* still down — the next press reloads instead */
      })
      .finally(() => setPending(false));
  };

  const block = (
    <div className={styles.contextRetry}>
      <div className={styles.contextRetryText} role="status">
        <p className={styles.contextRetryMessage}>{OAUTH_NOT_READY_PRIMARY}</p>
        <p className={styles.contextRetryAgain}>{CHIP_AUX_NOT_READY}</p>
      </div>
      <button
        type="button"
        className={styles.contextRetryBtn}
        onClick={handleRetry}
        disabled={pending}
        aria-busy={pending}
      >
        {pending ? CONTEXT_RETRY_BUSY_LABEL : CONTEXT_RETRY_ACTION_LABEL}
      </button>
    </div>
  );

  if (isDesktop) {
    return (
      <AppShell
        nav={<SideNav items={ramiroNavItems} user={{ initial: '', displayName: '', weekLabel: '' }} />}
        main={block}
        rail={null}
      />
    );
  }
  return (
    <div className={styles.page}>
      <TopBar userInitial="" />
      {block}
    </div>
  );
}

interface TodayContextRetryProps {
  isDesktop: boolean;
  /**
   * True while EITHER read is in flight. ⚠️ NOT "a retry is in flight": this
   * surface opens on a FAILED context read while the season read may still be
   * running, and those reads are nobody's retry. The retry beat is derived
   * from a PRESS below (B47 fix 4, round-20 UX 3).
   */
  readsInFlight: boolean;
  onRetry: () => void;
}

/**
 * B47 — the held surface for a FAILED athlete-context read (decision 1,
 * founder ruling 2026-09-09).
 *
 * ⚖️ B47 fix 2 (round-15 critic 1, security 3, UX 1) — IT KEEPS THE CHROME.
 * The first cut was deliberately bare, on the reasoning that any chrome would
 * imply the app knows something about this athlete. What it actually implied
 * was that the app had ended: every other mobile Today state renders `TopBar`
 * (happy path, first-run, no-season, even the skeleton), and on the Capacitor
 * shell there is no URL bar, so an offline boot left a minor on a blank page
 * whose one control repeats the read that just failed — Settings, chat, plan
 * and SIGN OUT all unreachable, force-quit the only exit. The chrome is not a
 * claim about her data; it is the way out. Same frame as `TodayNoSeason` on
 * mobile, the AppShell on desktop, with the empty user the skeletons use
 * because nothing about her is known.
 *
 * The control is the same pair of reads that failed (`reload()` +
 * `season.refetch()`, wired by the caller); a context read that succeeds
 * clears `loadFailed`, which is what dismisses this surface and lets the
 * normal gate decide for the first time.
 *
 * The region is `role="status"`, never `role="alert"`: an assertive live region
 * interrupts, and its content should not require interaction — the button sits
 * OUTSIDE it so a screen reader does not read the action as part of the news.
 */
function TodayContextRetry({ isDesktop, readsInFlight, onRetry }: TodayContextRetryProps) {
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const pressInFlight = useRef(false);
  /**
   * ⚖️ B47 fix 7 — THE PRESS RETRIES THE ROLE FACT TOO.
   *
   * The outage that opens this surface is "the backend is unreachable", and
   * that outage fails `/api/user/role` alongside the `athlete_context` read.
   * The role fetch had no second chance: its effect's deps are frozen after a
   * mount, so a blip left this account on the degraded boundary answer for the
   * whole session. The press already re-runs both reads; it re-asks the role
   * fact in the same gesture, so one press heals everything the outage broke.
   *
   * The hook is read HERE rather than at route level because this surface is
   * the only thing that presses, and it mounts only on a failed read — nothing
   * on the happy path gains a hook. `refetch` reaches every mounted instance
   * of the hook, the guard's included (`useAccountRole.ts`).
   */
  const { refetch: refetchRole } = useAccountRole();
  /**
   * ⚖️ B47 fix 9 (round-44 critic F3a, B162) — AND THE TEAM CHECK WITH IT.
   *
   * The outage that opens this surface fails `/api/trainer/teams` too, and that
   * answer had the exact defect `refetch` was invented for one hook over:
   * frozen effect deps, a failure that is never cached, and nothing able to ask
   * again for the rest of the session. Its `isCoach` feeds this route's own
   * onboarding gate (`coachMode.isCoach` → `/team`), so a membership-detected
   * coach whose check failed could be routed as an un-onboarded athlete on the
   * next partial outage. One press, everything the outage broke.
   */
  const { refetch: refetchCoachCheck } = useCoachMode();
  /**
   * ⚖️ B47 fix 4 (round-20 UX 3) — THE BEAT BELONGS TO THE PRESS.
   *
   * The beat used to be driven straight off `athleteContext.loading ||
   * season.loading`, which is TRUE AT MOUNT: this surface opens on a context
   * read that failed, and the season read of the same page load is usually
   * still running. So the athlete's first paint was a disabled "Trying again…"
   * for as long as that read took (9 s, measured), and when it settled the
   * effect below counted a failure she had never asked for and pulled focus
   * onto the button. She had pressed nothing.
   *
   * A press is the only thing that makes a read a RETRY, so the press is what
   * starts the beat: the label, the disable and the escalation sentence all
   * ride `pressed`, and the reads this surface was born from pass unremarked.
   */
  const [pressed, setPressed] = useState(false);
  /**
   * ⚖️ B47 fix 5 (round-24 critic 1, round-26 security 3) — DID THE CONTROL
   * HOLD FOCUS WHEN THE PRESS STARTED?
   *
   * Fix 3 restored focus only when it sat on BODY, reading that as "the
   * disable dropped it there". On WebKit (the Capacitor shell and Safari) a
   * tap never focuses a button at all, so body is also where focus sits for an
   * athlete who tapped this control and then tapped the account menu — and the
   * restore threw her out of the open sheet, onto a button behind the scrim.
   * Body is not evidence of anything. What the restore is FOR is the keyboard
   * or Switch Control user whose focus the disable took away, and the one
   * moment that is knowable is the press itself.
   */
  const wasFocusedRef = useRef(false);
  // Consecutive failures BEYOND the one that opened this surface. This
  // component is mounted only while `loadFailed` is true, so a PRESSED retry
  // that settles while it is still mounted is a retry that failed again.
  const [failedRetries, setFailedRetries] = useState(0);
  const retrying = pressed && readsInFlight;

  useEffect(() => {
    // No press, no beat: whatever is in flight, nobody asked for it.
    if (!pressed) return;
    if (readsInFlight) {
      pressInFlight.current = true;
      return;
    }
    if (!pressInFlight.current) {
      // ⚖️ B47 fix 5 (round-26 critic F3) — A PRESS THAT REACHED NO READ IS
      // OVER. `reload()` sets its loading flag and then builds the query, so a
      // synchronous throw from the builder runs catch and finally inside the
      // click's own batch: this effect then sees a press with nothing in
      // flight and nothing ever started. Returning here left `pressed` true
      // for the life of the surface, and the NEXT toggle of `readsInFlight`
      // from any source — the season read, a visibility refetch — cashed it in
      // as a failed retry, with an escalation sentence and a focus move for a
      // press that was already spent. So the beat resets instead: nothing is
      // counted, because nothing was observed.
      setPressed(false);
      wasFocusedRef.current = false;
      return;
    }
    pressInFlight.current = false;
    setPressed(false);
    setFailedRetries((n) => n + 1);
    // UX 4 — disabling the focused control drops focus to BODY, and re-enabling
    // it does not bring focus back: a keyboard or Switch Control user had to
    // Tab from the top of the document to reach the only control on the page.
    // ⚖️ B47 fix 5 — and it goes back ONLY to a control that had it when the
    // press started (see `wasFocusedRef`). Anything else is a focus move
    // nobody asked for, at the one moment she may be reading the account
    // sheet this surface deliberately keeps.
    // ⚖️ B47 fix 6 — AND ONLY IF NOBODY HAS MOVED IT SINCE. The two conditions
    // answer two different questions and both have to hold: `wasFocusedRef`
    // says the disable is what took focus away (a tap never focuses a button on
    // WebKit, so it is false for the whole gesture fix 5 was about), and body
    // says nothing has claimed it since. A keyboard user who presses Enter and
    // then tabs into the account sheet fails the second, and keeps her place.
    if (wasFocusedRef.current && document.activeElement === document.body) {
      btnRef.current?.focus();
    }
    wasFocusedRef.current = false;
  }, [pressed, readsInFlight]);

  const block = (
    <div className={styles.contextRetry}>
      <div className={styles.contextRetryText} role="status">
        <p className={styles.contextRetryMessage}>{CONTEXT_RETRY_MESSAGE}</p>
        {failedRetries > 0 && (
          <p className={styles.contextRetryAgain}>{CONTEXT_RETRY_AGAIN_MESSAGE}</p>
        )}
      </div>
      <button
        ref={btnRef}
        type="button"
        className={styles.contextRetryBtn}
        onClick={() => {
          // The one moment the answer is knowable — before the disable.
          wasFocusedRef.current = document.activeElement === btnRef.current;
          setPressed(true);
          // ⚖️ B47 fix 7 — the role fact rides the same press as the reads.
          refetchRole();
          // ⚖️ B47 fix 9 — and the team check rides it too (B162).
          refetchCoachCheck();
          onRetry();
        }}
        disabled={retrying}
        aria-busy={retrying}
      >
        {retrying ? CONTEXT_RETRY_BUSY_LABEL : CONTEXT_RETRY_ACTION_LABEL}
      </button>
    </div>
  );

  if (isDesktop) {
    return (
      <AppShell
        nav={<SideNav items={ramiroNavItems} user={{ initial: '', displayName: '', weekLabel: '' }} />}
        main={block}
        rail={null}
      />
    );
  }
  return (
    <div className={styles.page}>
      <TopBar userInitial="" />
      {block}
    </div>
  );
}

interface TodayNoSeasonProps {
  isDesktop: boolean;
  meetCount: number;
  onStartSeason: () => void;
  starting: boolean;
  startPhase: 'idle' | 'creating' | 'generating';
  startError: Error | null;
  /** ⚖️ R71 — the ask was refused with `409 replan_blocked_in_flight`. */
  refused: boolean;
  /** ⚖️ B99 — the ACCOUNT is held by the B94 COPPA gate's 403, not the ask.
   *  Derived at route level (see `TodayRealData`) through `editGuardianHold`,
   *  because the hold can arrive after this card has already unmounted. */
  held: boolean;
  /** ⚖️ B196 L8 (spec §4.8, RULE 1) — the completion write would establish,
   *  preserve, or erase-into-unknown an under-13 age. Derived the same way
   *  as `held`, beside it in `TodayRealData`. */
  ageBelowMinimum: boolean;
  /** ⚖️ B196 L8 (spec §4.8, RULE 2) — no age band established on the
   *  request path. Derived the same way as `held`, beside it in
   *  `TodayRealData`. */
  ageBandRequired: boolean;
  /** ⚖️ B196 L9 (spec §4.5, F8) — RULE 2.6a, the actual-knowledge review
   *  hold. Derived the same way as `held`, beside it in `TodayRealData`. */
  ageReviewPending: boolean;
  /** ⚖️ B198 fix 2 (security F1 / UX D1) — the same `deriveSignedInUser(auth.user,
   *  athleteContext.raw)` the happy path passes to `SideNav` (`:2762`), computed
   *  at the call site from the same `auth`/`athleteContext` this route already
   *  has by the time it knows there is no season. Never the empty nav user —
   *  that placeholder is for the transient `TodayContextRetry` skeleton only,
   *  not for a screen that can persist for days under a guardian hold. */
  navUser: { displayName: string; initial: string; weekLabel: string };
}

/**
 * Season 2c — signed-in athlete with NO active season. Surfaces the shared
 * "Start your season" CTA (create + generate in one action) instead of the
 * generic first-run surface, so the user can launch a season straight from
 * Today. Mirrors the /v2/plan empty-state (the locked primary entry point).
 */
function TodayNoSeason({
  isDesktop,
  meetCount,
  onStartSeason,
  starting,
  startPhase,
  startError,
  refused,
  held,
  ageBelowMinimum,
  ageBandRequired,
  ageReviewPending,
  navUser,
}: TodayNoSeasonProps) {
  const navigate = useNavigate();
  // Race doors sweep (2026-10-18): "Add a goal race first" is the ATHLETE's door. With `races_2_0_v1` on it opens
  // the Upcoming sheet (`openRaceForm`) for every account; `openMeetForm` is the coach's door on that route, and
  // sends a dual-role account to the team-meet sheet. Read here, above the desktop early return.
  const racesV2On = useServerFlag('races_2_0_v1');
  // ⚖️ B196 L9 (UX review finding 5/critic — two refusal lines never stack)
  // — this card carries one of the four coded holds; the banner above it
  // must not print the SAME sentence a second time if the athlete taps
  // Join/Decline while it is showing.
  const cardHolds = held || ageBelowMinimum || ageBandRequired || ageReviewPending;
  // ⚖️ B99 seam 6 — the same start chain runs from /plan, where a COPPA-held
  // athlete reads one quiet line instead of a red block beside a "Try again"
  // that can never work. B99 fix 2: both coded refusals now arrive as PROPS
  // (`routes/plan.tsx:549-552` passes the same pair), because a hold derived in
  // here can only be seen while `!season.data` — and the create that triggers
  // it succeeds, so by then this card is gone. The route nulls `startError`
  // for either one, so the card cannot render the server's sentence either.
  const card = (
    <EmptyMacroPlan
      variant="no-season"
      meetCount={meetCount}
      onStartSeason={onStartSeason}
      starting={starting}
      startPhase={startPhase}
      startError={startError}
      refused={refused}
      held={held}
      ageBelowMinimum={ageBelowMinimum}
      ageBandRequired={ageBandRequired}
      ageReviewPending={ageReviewPending}
      onAddRace={() => navigate(racesV2On ? '/races?openRaceForm=1' : '/races?openMeetForm=1')}
    />
  );
  // ⚖️ B198 — IT KEEPS THE CHROME, same reasoning as `TodayContextRetry`
  // (B47 fix 2): a held minor's ONLY escape from this card used to be
  // "Add a goal race first" — no nav rail, no path to Settings. Desktop now
  // gets the real `AppShell` + `SideNav`, so the bell renders as the
  // `sidenav-row` variant anchored to the SideNav footer instead of the
  // mobile icon-button's dropdown, which has no positioned ancestor at
  // desktop widths and opened off-canvas (the inert click this lane traced).
  // ⚖️ B198 fix 2 (UX D1) — the empty nav user was a loading placeholder
  // borrowed from `TodayContextRetry`, a transient skeleton. This screen is
  // not transient: it can persist for days under a guardian hold, so the
  // SideNav me-block gets her real identity, same as the happy path.
  if (isDesktop) {
    return (
      <AppShell
        nav={<SideNav items={ramiroNavItems} user={navUser} />}
        main={
          <div className={styles.firstRunDesktop}>
            {/* P7(a) A1 — a freshly-invited athlete often has NO season yet;
                the acceptance banner must reach them here too. */}
            <TeamInviteBanner suppressHoldLine={cardHolds} />
            {/* PR-D (G3) — center the card in the freed viewport instead of
                stranding it in the top half with an empty lower half (S2-m-4). */}
            <div className={styles.noSeasonCenter}>{card}</div>
          </div>
        }
        rail={null}
      />
    );
  }
  return (
    <div className={styles.page}>
      <TopBar userInitial={newbie.initial} hasNotification={false} />
      {/* P7(a) A1 — a freshly-invited athlete often has NO season yet; the
          acceptance banner must reach them here too. */}
      <TeamInviteBanner suppressHoldLine={cardHolds} />
      {/* PR-D (G3) — center the card in the freed viewport instead of
          stranding it in the top half with an empty lower half (S2-m-4). */}
      <div className={styles.noSeasonCenter}>{card}</div>
    </div>
  );
}

interface TodayHappyPathDesktopProps {
  /** ⚖️ B47 fix 4 — see `TodayHappyPathProps.notice`. Here it goes at the top
   *  of the main pane, INSIDE the AppShell, above the hero it explains. */
  notice?: ReactNode;
  coachState: keyof typeof coachStates;
  /**
   * Phase 6c PR 6 — preferred CoachCard content source. When provided,
   * the new presentational <CoachCard message={...}> path renders;
   * otherwise the legacy fixture path falls back via `coachState`.
   */
  coachMessage?: CoachMessage | null;
  /** PR 2 — the daily coach voice line (`useMotivation().data?.message`)
   *  shown inside the loop hero's CoachLine. Null while the LLM line is
   *  in flight (the line hides cleanly). */
  coachLine?: string | null;
  /** Coach-intel Rung 1 — the coach-hue tap target; carries the run reference
   *  when the reaction echo owns the line (§11). Defaults to the coach chat. */
  coachHref?: string;
  raceState: 'present' | 'none';
  session?: TodaySession | null;
  weekPlan?: WeeklyPlan | null;
  race?: Race | null;
  /** Active season display name for the SeasonGlance kicker. */
  seasonName?: string | null;
  /** Macro-plan weeks (Phase 6c PR 4 — drives <SeasonGlance />). */
  macroPlanWeeks?: MacroPlanWeek[] | null;
  /**
   * Season-model Phase 3 — week_numbers with a DRAFTED weekly plan. When
   * provided, SeasonGlance marks drafted vs undrafted. Omitted on demo.
   */
  draftedWeeks?: Set<number>;
  /**
   * Phase 6c long-tail PR 7 — Ultra-tier `<PinnedContext>` items.
   * When provided, sourced from `useAthleteContext().pills` via the
   * `pillsToContextItems` adapter. When null/undefined, the desktop
   * happy-path falls back to the `ramiroContext` fixture (demo path).
   */
  contextItems?: ContextItem[] | null;
  /**
   * Phase 6 cross-cutting PR 5 — real consecutive-completed-day
   * training streak from `computeCurrentStreak(seasonPlans.data)`.
   * Drives the Ultra `<StreakChip>` in the right rail. Demo path
   * passes undefined → falls back to the `ramiroStreak.streakDays`
   * fixture so the design preview stays stable. Zero hides the chip.
   */
  currentStreak?: number;
  /**
   * Phase 6c.1 — toggle handler for plan_day completions. When
   * provided, WeekPlanDesktop rows with real `id`s become click
   * targets that open the day's details (B309: a click never writes; the
   * details button calls this). Demo path leaves undefined → rows stay inert.
   */
  onToggleDay?: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /** RPE restore — hero "Add effort" recovery (today completed, no RPE). */
  onAddEffort?: (dayId: string, workoutLabel: string) => void;
  /**
   * Phase 6 cross-cutting PR 6 — primary proactive trigger type for
   * this load (from `useProactiveChecks().primaryHint?.type`). Threads
   * into `<ChampionshipCardDesktop proactiveType={...}>` so the moti
   * block renders `RELATED · <type-readable>` + `--today` left-rule.
   */
  proactiveType?: ProactiveTriggerType | null;
  /**
   * Phase 7 PR 1 — Real signed-in user metadata for the SideNav
   * me-block (avatar + display name + week label). Provided by
   * `TodayRealData` (derived from auth.user + athleteContext.raw.Name).
   * Demo path leaves undefined → SideNav falls back to the `ramiro`
   * fixture so the design preview stays stable.
   */
  signedInUser?: {
    displayName: string;
    /** B73 — the PERSONAL name, or '' when the app knows none. The hero
     *  greeting reads this (never the email local part); `displayName`
     *  stays the never-blank me-block label. Absent on the demo path. */
    greetingName?: string;
    initial: string;
    weekLabel: string;
  };
  /**
   * Phase 7 PR 1 (post-smoke follow-up) — Real kudos feed for the
   * desktop `desktopInsights` TeamFeed slot (Wide+ only). Provided by
   * `TodayRealData` via `useTeamHub` + `useTeamKudos` +
   * `kudoRowsToEntries`. Empty → slot hides. Demo path leaves
   * undefined → falls back to the `ramiroTeam` fixture.
   */
  kudosEntries?: KudosEntry[];
  /**
   * Phase 7 PR 1 (post-smoke fix #3) — Real last-session card data.
   * Provided by `TodayRealData` via `useRaceResults` +
   * `lastSessionFromRaces`. Null → no race history → desktop falls
   * back to the ramiroLastSession fixture (demo) or hides on real-
   * data path with no races.
   */
  lastSession?: LastSessionData | null;
  /** B-24 — real hero weather (see TodayHappyPathProps.weather). */
  weather?: { tempDisplay: string; condition?: string; windMph: number } | null;
  sessionLoading?: boolean;
  weekLoading?: boolean;
  raceLoading?: boolean;
  /** W1-TODAY-WIRE — `today_2_0_v1` (see `TodayHappyPathProps.v2`). */
  v2?: boolean;
  /** The macro plan's approval status (`useMacroPlan().status`), for Next steps. */
  approvalStatus?: MacroPlanStatus;
  /** W1-TODAY-FIX1, FIX4 — the cold-load gate's other reads (see `TodayHappyPathProps.coldEssentialPending`). */
  coldEssentialPending?: boolean;
  coldOptionalPending?: boolean;
  /** W1-TODAY-FIX1, FIX2 (R1) — the weather row's place in the skeleton (see `TodayHappyPathProps`). */
  weatherRowPending?: boolean;
  /** B413: the page asks for a location (see `TodayHappyPathProps`). */
  promptForLocation?: boolean;
  /** W1-TODAY-FIX2 (R3, R5) — the cold-load gate's memory (see `TodayHappyPathProps`). */
  gateMemory?: MutableRefObject<ColdGateMemory>;
}

/**
 * Desktop happy-path (Phase 6c PR 4 — re-densified):
 *
 *   AppShell:
 *     nav  : SideNav (220 px)
 *     main : <TodayHero> (full width, replaces Greeting + HeroToday split)
 *            <SeasonGlance display="strip">
 *            <WeekPlanDesktop> (full-width 7-up)
 *            <insightsRow>: 2-up at Default, 3-up at Wide+
 *              [CoachCard, LastSession, TeamFeed (Wide+ only)]
 *     rail : QuickActions
 *            ChampionshipCardDesktop / NoRaceCard
 *            <SeasonGlance display="rail">
 *
 * CoachCard consumes the 5-state map via the `coachState` URL param
 * (default 'active'). Preview any state at /v2/today?coach=pattern etc.
 */
function TodayHappyPathDesktop({
  notice,
  coachState,
  coachMessage,
  coachLine,
  coachHref,
  raceState,
  session,
  weekPlan,
  race,
  seasonName,
  macroPlanWeeks,
  draftedWeeks,
  contextItems,
  currentStreak,
  onToggleDay,
  onAddEffort,
  proactiveType,
  signedInUser,
  kudosEntries,
  lastSession,
  weather,
  sessionLoading,
  weekLoading,
  raceLoading,
  v2,
  approvalStatus,
  coldEssentialPending,
  coldOptionalPending,
  weatherRowPending,
  promptForLocation,
  gateMemory,
}: TodayHappyPathDesktopProps) {
  const isDemo = session === undefined && weekPlan === undefined && race === undefined;
  // B-3 — see TodayHappyPath: re-aim the undrafted-week hero at /v2/plan/draft
  // when the flag is ON (else the legacy chat deep-link). Flag OFF → byte-identical.
  const draftWeekJourneyOn = useServerFlag('draft_week_journey_v1');
  // FRAME 5 · E7 — same string, the desktop insights mount. See the note on
  // `TodayHappyPath`; the two mounts must not diverge, which is why both read
  // the flag rather than one of them being "the" mount.
  const athleteKudosOn = useServerFlag('athlete_kudos_v1');
  // 2.0 chat (P9): with `chat_2_0_v1` on, the coach card's link opens the room with that coach called
  // (`roomCoachMessage`). A hook, above every return. Flag off → the card's own href, untouched. Build 35: never in the iOS app.
  const roomOn = useServerFlag('chat_2_0_v1') && roomAvailableOnThisClient();
  // W1-TODAY-WIRE (TWR-6) — the same cold-load gate as the phone. A hook: above
  // every return. (W1-TODAY-FIX1: the same reads as the phone, see there.)
  const sharedHealth = useSharedHealthSync();
  const coldGate = useColdLoadGate(
    !!v2,
    !!sessionLoading || (!!weekLoading && !weekPlan) || !!raceLoading || !!coldEssentialPending,
    !!coldOptionalPending || (!!session && !!sharedHealth && !sharedHealth.loaded),
    gateMemory,
  );
  // W1-TODAY-LAYOUT-MEMORY: whose layout the loaded page remembers (see `useRememberTodayLayout`),
  // and whether the hero's action area is Start run (the frame cannot read the flag before the
  // config answers, so the memory carries it).
  const layoutUserId = useAuthContext().user?.id ?? null;
  const runCaptureOn = useServerFlag('run_capture_v1');
  // Phase 7 PR 1 — Real signed-in user when available; ramiro fixture
  // fallback for demo path. Without this the SideNav me-block always
  // showed "Ramiro" regardless of who was signed in. Surfaced by user
  // during waisports test-account smoke 2026-05-15.
  const navUser = signedInUser ?? ramiro;
  // B73 — see TodayHappyPath: the greeting reads the PERSONAL name (or '' →
  // plain greeting), while the me-block keeps the never-blank label.
  const navGreetingName = signedInUser ? signedInUser.greetingName ?? '' : navUser.displayName;
  const navigate = useNavigate();
  // PR 5.2 polish: wire the desktop ChampionshipCardDesktop's
  // "Season plan ›" CTA at the bottom of the rail card to navigate
  // to /v2/plan. The mobile path uses a SectionLabel link; desktop
  // doesn't have the SectionLabel, so the in-card button IS the
  // affordance.
  // W1-TODAY-FIX3 (TC-5): the target is `useSeasonPath()` (`/plan` with `train_2_0_v1` off).
  const seasonPath = useSeasonPath();
  const goToSeasonPlan = () => navigate(seasonPath);

  const effectiveSession = session ?? (isDemo ? ramiroToday : null);
  const effectiveWeek = weekPlan ?? (isDemo ? ramiroWeek : null);
  const effectiveRace = race ?? (isDemo && raceState !== 'none' ? championshipFinal : null);
  const effectiveMacro: MacroPlanWeek[] | null =
    macroPlanWeeks ?? (isDemo ? ramiroMacroPlan : null);
  const effectiveSeasonName = seasonName ?? (isDemo ? 'Spring 2026 XC' : null);
  const currentWeekDerived = phaseFromMacroPlan(effectiveMacro).weekNumber ?? null;
  // B333 — see the mobile body: the quote follows whether a season is running.
  const seasonRunning = resolveCurrentWeek(effectiveMacro, null).state === 'in_season';
  // #2a — same "draft upcoming weeks" surfacing as the mobile body: count future
  // macro weeks not drafted yet (real path only) to surface a plan-ahead entry.
  const undraftedUpcomingCount = effectiveMacro && draftedWeeks && currentWeekDerived != null
    ? effectiveMacro.filter((w) => w.week_number > currentWeekDerived && !draftedWeeks.has(w.week_number)).length
    : 0;
  // R-Ultra-1 — the same gate that used to wrap the retired Ultra left pane,
  // now read twice in the rail: once for the streak chip, once for the season.
  const hasSeason = !!effectiveMacro && effectiveMacro.length > 0;

  // W1-TODAY-WIRE (TWR-4, TWR-5) — as on the phone: the 2.0 desktop layout needs the
  // flag AND a session (or the cold gate holding for one); the no-session states
  // keep their legacy look. The shell, the side nav and the right rail stay.
  const v2Layout = !!v2 && (coldGate || !!effectiveSession);

  // W1-TODAY-LAYOUT-MEMORY: as on the phone, the loaded page remembers the block set it drew
  // once every read that decides it has settled. A hook, above the cold-load return below.
  useRememberTodayLayout(
    layoutUserId,
    v2 && v2Layout && !coldGate && !!effectiveSession && !!effectiveWeek &&
      !sessionLoading && !weekLoading && !raceLoading && !coldEssentialPending && !coldOptionalPending &&
      !(sharedHealth && !sharedHealth.loaded)
      ? {
          ...coldSkeletonPlace({
            approvalStatus,
            draftWeeksLabel: draftWeeksLabelFor(effectiveMacro, currentWeekDerived, draftedWeeks),
            draftJourneyOn: draftWeekJourneyOn,
            noGoalRace: !effectiveRace && !raceLoading,
            today: effectiveWeek.days.find((d) => d.today) ?? null,
            days: effectiveWeek.days,
            coachLine,
            coachHref,
            proactiveType,
          }),
          weatherRow: !!weatherRowPending,
          weatherPrompt: !!promptForLocation,
          healthRow: healthRowFor(sharedHealth),
          startRun: isNativeApp() && runCaptureOn,
        }
      : null,
  );

  // TWR-6 — the cold-load gate: the shell with ONE skeleton in the main column, and
  // the rail holding its place (Quick actions stay, D10; the race card as bars). As on the
  // phone, the skeleton is the device's remembered layout (W1-TODAY-LAYOUT-MEMORY).
  if (coldGate) {
    return <TodayColdFrameDesktop navUser={navUser} />;
  }

  return (
    <AppShell
      nav={<SideNav items={ramiroNavItems} user={navUser} />}
      main={
        <div className={styles.desktopMain}>
          {/* ⚖️ B47 fix 4 (round-20 UX 1) — the route's passive line, INSIDE the
              shell. Above it, as a sibling of this component, it floated over
              the logo at 1440. (2.0: below the week card, with the tail, Q7.) */}
          {!v2Layout && notice}

          {/* P7(a) A1 — team-invite acceptance banner (self-contained). */}
          {!v2Layout && <TeamInviteBanner />}

          {/* Hero — full-width TodayHero (Phase 6c PR 4 — replaces the
              prior Greeting + HeroTodayDesktop split). */}
          {v2Layout && effectiveSession ? (
            /* 2.0 desktop (spec 7.3): the phone's order in the main column: date
               line and hero, status row, Next steps, the week card. The race card
               stays in the right rail. */
            <>
              {renderLoopHero({
                session: effectiveSession,
                race: effectiveRace,
                weekPlan: effectiveWeek,
                macroPlanWeeks: effectiveMacro,
                streakDays: typeof currentStreak === 'number' ? currentStreak : 0,
                weather: weather ?? null,
                weatherFromApple: !!weather,
                athleteName: navGreetingName,
                onToggleDay,
                coachLine: coachLine ?? null,
                coachHref,
                proactiveType,
                onAddEffort,
                v2: true,
              })}
              <TodayV2Cards
                gutter={false}
                approvalStatus={approvalStatus ?? 'none'}
                draftWeeksLabel={draftWeeksLabelFor(effectiveMacro, currentWeekDerived, draftedWeeks)}
                draftJourneyOn={draftWeekJourneyOn}
                hasGoalRace={!!effectiveRace}
              />
              {effectiveWeek ? (
                <WeekPlanDesktop
                  plan={effectiveWeek}
                  onToggle={onToggleDay}
                  // B78 — same destination rule as the hero's "Open week plan"
                  // (UX-S2 W5): the CURRENT week's page, overview fallback.
                  fullPlanHref={currentWeekDerived != null ? `/v2/plan/week/${currentWeekDerived}` : '/v2/plan'}
                  variant="v2"
                />
              ) : weekLoading ? (
                <WeekPlanSkeleton />
              ) : null}
            </>
          ) : sessionLoading ? (
            <TodayHeroSkeleton />
          ) : effectiveSession ? (
            renderLoopHero({
              session: effectiveSession,
              race: effectiveRace,
              weekPlan: effectiveWeek,
              macroPlanWeeks: effectiveMacro,
              streakDays:
                typeof currentStreak === 'number'
                  ? currentStreak
                  : ramiroStreak.streakDays,
              // B-24 — real weather on the real-data path (null → row
              // hides); the fixture is DEMO-ONLY.
              weather: weather ?? (isDemo ? ramiroWeather : null),
              // REL-8 — the mark credits APPLE, so it rides the real value only.
              weatherFromApple: !!weather,
              // Phase 7 PR 1 — real signed-in athlete name when
              // available; ramiro fixture fallback for demo path.
              athleteName: navGreetingName,
              onToggleDay,
              // PR 2 — loop hero owns the CTA + reward strip now; the
              // standalone MarkTodayCompleteButton is retired.
              coachLine: coachLine ?? (isDemo ? ramiroToday.heroCoachNote?.text ?? null : null),
              coachHref,
              proactiveType,
              onAddEffort,
            })
          ) : (
            renderEmptyOrBeforeSeasonHero(effectiveMacro, effectiveSeasonName, draftedWeeks, draftWeekJourneyOn)
          )}

          {/* UAT-B2 (frame 1d) — one-time guardian-invite resurface, below the
              hero. Self-gates to a skipped 13–15 minor; null otherwise.
              (2.0: below the week card, with the tail.) */}
          {!v2Layout && <GuardianNudgeCard />}

          {/* SeasonGlance strip — between the loop hero and WeekPlan at 1024
              and 1280. G4 (UX-S3): fixed-116px scrolling cells (the collision
              fix).

              R-Ultra-1 — the strip YIELDS at ≥1920 and the rail SeasonRail is
              the season there. PR-B's earlier attempt at this hide was
              reverted because the season then lived in the far-left Ultra
              pane and the strip was the only module the reader could reach;
              with the season in the RIGHT rail the strip is the duplicate.
              `.centreSeasonSlot` is `display: contents` below 1920, so the
              wrapper adds no box and the two lower tiers are byte-identical in
              layout to the bare mount. (2.0 Today draws no season strip: frame
              1v has none, and Next steps carries the draft entry.) */}
          {!v2Layout && effectiveMacro && effectiveMacro.length > 0 && (
            <div className={styles.centreSeasonSlot}>
              <SeasonGlance
                display="strip"
                weeks={effectiveMacro}
                currentWeek={phaseFromMacroPlan(effectiveMacro).weekNumber ?? null}
                // B2.1 — make the strip cells navigable (matches the already-
                // navigable Ultra SeasonRail). Cells render as <a> when set.
                weekHref={(n) => `/v2/plan/week/${n}`}
                seasonName={effectiveSeasonName ?? undefined}
                openLink={{ label: 'Open season plan', href: '/v2/plan' }}
                meetLabels={isDemo ? ramiroMacroPlanMeetLabels : undefined}
                // Phase 3 — mark drafted vs undrafted (real path only; undefined
                // on demo → unmarked showcase).
                draftedWeeks={draftedWeeks}
              />
            </div>
          )}

          {/* #2a — explicit "plan ahead" entry (desktop): drafting isn't limited
              to the current week. Shown only when upcoming weeks are undrafted. */}
          {!v2Layout && undraftedUpcomingCount > 0 && (
            <InlineAction
              to="/plan"
              label="Draft upcoming weeks"
              sub={`${undraftedUpcomingCount} not drafted yet`}
            />
          )}

          {/* WeekPlan — full-width 7-up. B309 fix round: skeleton only while
              there is no week to show yet (see the mobile mount above). */}
          {v2Layout ? null : weekLoading && !weekPlan ? (
            <WeekPlanSkeleton />
          ) : effectiveWeek ? (
            <WeekPlanDesktop
              plan={effectiveWeek}
              onToggle={onToggleDay}
              // B78 — same destination rule as the hero's "Open week plan"
              // (UX-S2 W5): the CURRENT week's page, overview fallback.
              fullPlanHref={currentWeekDerived != null ? `/v2/plan/week/${currentWeekDerived}` : '/v2/plan'}
            />
          ) : (
            // F-116(c) — dedupe: the "add a race" prompt lives in the right-rail
            // <NoRaceCard>; the old <NoPlanBanner> here was a second copy of the
            // same prompt. Drop it so the rail card is the single race prompt.
            null
          )}

          {/* 2.0 tail (spec 7.3, Q7): the passive notices, the invite banner and the
              guardian nudge, in the order legacy has them, below the week card and
              ahead of the insights row. */}
          {v2Layout && (
            <>
              {notice}
              <TeamInviteBanner />
              <GuardianNudgeCard />
            </>
          )}

          {/* Insights row — 2-up at Default, 3-up at Wide+
              Full 3-card layout on both demo AND real-data paths
              (per user request 2026-05-16 — the show-off account
              must exercise the full layout for comparison against
              empty-state designs).

              Data sources by card:
                - CoachCard: real coachMessage from rules-engine
                             placeholder (mockCoachMessages().active).
                             When the LLM rules engine ships, swap
                             the source in TodayRealData.
                - LastSession: real-data path → lastSession from
                             useRaceResults via lastSessionFromRaces
                             adapter; demo path → ramiroLastSession
                             fixture.
                - TeamFeed:  real-data path → kudoRowsToEntries
                             output from useTeamKudos; demo path →
                             ramiroTeam fixture.

              Row hides only when ALL three are empty on real-data
              path AND it's not demo. */}
          {(() => {
            const realTeam: KudosEntry[] = isDemo
              ? ramiroTeam
              : (kudosEntries ?? []);
            const effectiveLastSession = isDemo
              ? ramiroLastSession
              : lastSession;
            // w1-today-finish (Fable wave 1 sign-off): the 2.0 page has no coach card, so a coach
            // message alone is no reason to open the row. It used to (`!!coachMessage`), and at 1024 to
            // 1279, where the Team slot is `display: none` (today.module.css), that left an empty grid
            // in the column. With the flag off, or in the states it leaves alone, the card is still
            // there and still opens the row.
            const showRow =
              isDemo ||
              realTeam.length > 0 ||
              effectiveLastSession !== null ||
              (!v2Layout && !!coachMessage);
            if (!showRow) return null;
            // B-40 BUG 4a — the ≥1280 grid was hardwired `1.4fr 1fr 1fr`,
            // assuming three cards. But CoachCard and LastSession are each
            // conditional (the TeamFeed/kudos slot is always mounted at
            // Wide+), so a 2-card row (e.g. coach + kudos, no logged
            // session) left the kudos card stranded in the narrow 1fr col
            // with an empty 3rd column — the founder's "weird size". Count
            // the cards actually rendered at desktop and pick a matching
            // column template so the emphasised 1.4fr lead is preserved
            // without a dangling empty track.
            // W1-TODAY-COLDLOAD (rulings 2026-10-18 item 2, CD item 2): on the 2.0 page (the flag on
            // and a session or the cold gate: `v2Layout`) the coach card ("TRAINING · AIRO · N of 5
            // sessions") is not part of Today, and nothing replaces it; the week card ends the column.
            // The states the flag leaves alone (first run, before season, an undrafted week: TWR-5)
            // keep their legacy look, card included. The card, and its share of the column template, go;
            // the row keeps mounting for a Team entry or a last session (and, from w1-today-finish, no
            // longer for a coach message alone, see `showRow` above).
            const hasCoachCard = !v2Layout && (!!coachMessage || isDemo);
            const desktopCardCount =
              (hasCoachCard ? 1 : 0) + (effectiveLastSession ? 1 : 0) + 1; // +1 = the always-mounted team/kudos slot
            const insightsClass = [
              styles.desktopInsights,
              desktopCardCount === 2 ? styles.desktopInsightsTwo : '',
              desktopCardCount === 1 ? styles.desktopInsightsOne : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div className={insightsClass}>
                {v2Layout ? null : coachMessage ? (
                  <CoachCard message={roomOn ? roomCoachMessage(coachMessage) : coachMessage} />
                ) : isDemo ? (
                  <CoachCard content={coachStates[coachState]} />
                ) : null}
                {effectiveLastSession && (
                  <LastSession
                    title={effectiveLastSession.title}
                    intensityLabel={effectiveLastSession.intensityLabel}
                    stats={effectiveLastSession.stats}
                    note={effectiveLastSession.note}
                  />
                )}
                {/* TeamFeed slot — hidden at Default, unhid at Wide+ via CSS.
                    Truncated to 3 entries here so the insights row stays
                    height-balanced with CoachCard + LastSession siblings.
                    Overflow "View all team →" links to /v2/team. Mobile
                    section + standalone team route keep the full feed. */}
                {/* UX-S3 DD-4 — always mounted (Wide+ via CSS); renders the
                    kudos feed, or a teaser empty-state when there are none. */}
                <div className={styles.insightsTeamSlot}>
                  <TeamFeed
                    entries={realTeam}
                    maxEntries={3}
                    viewAllHref="/team"
                    showEmptyHint
                    kudosV1={athleteKudosOn}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      }
      rail={
        <>
          {/* Phase 6 cross-cutting PR 5 — TopRailUnread chip surfaces
              notification unread count at Ultra (≥ 1920 px) only.
              Self-gates via CSS @media + via `unreadCount > 0`
              (zero-state returns null). Below Ultra the bell badge
              in the SideNav footer is the single signal; at Ultra
              the chip earns the extra horizontal real estate so the
              count stays visible without the user looking down-left. */}
          <TopRailUnread />
          {/* R-Ultra-1 — the StreakChip comes across from the retired Ultra
              left pane and sits second in the rail. It keeps its OWN 1920 CSS
              gate (`StreakChip.module.css`) AND its `currentStreak <= 0` null,
              so nothing below Ultra changes. Real-data path passes the PR
              4e-computed streak; demo path (currentStreak undefined) falls
              back to the ramiroStreak fixture so the design preview stays
              stable. Personal best is null until the schema lands a stored
              `_streak_personal_best` (deferred). 2.0 retires the streak chip
              (spec 7.3, TH-11), so it is not rendered here under the flag. */}
          {!v2Layout && hasSeason && (
            <StreakChip
              currentStreak={
                typeof currentStreak === 'number'
                  ? currentStreak
                  : ramiroStreak.streakDays
              }
              personalBest={null}
            />
          )}
          <QuickActions />
          <div className={styles.desktopRailDivider} />
          {raceLoading ? (
            <ChampionshipCardSkeleton />
          ) : effectiveRace ? (
            <ChampionshipCardDesktop
              race={effectiveRace}
              withMotivation={!!effectiveRace.motivationalLine}
              onSeasonPlanClick={goToSeasonPlan}
              proactiveType={proactiveType}
              v2={v2Layout}
            />
          ) : (
            <NoRaceCard
              variant="desktop"
              seasonRunning={seasonRunning}
              v2={v2Layout}
              phase={v2Layout ? noRacePhaseFromMacro(effectiveMacro) : undefined}
            />
          )}
          {/* G4 (UX-S3), as amended by R-Ultra-1: one canonical season module
              per tier — the centre strip below 1920, the SeasonRail here at
              Ultra. The `display="rail"` SeasonGlance stays removed.

              ⚠️ The Ultra gate is `.railSeasonSlot` in this route's CSS, NOT
              the SeasonRail's own media query: R-Train-2 moves that component
              gate down to 1024 for the Train rail, so relying on it would
              spill the season into Today's rail at 1024. The wrapper is the
              gate. */}
          {hasSeason && (
            <div className={styles.railSeasonSlot}>
              <SeasonRail
                seasonName={effectiveSeasonName}
                weeks={effectiveMacro}
                currentWeek={currentWeekDerived}
                currentWeekSuffix=", now"
              />
            </div>
          )}


          {/* PinnedContext — Phase 6c PR 5 — Ultra-only rail card.
              Mounted at all tiers but display-gated to Ultra via the
              `.railPinnedSlot` @media (min-width: 1920px) rule below.
              Below Ultra the ContextDrawer (mobile) covers the same
              role; on Default + Wide it stays out of the rail to keep
              the right-rail focused on the championship + actions.

              Phase 6c PR 7 — `contextItems` sources from real
              `useAthleteContext().pills` (real-data path) or the
              `ramiroContext` fixture (demo path). Empty → null →
              PinnedContext returns null → card hides cleanly. The
              `updatedLabel` is dropped on the real-data path until
              we plumb the upstream `updated_at` into useAthleteContext;
              demo keeps "updated 2d" so the design preview is stable. */}
          <div className={styles.railPinnedSlot}>
            <div className={styles.desktopRailGap} />
            {/* UX-S2 D2 — the `ramiroContext` fixture is DEMO-ONLY. On the
                real-data path an empty context must NOT fall back to the
                fixture: that painted plausible fake values (Age 39 / coffee
                80mg / …) for a signed-in athlete and silently masked the
                context-wipe data loss. Mirror the mobile ContextDrawer gate
                (isDemo ? fixture : real) — real-but-empty → [] → PinnedContext
                returns null → card hides honestly (no fake "sample" data). */}
            <PinnedContext
              items={isDemo ? ramiroContext : (contextItems ?? [])}
              updatedLabel={isDemo ? 'updated 2d' : undefined}
              // UX-S2 D1b — unify the two "edit context" affordances on /v2/today.
              // The Quick-actions "Update context" goes to /v2/settings; this
              // footer link went to /v2/onboarding/context — a different route
              // AND an onboarding-write surface in the P0 wipe class. Point both
              // at the canonical Settings context view.
              // F-513 — anchor to the read-only "What Airo knows" section and
              // label the affordance for what it opens (a view, not an editor).
              editHref="/v2/settings#airo-knows"
              editLabel="What Airo knows ›"
            />
          </div>
        </>
      }
    />
  );
}

// ─── Loading skeletons (full-page) ────────────────────────────────────
//
// ⚖️ B47 fix 7 (B134's other half) — EXPORTED for `AthleteSurfaceGuard`.
// The guard is the route element for /today and seven other athlete surfaces,
// so while it holds, this route is not mounted and cannot paint its own
// skeleton. It renders these instead of the empty cream div it used to. They
// take no props and no account data on purpose (`userInitial=""`, the empty
// nav user), which is what makes them safe on a hold that can span an account
// change: there is nothing of the previous reader in them.
//
// W1-TODAY-COLDLOAD — and they are the ONE place every loading state of this route
// (the guard's hold, `auth.loading`, the season and onboarding reads, the tier read)
// asks "which Today is this?", so each decides its own frame (`TodayFrameSwitch`)
// before it paints: the neutral frame while the flag is unanswered and uncached, the
// 2.0 loading frame (the loaded page's own boxes, neutral bars) with the flag on, and,
// with it off once it is known, the legacy skeleton below, byte for byte as it was.
// Without this the guard and these early returns painted the legacy skeleton for the
// length of the config read on every cold load, flag on or not.

export function DashboardSkeletonMobile() {
  return (
    <TodayFrameSwitch
      v2={<TodayColdFrameMobile userInitial="" />}
      legacy={<LegacyDashboardSkeletonMobile />}
    />
  );
}

export function DashboardSkeletonDesktop() {
  return (
    <TodayFrameSwitch
      v2={<TodayColdFrameDesktop navUser={EMPTY_NAV_USER} />}
      legacy={<LegacyDashboardSkeletonDesktop />}
    />
  );
}

function LegacyDashboardSkeletonMobile() {
  return (
    <div className={styles.page}>
      <TopBar userInitial="" />
      <TodayHeroSkeleton />
      <div style={{ height: 24 }} />
      <WeekPlanSkeleton />
      <div style={{ height: 24 }} />
      <ChampionshipCardSkeleton />
    </div>
  );
}

function LegacyDashboardSkeletonDesktop() {
  return (
    <AppShell
      nav={<SideNav items={ramiroNavItems} user={{ initial: '', displayName: '', weekLabel: '' }} />}
      main={
        <>
          <div className={styles.desktopSplit}>
            <TodayHeroSkeleton />
            {/* B169 — the settled desktop week strip (`<WeekPlanDesktop>`) is a
                card of 7 stacked rows, not the mobile `<WeekPlanSkeleton>`'s
                7-col grid; see skeletons.module.css's "WeekPlan skeleton,
                desktop" block for the geometry this mirrors. */}
            <div
              className={skeletonStyles.weekDesktopCard}
              role="status"
              aria-busy="true"
              aria-label="Loading this week's plan"
            >
              <div className={skeletonStyles.weekDesktopList}>
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className={skeletonStyles.weekDesktopRow} aria-hidden>
                    <span className={`${skeletonStyles.bar} ${skeletonStyles.weekDesktopDate}`} />
                    <span className={skeletonStyles.weekDesktopBody}>
                      <span className={`${skeletonStyles.bar} ${skeletonStyles.weekDesktopLabel}`} />
                      <span className={`${skeletonStyles.bar} ${skeletonStyles.weekDesktopPace}`} />
                    </span>
                    <span className={`${skeletonStyles.bar} ${skeletonStyles.weekDesktopValue}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      }
      rail={
        <>
          <QuickActions />
          <div className={styles.desktopRailDivider} />
          <ChampionshipCardSkeleton />
        </>
      }
    />
  );
}

/** The nav user of a frame that carries no account (the guard's hold, an unauthed read). */
const EMPTY_NAV_USER = { initial: '', displayName: '', weekLabel: '' };

/**
 * The 2.0 loading frame on a phone: the bar, then ONE skeleton, which is the loaded
 * page's own boxes with neutral bars (CD frames 1a to 1d) as the device's last visit drew
 * them, or the plain frame (the date bar and an empty column) on a device with no memory.
 * It is the frame the cold-load gate paints AND the frame every earlier loading state
 * paints, and all of them read the same memory, so the page does not change shape between
 * them, and nothing a read answers changes it: the page arrives at the swap.
 */
function TodayColdFrameMobile({ userInitial }: { userInitial: string }) {
  // W1-TODAY-LAYOUT-MEMORY: every loading state draws the SAME frame, from the device's
  // remembered layout (or the plain frame), so no state of the load changes its shape.
  const layout = useRememberedLayout();
  return (
    <div className={styles.page}>
      <TopBar userInitial={userInitial} />
      <TodaySkeletonV2 gutter layout={layout} />
    </div>
  );
}

/** The same frame on a desktop: the shell, the side nav, the skeleton in the main column and the rail holding its place. */
function TodayColdFrameDesktop({ navUser }: { navUser: ComponentProps<typeof SideNav>['user'] }) {
  const layout = useRememberedLayout();
  return (
    <AppShell
      nav={<SideNav items={ramiroNavItems} user={navUser} />}
      main={
        <div className={styles.desktopMain}>
          <TodaySkeletonV2 layout={layout} />
        </div>
      }
      rail={
        <>
          <QuickActions />
          <div className={styles.desktopRailDivider} />
          <ChampionshipCardSkeleton decorative />
        </>
      }
    />
  );
}
